import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash, PencilSimple, MagnifyingGlass, UserPlus, WarningCircle, CalendarBlank, CalendarPlus, Check, X, ClipboardText, Eye, CaretDown, CaretLeft, CaretRight, Circle, House, Sparkle, PaperPlaneTilt, ArrowCounterClockwise, ArrowClockwise, EnvelopeSimple, DownloadSimple, Heartbeat, ClockCounterClockwise, ChatCircleText } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import SatisfactionDiagnosticDialog from '../components/study/SatisfactionDiagnosticDialog';
import ESignature from '../components/ESignature';
import { DermScaleRenderer } from '../components/DermScaleRenderer';
import { MasiRenderer } from '../components/MasiRenderer';
import { VolunteerPhotoCompare } from '../components/VolunteerPhotoCompare';
import { CustomTableRenderer, customTableMissing } from '../components/CustomTable';
import { CompositeScaleRenderer, compositeScaleMissing } from '../components/CompositeScale';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import AppointmentFormDialog from '../components/agenda/AppointmentFormDialog';
import SendInfoSheetOnAssignDialog from '../components/study/SendInfoSheetOnAssignDialog';
import { DAY_SLOT_MINUTES, DAY_CELL_HEIGHT, DAY_TIME_SLOTS, timeToMinutesFrom8, hexToRgba } from '../components/agenda/agendaConstants';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { sortByVCode } from '../utils/sortVolunteers';
import { MonitorCell, QuestionQueryBadge, StatusDot } from '../components/study/monitoring/MonitorCell';
import { MonitorFooterButton } from '../components/study/monitoring/MonitorFooterButton';
import QuestionnaireQueriesPanel from '../components/study/monitoring/QuestionnaireQueriesPanel';
import { QuestionnaireHistory } from '../components/study/monitoring/QuestionnaireHistory';
import { monitorableQids, monitoringApi, queriesForQuestionnaire, isQueryOpen } from '../components/study/monitoring/monitoringUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const volStatusColors = {
  pendiente: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  incluido: 'bg-green-50 text-green-700 border-green-200',
  // URS-F344b: voluntarios que terminaron su ultima visita (auto-completed)
  // siguen visibles en la pestana "Incluidos" con este distintivo emerald.
  completado: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  excluido: 'bg-red-50 text-red-600 border-red-200',
  abandono: 'bg-amber-50 text-amber-700 border-amber-200',
  apto: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  no_apto: 'bg-rose-50 text-rose-600 border-rose-200',
};

// URS-F499 / URS-F503: helpers para cuestionarios "multi-entry"
// (Eventos Adversos + Medicacion Concomitante). Etiquetas y textos
// especificos se derivan del `type` del cuestionario.
const MULTI_ENTRY_TYPES = new Set(['eventos_adversos', 'medicacion_concomitante']);
const isMultiEntryType = (t) => MULTI_ENTRY_TYPES.has((t || '').toLowerCase());
const entryPrefix = (t) => ((t || '').toLowerCase() === 'medicacion_concomitante' ? 'Med' : 'EA');
const addEntryLabel = (t, lang) => {
  const isMC = (t || '').toLowerCase() === 'medicacion_concomitante';
  if (isMC) return lang === 'es' ? 'Añadir Medicación' : 'Add Medication';
  return lang === 'es' ? 'Añadir EA' : 'Add AE';
};
const removeEntryTitle = (t, lang) => {
  const isMC = (t || '').toLowerCase() === 'medicacion_concomitante';
  if (isMC) return lang === 'es' ? 'Eliminar esta medicación' : 'Delete this medication';
  return lang === 'es' ? 'Eliminar este EA' : 'Delete this AE';
};
const notApplicableLabel = (t, lang) => {
  const isMC = (t || '').toLowerCase() === 'medicacion_concomitante';
  if (isMC) {
    return lang === 'es'
      ? 'No aplica — Sin cambios desde la última visita'
      : 'Not applicable — no changes since last visit';
  }
  return lang === 'es'
    ? 'No aplica — el voluntario no presenta ningún evento adverso en esta visita'
    : 'Not applicable — volunteer reports no adverse events at this visit';
};

export default function VolunteersPanel({ studyId, lang, studyPhase, autoExpandVolunteerId, forcedSubTab, highlightVolunteerId, openQuestionnaireKey, openQueryId, shellHidden = false, onFillClosed }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  // URS-F401b: gate del toggle Pago 1/Pago 2 por el permiso de estudio
  // `costes:edit`. Los permisos de estudio ya los precarga StudyForm.js via
  // loadStudyPerms(id), asi que aqui solo consultamos el cache.
  const { canStudy, can } = usePermissions();
  // URS-F420: popup Qsalud desde la fila del voluntario incluido.
  const canSeeQsalud = can('qsalud', 'view');
  const [qsaludVolunteer, setQsaludVolunteer] = useState(null);
  // URS-F423b: dialogo compartido de diagnostico/historial de envios de satisfaccion
  const [showSatDiag, setShowSatDiag] = useState(false);
  const canEditCostes = isAdmin || canStudy(studyId, 'costes', 'edit');
  const [assigned, setAssigned] = useState([]);
  // URS-F269: mapa {volunteer_id: {VAL: appointment, T0: appointment}} con la
  // ultima cita ACTIVA para Valoracion y T0 (filtramos canceladas/no_asiste).
  // Permite mostrar "Citado dd/MM" en lugar del boton de crear cita en las
  // pestanas Pendientes (VAL) y Aptos (T0).
  const [apptByVolVisit, setApptByVolVisit] = useState({});
  // URS-F395: guardar tambien la ultima cita FALLIDA (anulada / no_asiste)
  // por (voluntario, VAL|T0) para poder mostrarla en rojo en Pendientes.
  const [missedApptByVolVisit, setMissedApptByVolVisit] = useState({});
  const [protocolData, setProtocolData] = useState({});
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [staffOnly, setStaffOnly] = useState(false);
  const [showConflict, setShowConflict] = useState(null);
  const [pendingVol, setPendingVol] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState({ volunteer_ids: [], base_date: '', base_time: '09:00' });
  const [genVolSearch, setGenVolSearch] = useState('');
  // Day-view mini-calendar for the generate dialog (all studies on that day)
  const [calendarDay, setCalendarDay] = useState(() => new Date().toISOString().split('T')[0]);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [loadingDayAppts, setLoadingDayAppts] = useState(false);
  // URS-F331: huecos programados + bloqueos del dia (para alinear con la
  // vista Dia principal de la Agenda).
  const [dayScheduleSlots, setDayScheduleSlots] = useState([]);
  const [dayScheduleBlocks, setDayScheduleBlocks] = useState([]);
  const [allStudies, setAllStudies] = useState([]);
  const [genResult, setGenResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState('config');
  const [previewAppts, setPreviewAppts] = useState([]);
  const [activePreviewIdx, setActivePreviewIdx] = useState(0);
  // Email-with-appointments checkbox (pre-checked) and per-step status.
  const [sendEmailWithAppts, setSendEmailWithAppts] = useState(true);
  const [emailSendResult, setEmailSendResult] = useState(null);
  // URS-F459: reemplazar citas existentes en lugar de saltarlas.
  const [replaceExistingAppts, setReplaceExistingAppts] = useState(false);
  // I/E questionnaire state
  const [showIEDialog, setShowIEDialog] = useState(null);
  const [ieQuestions, setIEQuestions] = useState([]);
  const [ieResponses, setIEResponses] = useState({});
  const [ieSaving, setIESaving] = useState(false);
  const [ieError, setIEError] = useState('');
  const [subTab, setSubTab] = useState(forcedSubTab || (autoExpandVolunteerId ? 'incluidos' : 'pendientes'));
  // URS-F495: dialogo de envio de HIP tras asignar un voluntario.
  const [sendHipDialog, setSendHipDialog] = useState(null); // {volunteerId, volunteerName, volunteerEmail}
  // URS-F498: dialogo para añadir cuestionario de Eventos Adversos.
  const [addEaDialog, setAddEaDialog] = useState(false);
  const [eaSelVols, setEaSelVols] = useState(['all']); // ['all'] o lista de volunteer_ids
  const [eaSelVisits, setEaSelVisits] = useState(['all']); // ['all'] o lista de visit codes
  const [eaSaving, setEaSaving] = useState(false);
  const [showCannotInclude, setShowCannotInclude] = useState(false);
  const [showIncludeSignature, setShowIncludeSignature] = useState(false);
  const [showExcludeSignature, setShowExcludeSignature] = useState(false);
  const [showStatusSignature, setShowStatusSignature] = useState(false);
  // Expandable included volunteers
  const [expandedVol, setExpandedVol] = useState({});
  const [studyVisits, setStudyVisits] = useState([]);
  // URS-F463: primera visita real del protocolo (menor `day`, excluye VAL/EA).
  // Sirve para el boton "Citar {basal}" en Aptos y para filtrar cual omitir en
  // la generacion. Si es T0 sigue diciendo "Citar T0"; si es T-14D dira "Citar T-14D".
  const firstProtocolVisit = useMemo(() => {
    const candidates = (studyVisits || []).filter(v => {
      const c = (v.code || '').toUpperCase();
      return c && c !== 'VAL' && c !== 'EA';
    });
    if (candidates.length === 0) return null;
    return candidates.reduce((min, v) => ((v.day ?? 0) < (min.day ?? 0) ? v : min), candidates[0]);
  }, [studyVisits]);
  const firstProtocolVisitCode = firstProtocolVisit?.code || 'T0';
  const [symptomsByKey, setSymptomsByKey] = useState({}); // {`${volunteer_code}|${visit_code}`: [{symptom_id, observation, created_at}, ...]}
  const [showSymptomDialog, setShowSymptomDialog] = useState(null); // {assignment, visit_code}
  const [studyQuestionnaires, setStudyQuestionnaires] = useState([]);
  const [volAppointments, setVolAppointments] = useState({});
  const [visitResponses, setVisitResponses] = useState({});
  // Fill questionnaire dialog
  const [fillQ, setFillQ] = useState(null);
  // Monitorización + queries del cuestionario
  const [studyStaff, setStudyStaff] = useState({});
  const [studyQueries, setStudyQueries] = useState([]);
  const [qPanelOpen, setQPanelOpen] = useState(false);
  const [qPanelStartNew, setQPanelStartNew] = useState(0);
  const [focusQueryId, setFocusQueryId] = useState(null);
  const [monitorBusy, setMonitorBusy] = useState(false);
  const [highlightQid, setHighlightQid] = useState(null);
  const ieObservationsRef = useRef(null);
  const [satisfactionDialog, setSatisfactionDialog] = useState(null);
  const [sendingSat, setSendingSat] = useState(false);
  // Sender picker state for satisfaction emails
  const [satSenders, setSatSenders] = useState({ default: null, alternatives: [] });
  const [satSenderChoice, setSatSenderChoice] = useState('');
  const [satCustomEmail, setSatCustomEmail] = useState('');
  const [satCustomName, setSatCustomName] = useState('');
  // URS-F391: cuando el cuestionario ya esta completo, el admin puede
  // "reabrir" para permitir al voluntario rellenarlo de nuevo.
  const [satReopenConfirm, setSatReopenConfirm] = useState(false);

  useEffect(() => {
    if (!satisfactionDialog) return;
    setSatSenderChoice(''); setSatCustomEmail(''); setSatCustomName('');
    setSatReopenConfirm(false);
    axios.get(`${API_URL}/api/integrations/acumbamail/senders`, { withCredentials: true })
      .then(r => setSatSenders({ default: r.data?.default || null, alternatives: r.data?.alternatives || [] }))
      .catch(() => setSatSenders({ default: null, alternatives: [] }));
  }, [satisfactionDialog]);
  const [fillAnswers, setFillAnswers] = useState({});
  const [fillSaving, setFillSaving] = useState(false);
  // URS-F499 / URS-F503: sub-pestanas para cuestionarios multi-entry
  // (EA y Medicacion Concomitante). `eaEntries` es la copia de trabajo
  // cliente (nombre historico); aplica a ambos tipos.
  const [eaEntries, setEaEntries] = useState(null);
  const [activeEaIdx, setActiveEaIdx] = useState(0);
  // URS-F500: bandera "No aplica" a nivel visita multi-entry.
  const [eaNotApplicable, setEaNotApplicable] = useState(false);
  // Reopen locked questionnaire (admin only)
  const [reopenDialog, setReopenDialog] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  // E-Signature state
  const [showESign, setShowESign] = useState(false);
  // Confirm delete dialog
  const [confirmDeleteVol, setConfirmDeleteVol] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // URS-F323: popup para visualizar motivo de exclusion/abandono.
  const [reasonPopup, setReasonPopup] = useState(null); // { volunteer, kind: 'excluido'|'abandono' } | null
  // In-place appointment dialog (keeps user inside the volunteers tab)
  const [apptPreselect, setApptPreselect] = useState(null);
  // ^^ { studyId, volunteerId, volunteerName, visitCode } | null

  const lb = lang === 'es'
    ? {
        add: 'Asignar Voluntario', search: 'Buscar voluntario por nombre, DNI o ID...', no_assigned: 'No hay voluntarios asignados a este estudio',
        staff_only: 'Personal de la casa',
        vcode: 'V-Code', vol_id: 'ID Voluntario', name: 'Nombre', age: 'Edad', dni: 'DNI', status: 'Estado', assigned_date: 'Asignado', actions: 'Acciones',
        incluido: 'Incluido', completado: 'Finalizado', excluido: 'Excluido', abandono: 'Abandono', apto: 'Apto', no_apto: 'No Apto', pendiente: 'Pendiente',
        change_status: 'Cambiar estado', remove: 'Desasignar', confirm_remove: 'Desasignar este voluntario del estudio?',
        notes: 'Motivo / Notas', save: 'Guardar', cancel: 'Cancelar',
        conflict_title: 'Voluntario en otro estudio', conflict_msg: 'Este voluntario ya esta participando activamente en:',
        conflict_accept: 'Asignar igualmente', conflict_reject: 'No asignar',
        assign_success: 'Voluntario asignado', status_updated: 'Estado actualizado', removed: 'Voluntario desasignado',
        select_vol: 'Seleccionar voluntarios disponibles', no_results: 'No se encontraron voluntarios',
        generate: 'Generar citas', gen_title: 'Generar citas automaticamente',
        gen_desc: 'Se generaran citas para todas las visitas del protocolo a partir de la fecha T0 indicada.',
        gen_base_date: 'Fecha T0 (inicio del estudio para estos voluntarios)', gen_base_time: 'Hora base de las citas',
        gen_select_all: 'Seleccionar todos', gen_select_included: 'Solo incluidos',
        gen_success: 'Citas generadas correctamente', gen_created: 'citas creadas', gen_skipped: 'omitidas (ya existian)', gen_volunteers: 'Voluntarios',
        ie_title: 'Cuestionario de Inclusion-Exclusion', ie_inclusion: 'Criterios de Inclusion', ie_exclusion: 'Criterios de Exclusion',
        ie_observations: 'Observaciones', ie_obs_required: 'Debe completar el campo Observaciones cuando algun criterio de inclusion es "No" o algun criterio de exclusion es "Si".',
        ie_include: 'Apto', ie_exclude: 'No Apto', ie_fill: 'Rellenar cuestionario I/E', ie_view: 'Ver respuestas I/E',
        ie_no_questionnaire: 'No se ha generado el cuestionario de I/E. Guarde primero los datos del protocolo con criterios de inclusion/exclusion.',
        ie_included_success: 'Voluntario incluido y V-code asignado', ie_excluded_success: 'Voluntario marcado como No Apto',
        ie_apto_success: 'Voluntario marcado como Apto',
        ie_cannot_include: 'No se puede marcar como Apto porque no cumple con los criterios del estudio.',
        tab_pending: 'Pendientes', tab_aptos: 'Aptos', tab_included: 'Incluidos', tab_no_aptos: 'No Aptos', tab_excluded: 'Excluidos', tab_abandonos: 'Abandonos', tab_anulados: 'Anulados',
        no_pending: 'No hay voluntarios pendientes de evaluacion', no_aptos: 'No hay voluntarios aptos', no_included: 'No hay voluntarios incluidos', no_no_aptos: 'No hay voluntarios no aptos', no_excluded: 'No hay voluntarios excluidos', no_abandonos: 'No hay voluntarios con abandono', no_anulados: 'No hay voluntarios con cita anulada o no asistida',
        give_t0: 'Dar cita T0',
        ie_completed: 'I/E completado', ie_not_completed: 'I/E pendiente',
        q_not_started: 'No iniciado', q_in_progress: 'En progreso', q_completed: 'Completado',
        q_fill: 'Rellenar cuestionario', q_save: 'Guardar', q_finish: 'Terminar y Guardar',
        q_saved_partial: 'Respuestas guardadas (parcial)', q_saved_complete: 'Cuestionario completado y guardado',
        q_locked_badge: 'Completado y firmado · Solo lectura',
        q_locked_note: 'Este cuestionario fue terminado y firmado. Por integridad de datos (21 CFR Part 11 / ALCOA+) ya no es editable. Si detectas un error, usa el modulo de Queries para solicitar la aclaracion.',
        q_close: 'Cerrar',
        q_reopen: 'Reabrir',
        q_reopen_title: 'Reabrir cuestionario',
        q_reopen_desc: 'Esta accion permite editar de nuevo el cuestionario. Quedara registrada en auditoria junto con tu usuario y la firma anterior se conservara en el historial. Debes indicar un motivo y el cuestionario debera volver a firmarse para quedar completado.',
        q_reopen_reason: 'Motivo de la reapertura',
        q_reopen_reason_ph: 'Ej: Correccion de respuesta erronea en pregunta X validada con el voluntario...',
        q_reopen_confirm: 'Reabrir cuestionario',
        q_reopened: 'Cuestionario reabierto. Vuelve a firmar para completarlo.',
        visits_header: 'Visitas', no_visits_assigned: 'No hay visitas con cuestionarios asignados',
      }
    : {
        add: 'Assign Volunteer', search: 'Search volunteer by name, DNI or ID...', no_assigned: 'No volunteers assigned to this study',
        staff_only: 'Staff members',
        vcode: 'V-Code', vol_id: 'Volunteer ID', name: 'Name', age: 'Age', dni: 'DNI', status: 'Status', assigned_date: 'Assigned', actions: 'Actions',
        incluido: 'Included', completado: 'Completed', excluido: 'Excluded', abandono: 'Withdrawal', apto: 'Eligible', no_apto: 'Not Eligible', pendiente: 'Pending',
        change_status: 'Change status', remove: 'Remove', confirm_remove: 'Remove this volunteer from the study?',
        notes: 'Reason / Notes', save: 'Save', cancel: 'Cancel',
        conflict_title: 'Volunteer in another study', conflict_msg: 'This volunteer is already actively participating in:',
        conflict_accept: 'Assign anyway', conflict_reject: 'Do not assign',
        assign_success: 'Volunteer assigned', status_updated: 'Status updated', removed: 'Volunteer removed',
        select_vol: 'Select available volunteers', no_results: 'No volunteers found',
        generate: 'Generate appointments', gen_title: 'Generate appointments automatically',
        gen_desc: 'Appointments will be generated for all protocol visits starting from the T0 date.',
        gen_base_date: 'T0 date (study start for these volunteers)', gen_base_time: 'Base appointment time',
        gen_select_all: 'Select all', gen_select_included: 'Only included',
        gen_success: 'Appointments generated successfully', gen_created: 'appointments created', gen_skipped: 'skipped (already existed)', gen_volunteers: 'Volunteers',
        ie_title: 'Inclusion-Exclusion Questionnaire', ie_inclusion: 'Inclusion Criteria', ie_exclusion: 'Exclusion Criteria',
        ie_observations: 'Observations', ie_obs_required: 'You must fill in the Observations field when any inclusion criteria is "No" or any exclusion criteria is "Yes".',
        ie_include: 'Eligible', ie_exclude: 'Not Eligible', ie_fill: 'Fill I/E questionnaire', ie_view: 'View I/E responses',
        ie_no_questionnaire: 'No I/E questionnaire generated. Save the protocol data with inclusion/exclusion criteria first.',
        ie_included_success: 'Volunteer included and V-code assigned', ie_excluded_success: 'Volunteer marked as Not Eligible',
        ie_apto_success: 'Volunteer marked as Eligible',
        ie_cannot_include: 'This volunteer cannot be marked as Eligible because they do not meet the study criteria.',
        tab_pending: 'Pending', tab_aptos: 'Eligible', tab_included: 'Included', tab_no_aptos: 'Not Eligible', tab_excluded: 'Excluded', tab_abandonos: 'Withdrawals', tab_anulados: 'Cancelled',
        no_pending: 'No volunteers pending evaluation', no_aptos: 'No eligible volunteers', no_included: 'No included volunteers', no_no_aptos: 'No non-eligible volunteers', no_excluded: 'No excluded volunteers', no_abandonos: 'No withdrawals', no_anulados: 'No volunteers with cancelled or missed appointments',
        give_t0: 'Schedule T0',
        ie_completed: 'I/E completed', ie_not_completed: 'I/E pending',
        q_not_started: 'Not started', q_in_progress: 'In progress', q_completed: 'Completed',
        q_fill: 'Fill questionnaire', q_save: 'Save', q_finish: 'Finish and Save',
        q_saved_partial: 'Responses saved (partial)', q_saved_complete: 'Questionnaire completed and saved',
        q_locked_badge: 'Completed and signed · Read-only',
        q_locked_note: 'This questionnaire was finished and signed. For data integrity (21 CFR Part 11 / ALCOA+) it cannot be edited anymore. If you detect an error, use the Queries module to request a clarification.',
        q_close: 'Close',
        q_reopen: 'Reopen',
        q_reopen_title: 'Reopen questionnaire',
        q_reopen_desc: 'This action allows the questionnaire to be edited again. It will be logged in the audit trail with your user, and the previous signature will be kept in the history. You must provide a reason and the questionnaire will need to be signed again to be marked as completed.',
        q_reopen_reason: 'Reopen reason',
        q_reopen_reason_ph: 'E.g. Correction of an erroneous answer in question X validated with the volunteer...',
        q_reopen_confirm: 'Reopen questionnaire',
        q_reopened: 'Questionnaire reopened. Sign again to complete it.',
        visits_header: 'Visits', no_visits_assigned: 'No visits with assigned questionnaires',
      };

  const load = () => {
    if (!studyId) return;
    Promise.all([
      axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/questionnaires`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/subjective-symptoms`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/appointments?study_id=${studyId}&include_cancelled=true`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([ar, visr, qr, sr, obsr, appr]) => {
      setAssigned(ar.data);
      setStudyVisits(visr.data);
      setStudyQuestionnaires(qr.data);
      setProtocolData(sr.data?.protocol_data || {});
      setStudyStaff(sr.data?.staff || {});
      // URS-F463b: la basal se calcula desde `visr.data` recien cargado.
      // No podemos depender del state `firstProtocolVisitCode` (memo) porque
      // aun no se ha actualizado en este render y quedaria en el default 'T0',
      // lo que descartaba las citas T-14D del mapa apptByVolVisit y hacia
      // que el boton "Citar" no cambiase a "Citado" tras crear la cita.
      const _basalCandidates = (visr.data || []).filter(v => {
        const c = (v.code || '').toUpperCase();
        return c && c !== 'VAL' && c !== 'EA';
      });
      const _basal = _basalCandidates.length
        ? _basalCandidates.reduce((min, v) => ((v.day ?? 0) < (min.day ?? 0) ? v : min), _basalCandidates[0])
        : null;
      const basalCodeUpper = (_basal?.code || 'T0').toUpperCase();
      // URS-F269: indexar las citas ACTIVAS por (volunteer, VAL|basal). Excluimos
      // 'anulada' y 'no_asiste'. Si hay varias, nos quedamos con la mas reciente
      // (orden por date+time descendente).
      // URS-F395: en paralelo, indexamos las citas FALLIDAS (anulada|no_asiste)
      // para mostrarlas en rojo en Pendientes cuando el voluntario no tiene
      // otra cita activa. Se prioriza la mas reciente.
      const apptMap = {};
      const missedMap = {};
      const sorted = (appr.data || []).slice().sort((x, y) => {
        const kx = `${x.date || ''}T${x.time || ''}`;
        const ky = `${y.date || ''}T${y.time || ''}`;
        return ky.localeCompare(kx);
      });
      for (const ap of sorted) {
        const code = (ap.visit_code || '').toUpperCase();
        // URS-F463: aceptamos VAL y la primera visita del protocolo (que puede
        // ser T0 o T-14D u otra segun configuracion).
        if (code !== 'VAL' && code !== basalCodeUpper) continue;
        const vid = ap.volunteer_id;
        if (ap.status === 'anulada' || ap.status === 'no_asiste') {
          if (!missedMap[vid]) missedMap[vid] = {};
          if (!missedMap[vid][code]) missedMap[vid][code] = ap;
          continue;
        }
        if (!apptMap[vid]) apptMap[vid] = {};
        if (!apptMap[vid][code]) apptMap[vid][code] = ap;
      }
      setApptByVolVisit(apptMap);
      setMissedApptByVolVisit(missedMap);
      // Group subjective symptoms by `${volunteer_code}|${visit_code}` so
      // the "Sintoma Subjetivo Paciente" badge can render on the exact
      // visit row where each symptom was recorded.
      const grouped = {};
      for (const o of (obsr.data || [])) {
        const txt = (o.observation || '').trim();
        if (!txt) continue;
        const key = `${o.volunteer_code}|${o.visit_code}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({
          symptom_id: o.symptom_id,
          visit_code: o.visit_code,
          observation: txt,
          created_at: o.created_at,
        });
      }
      setSymptomsByKey(grouped);
    }).catch((err) => { if (err?.response?.status !== 401) toast.error('Error cargando datos'); });
  };
  useEffect(() => { load(); }, [studyId]);

  const loadQueries = useCallback(() => {
    if (!studyId) return;
    axios.get(`${API_URL}/api/studies/${studyId}/queries`, { withCredentials: true })
      .then(r => setStudyQueries(r.data || [])).catch(() => {});
  }, [studyId]);
  useEffect(() => { loadQueries(); }, [loadQueries]);

  useEffect(() => {
    if (autoExpandVolunteerId && assigned.length > 0) {
      toggleExpandVol(autoExpandVolunteerId);
    }
  }, [autoExpandVolunteerId, assigned.length]);

  // Highlight + scroll to volunteer in Pendientes tab (no expand). The
  // highlight stays on until the user navigates away or refreshes — gives
  // the coordinator a stable visual cue while they decide what to do.
  const [highlightedVol, setHighlightedVol] = useState(null);
  useEffect(() => {
    if (!highlightVolunteerId || assigned.length === 0) return;
    setHighlightedVol(highlightVolunteerId);
    const tid = setTimeout(() => {
      const el = document.querySelector(`[data-testid="pending-vol-${highlightVolunteerId}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(tid);
  }, [highlightVolunteerId, assigned.length]);

  const pendientesAll = sortByVCode(assigned.filter(a => a.status === 'pendiente'));
  // URS-F465: dividir Pendientes en dos subpestanas:
  // - Anulados: voluntarios pendiente con VAL cambiada a anulada/no_asiste
  //   y sin cita VAL activa (citada) que la reemplace.
  // - Pendientes: el resto (sin VAL aun o con VAL activa citada).
  const _isValAnulado = (a) => {
    const val_active = (apptByVolVisit[a.volunteer_id] || {})['VAL'];
    const val_missed = (missedApptByVolVisit[a.volunteer_id] || {})['VAL'];
    return !val_active && !!val_missed;
  };
  const anulados = pendientesAll.filter(_isValAnulado);
  const pendientes = pendientesAll.filter(a => !_isValAnulado(a));
  const aptos = sortByVCode(assigned.filter(a => a.status === 'apto'));
  // URS-F344b: los voluntarios auto-completados (terminaron su ultima visita)
  // siguen viendose en la pestana "Incluidos" para preservar acceso a sus
  // consentimientos, historial y cuestionarios firmados. Se distinguen con
  // un badge "Finalizado" en el render. NO se les generan nuevas citas.
  const incluidos = sortByVCode(assigned.filter(a => a.status === 'incluido' || a.status === 'completado'));
  const noAptos = sortByVCode(assigned.filter(a => a.status === 'no_apto'));
  const excluidos = sortByVCode(assigned.filter(a => a.status === 'excluido'));
  const abandonos = sortByVCode(assigned.filter(a => a.status === 'abandono'));

  // URS-F269: cambiar una cita ya creada (VAL o T0). Cancela la cita activa
  // existente (PUT status='anulada') y abre el flujo de crear nueva.
  const handleChangeAppointment = async (appt, a) => {
    const dateLabel = `${new Date(appt.date).toLocaleDateString('es-ES')} ${appt.time || ''}`.trim();
    if (!window.confirm(lang === 'es'
      ? `¿Cambiar la cita ${appt.visit_code} de ${a.volunteer_name} (${dateLabel})? Se cancelara la actual.`
      : `Change ${appt.visit_code} appointment for ${a.volunteer_name} (${dateLabel})? Current one will be cancelled.`)) return;
    try {
      await axios.put(`${API_URL}/api/appointments/${appt.appointment_id}`,
        { status: 'anulada' }, { withCredentials: true });
      setApptByVolVisit(prev => {
        const next = { ...prev };
        if (next[a.volunteer_id]) {
          next[a.volunteer_id] = { ...next[a.volunteer_id] };
          delete next[a.volunteer_id][appt.visit_code];
        }
        return next;
      });
      setApptPreselect({ studyId, volunteerId: a.volunteer_id, volunteerName: a.volunteer_name, visitCode: appt.visit_code });
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  // URS-F269: render "Citado dd/MM HH:MM" si ya hay cita; si no, boton crear.
  const renderCitadoOrCreate = (a, code) => {
    const appt = (apptByVolVisit[a.volunteer_id] || {})[code];
    if (!appt) {
      // URS-F395: si no hay cita activa pero SI hubo una fallida (anulada
      // o no_asiste), la mostramos en rojo con la fecha/hora original y a
      // continuacion el boton para agendar una nueva.
      const missed = (missedApptByVolVisit[a.volunteer_id] || {})[code];
      const label = code === 'VAL'
        ? (lang === 'es' ? 'Cita Valoracion' : 'Assessment Appt')
        : lb.give_t0;
      const createBtn = (
        <Button
          variant="outline" size="sm"
          className="text-xs gap-1 h-7 rounded-sm text-[#1f3e80] border-[#1f3e80]/30"
          data-testid={`give-${code.toLowerCase()}-${a.volunteer_id}`}
          onClick={() => setApptPreselect({ studyId, volunteerId: a.volunteer_id, volunteerName: a.volunteer_name, visitCode: code })}
        >
          <CalendarBlank size={12} />{label}
        </Button>
      );
      if (missed) {
        const mDate = new Date(missed.date);
        const mDateLabel = `${String(mDate.getDate()).padStart(2, '0')}/${String(mDate.getMonth() + 1).padStart(2, '0')}`;
        const mTimeLabel = missed.time || '';
        const statusLabel = missed.status === 'no_asiste'
          ? (lang === 'es' ? 'No asistio' : 'No show')
          : (lang === 'es' ? 'Anulada' : 'Cancelled');
        return (
          <div className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`missed-${code.toLowerCase()}-${a.volunteer_id}`}>
            <Badge
              className="bg-red-50 text-red-700 border-red-200 text-[11px] gap-1 px-2 py-0.5 whitespace-nowrap"
              title={`${statusLabel} - ${lang === 'es' ? 'Cita anterior' : 'Previous appointment'}: ${missed.date}${mTimeLabel ? ' ' + mTimeLabel : ''}`}
            >
              <CalendarBlank size={11} />
              {statusLabel} {mDateLabel} {mTimeLabel}
            </Badge>
            {createBtn}
          </div>
        );
      }
      return createBtn;
    }
    const date = new Date(appt.date);
    const dateLabel = `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}`;
    const timeLabel = appt.time || '';
    return (
      <div className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`citado-${code.toLowerCase()}-${a.volunteer_id}`}>
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[11px] gap-1 px-2 py-0.5 whitespace-nowrap">
          <CalendarBlank size={11} />
          {lang === 'es' ? 'Citado' : 'Booked'} {dateLabel} {timeLabel}
        </Badge>
        <Button
          variant="ghost" size="sm"
          className="h-6 px-2 text-[11px] text-stone-500 hover:text-[#1f3e80]"
          data-testid={`change-${code.toLowerCase()}-${a.volunteer_id}`}
          onClick={() => handleChangeAppointment(appt, a)}
          title={lang === 'es' ? 'Cancelar y crear nueva cita' : 'Cancel and create new appointment'}
        >
          {lang === 'es' ? 'Cambiar' : 'Change'}
        </Button>
      </div>
    );
  };

  // Per-tab quick filter (name or V-code / volunteer number)
  const [tabSearch, setTabSearch] = useState({ pendientes: '', aptos: '', incluidos: '', anulados: '' });
  const filterByTab = (list, key) => {
    const q = (tabSearch[key] || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter(a =>
      (a.volunteer_name || '').toLowerCase().includes(q) ||
      (a.study_volunteer_code || '').toLowerCase().includes(q) ||
      String(a.study_volunteer_num || '').includes(q) ||
      (a.volunteer_id || '').toLowerCase().includes(q) ||
      (a.volunteer_global_id || '').toLowerCase().includes(q) ||
      (a.volunteer_dni || '').toLowerCase().includes(q)
    );
  };
  const pendientesView = filterByTab(pendientes, 'pendientes');
  const aptosView = filterByTab(aptos, 'aptos');
  const incluidosView = filterByTab(incluidos, 'incluidos');
  const anuladosView = filterByTab(anulados, 'anulados');

  const assignedIds = new Set(assigned.map(a => a.volunteer_id));

  // Server-side search for eligible volunteers
  const [eligibleResults, setEligibleResults] = useState([]);
  const [eligibleMeta, setEligibleMeta] = useState({ total_matching: 0, shown: 0, truncated: false, limit: 200 });
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);

  // Unwrap response (server now returns {data, total_matching, shown, truncated, limit};
  // staff=true still returns a raw array for retrocompatibility)
  const unwrapResults = (resp) => {
    if (Array.isArray(resp)) {
      setEligibleResults(resp);
      setEligibleMeta({ total_matching: resp.length, shown: resp.length, truncated: false, limit: 200 });
    } else if (resp && Array.isArray(resp.data)) {
      setEligibleResults(resp.data);
      setEligibleMeta({
        total_matching: resp.total_matching ?? resp.data.length,
        shown: resp.shown ?? resp.data.length,
        truncated: !!resp.truncated,
        limit: resp.limit ?? 200,
      });
    } else {
      setEligibleResults([]);
      setEligibleMeta({ total_matching: 0, shown: 0, truncated: false, limit: 200 });
    }
  };

  useEffect(() => {
    if (staffOnly) {
      // Staff: load all immediately
      setSearchLoading(true);
      axios.get(`${API_URL}/api/studies/${studyId}/eligible-volunteers?staff=true`, { withCredentials: true })
        .then(r => unwrapResults(r.data))
        .catch(() => { setEligibleResults([]); setEligibleMeta({ total_matching: 0, shown: 0, truncated: false, limit: 200 }); })
        .finally(() => setSearchLoading(false));
    } else if (search.length >= 2) {
      // Debounced search
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSearchLoading(true);
        axios.get(`${API_URL}/api/studies/${studyId}/eligible-volunteers?search=${encodeURIComponent(search)}`, { withCredentials: true })
          .then(r => unwrapResults(r.data))
          .catch(() => { setEligibleResults([]); setEligibleMeta({ total_matching: 0, shown: 0, truncated: false, limit: 200 }); })
          .finally(() => setSearchLoading(false));
      }, 300);
    } else {
      setEligibleResults([]);
      setEligibleMeta({ total_matching: 0, shown: 0, truncated: false, limit: 200 });
    }
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search, staffOnly, studyId]);

  const filteredAvailable = eligibleResults;

  const handleAssign = async (vol) => {
    try {
      const { data: check } = await axios.get(`${API_URL}/api/studies/${studyId}/volunteers/check/${vol.volunteer_id}`, { withCredentials: true });
      if (check.conflict === 'same_study') { toast.error(lang === 'es' ? 'Ya esta asignado a este estudio' : 'Already assigned to this study'); return; }
      if (check.conflict === 'other_study') {
        setPendingVol(vol);
        setShowConflict(check.active_studies);
        return;
      }
      await doAssign(vol.volunteer_id, false, vol);
    } catch (err) { toast.error(errText(err)); }
  };

  const doAssign = async (volunteerId, force, volObj = null) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/studies/${studyId}/volunteers`, { volunteer_id: volunteerId, force }, { withCredentials: true });
      toast.success(lb.assign_success);
      setShowConflict(null);
      const volSource = volObj || pendingVol;
      setPendingVol(null);
      setShowAdd(false);
      setSearch('');
      load();
      // URS-F495: tras asignar, abrir dialogo para enviar la HIP por email.
      const pd = (volSource && volSource.personal_data) || {};
      const fullName = `${pd.nombre || ''} ${pd.apellidos || ''}`.trim() || volSource?.display_name || volunteerId;
      const email = pd.email || volSource?.email || '';
      setSendHipDialog({
        volunteerId,
        volunteerName: fullName,
        volunteerEmail: email,
      });
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleStatusChange = async () => {
    if (!showStatusDialog || !statusForm.status) return;
    setSaving(true);
    try {
      // If moving from Apto to Incluido, call the include endpoint to assign V-code
      if (showStatusDialog.status === 'apto' && statusForm.status === 'incluido') {
        const { data } = await axios.post(`${API_URL}/api/studies/${studyId}/volunteers/${showStatusDialog.volunteer_id}/include`, {}, { withCredentials: true });
        toast.success(`${lb.ie_included_success}: ${data.study_volunteer_code}`);
      } else {
        await axios.put(`${API_URL}/api/studies/${studyId}/volunteers/${showStatusDialog.volunteer_id}`, statusForm, { withCredentials: true });
        toast.success(lb.status_updated);
      }
      setShowStatusDialog(null);
      load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleRemove = async (volId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/volunteers/${volId}`, { withCredentials: true });
      toast.success(lb.removed);
      setConfirmDeleteVol(null);
      load();
    } catch (err) { toast.error(errText(err)); }
    setDeleting(false);
  };

  // --- I/E Questionnaire Logic ---
  const openIEDialog = async (vol) => {
    try {
      const { data: questionnaires } = await axios.get(`${API_URL}/api/studies/${studyId}/questionnaires`, { withCredentials: true });
      const ieQ = questionnaires.find(q => q.type === 'inclusion_exclusion');
      if (!ieQ) {
        toast.error(lb.ie_no_questionnaire);
        return;
      }
      setIEQuestions(ieQ.questions || []);
      const existingResponses = vol.ie_responses || {};
      setIEResponses(existingResponses);
      setIEError('');
      setShowIEDialog(vol);
    } catch { toast.error('Error loading questionnaire'); }
  };

  const setIEResponse = (qId, value) => {
    setIEResponses(prev => ({ ...prev, [qId]: value }));
  };

  const validateIEObservations = () => {
    const inclusionQuestions = ieQuestions.filter(q => q.source === 'inclusion');
    const exclusionQuestions = ieQuestions.filter(q => q.source === 'exclusion');
    const observationsQ = ieQuestions.find(q => q.source === 'observations');
    const hasInclusionNo = inclusionQuestions.some(q => ieResponses[q.question_id] === 'No');
    const hasExclusionSi = exclusionQuestions.some(q => ieResponses[q.question_id] === 'Sí');
    if ((hasInclusionNo || hasExclusionSi) && observationsQ) {
      const obsValue = (ieResponses[observationsQ.question_id] || '').trim();
      if (!obsValue) return false;
    }
    return true;
  };

  const allIEAnswered = () => {
    const selectQuestions = ieQuestions.filter(q => q.type === 'select');
    return selectQuestions.every(q => ieResponses[q.question_id]);
  };

  const failsCriteria = () => {
    const inclusionQs = ieQuestions.filter(q => q.source === 'inclusion');
    const exclusionQs = ieQuestions.filter(q => q.source === 'exclusion');
    return inclusionQs.some(q => ieResponses[q.question_id] === 'No') ||
           exclusionQs.some(q => ieResponses[q.question_id] === 'Sí');
  };

  const saveIEResponses = async () => {
    if (!validateIEObservations()) {
      setIEError(lb.ie_obs_required);
      return;
    }
    setIEError('');
    setIESaving(true);
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/volunteers/${showIEDialog.volunteer_id}/ie-responses`, { responses: ieResponses }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Respuestas guardadas' : 'Responses saved');
      load();
    } catch (err) { toast.error(errText(err)); }
    setIESaving(false);
  };

  const handleApto = async (vol) => {
    setIESaving(true);
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/volunteers/${vol.volunteer_id}/ie-responses`, { responses: ieResponses }, { withCredentials: true });
      await axios.post(`${API_URL}/api/studies/${studyId}/volunteers/${vol.volunteer_id}/mark-apto`, {}, { withCredentials: true });
      toast.success(lb.ie_apto_success);
      setShowIEDialog(null);
      load();
    } catch (err) { toast.error(errText(err)); }
    setIESaving(false);
  };

  const handleNoApto = async (vol, notes = '') => {
    setIESaving(true);
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}/volunteers/${vol.volunteer_id}/ie-responses`, { responses: ieResponses }, { withCredentials: true });
      await axios.post(`${API_URL}/api/studies/${studyId}/volunteers/${vol.volunteer_id}/mark-no-apto`, { notes }, { withCredentials: true });
      toast.success(lb.ie_excluded_success);
      setShowIEDialog(null);
      load();
    } catch (err) { toast.error(errText(err)); }
    setIESaving(false);
  };

  // --- Expand Included Volunteer + Visit Questionnaire Logic ---
  const toggleExpandVol = async (volId) => {
    if (expandedVol[volId]) {
      setExpandedVol(prev => ({ ...prev, [volId]: false }));
      return;
    }
    setExpandedVol(prev => ({ ...prev, [volId]: true }));
    try {
      const [respData, apptData] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/volunteers/${volId}/visit-responses`, { withCredentials: true }),
        axios.get(`${API_URL}/api/appointments?study_id=${studyId}&volunteer_id=${volId}&include_cancelled=true`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setVisitResponses(prev => ({ ...prev, [volId]: respData.data }));
      setVolAppointments(prev => ({ ...prev, [volId]: apptData.data || [] }));
    } catch { }
  };

  const getQStatus = (volId, visitId, qId) => {
    const responses = visitResponses[volId] || [];
    const r = responses.find(rr => rr.visit_id === visitId && rr.questionnaire_id === qId);
    if (!r) return 'not_started';
    return r.status;
  };

  const openFillQ = async (vol, visit, questionnaire, responsesOverride = null) => {
    const responses = responsesOverride || visitResponses[vol.volunteer_id] || [];
    const isEA = isMultiEntryType(questionnaire?.type);

    // URS-F340: helper para pre-rellenar "No valorable" en tolerancia.
    const seedAnswersFor = (baseAnswers) => {
      const seed = { ...(baseAnswers || {}) };
      const sqType = (questionnaire?.type || '').toLowerCase();
      for (const q of (questionnaire?.questions || [])) {
        if (q.type !== 'table_select') continue;
        const isTol = (q.category === 'tolerancia') || sqType === 'tolerancia';
        if (!isTol) continue;
        const cur = (typeof seed[q.question_id] === 'object' && seed[q.question_id]) || {};
        const next = { ...cur };
        for (const opt of (q.options || [])) {
          if (typeof opt === 'string' && opt.trim().startsWith('##')) continue;
          const key = `__rel:${opt}`;
          if (!next[key]) next[key] = 'No valorable';
        }
        seed[q.question_id] = next;
      }
      return seed;
    };

    if (isEA) {
      // URS-F499: cargar todas las entries del cuestionario EA.
      try {
        const { data } = await axios.get(
          `${API_URL}/api/studies/${studyId}/volunteers/${vol.volunteer_id}/visit-responses/${visit.visit_id}/${questionnaire.questionnaire_id}/entries`,
          { withCredentials: true }
        );
        let entries = Array.isArray(data) ? data : [];
        if (entries.length === 0) {
          // Auto-crear EA1 vacia para que el usuario pueda empezar.
          const { data: created } = await axios.post(
            `${API_URL}/api/studies/${studyId}/volunteers/${vol.volunteer_id}/visit-responses/${visit.visit_id}/${questionnaire.questionnaire_id}/entries`,
            {}, { withCredentials: true }
          );
          entries = [created];
        }
        // Estado local por entry.
        const localEntries = entries.map(e => ({
          entry_index: e.entry_index || 0,
          answers: seedAnswersFor(e.answers || {}),
          status: e.status || 'not_started',
          signature: e.signature || null,
          response_id: e.response_id || null,
          monitoring: e.monitoring || {},
          monitored: e.monitored || null,
          not_applicable: !!e.not_applicable,
        }));
        setEaEntries(localEntries);
        setActiveEaIdx(0);
        setFillAnswers(localEntries[0].answers);
        // URS-F500: la bandera "No aplica" es a nivel visita; se lee de
        // cualquier entry (deberia estar sincronizada). Usamos entry 0.
        setEaNotApplicable(!!entries[0]?.not_applicable);
        const readonly = localEntries[0].status === 'completed';
        setFillQ({ vol, visit, questionnaire, readonly, existing: entries[0] });
      } catch (err) {
        toast.error(errText(err));
      }
      return;
    }

    // --- Cuestionario NO-EA: flujo clasico single-entry ---
    const existing = responses.find(r => r.visit_id === visit.visit_id && r.questionnaire_id === questionnaire.questionnaire_id);
    setEaEntries(null);
    setActiveEaIdx(0);
    setEaNotApplicable(false);
    setFillAnswers(seedAnswersFor(existing?.answers || {}));
    const readonly = existing?.status === 'completed';
    setFillQ({ vol, visit, questionnaire, readonly, existing });
  };

  // URS-F499: cambiar sub-pestana EA (guarda la actual en memoria local
  // sin persistir, y carga la siguiente).
  const switchEaTab = (newIdx) => {
    if (!eaEntries || newIdx === activeEaIdx) return;
    setEaEntries(prev => {
      const next = [...prev];
      next[activeEaIdx] = { ...next[activeEaIdx], answers: fillAnswers };
      return next;
    });
    const target = eaEntries[newIdx];
    setActiveEaIdx(newIdx);
    setFillAnswers(target.answers || {});
    setFillQ(prev => prev ? { ...prev, readonly: target.status === 'completed', existing: { ...target } } : prev);
  };

  // URS-F499: añadir nueva sub-pestana EA (POST /entries).
  const addEaEntry = async () => {
    if (!fillQ || !eaEntries) return;
    try {
      // Guardar en memoria la actual antes de crear la nueva.
      setEaEntries(prev => {
        const next = [...prev];
        next[activeEaIdx] = { ...next[activeEaIdx], answers: fillAnswers };
        return next;
      });
      const { data: created } = await axios.post(
        `${API_URL}/api/studies/${studyId}/volunteers/${fillQ.vol.volunteer_id}/visit-responses/${fillQ.visit.visit_id}/${fillQ.questionnaire.questionnaire_id}/entries`,
        {}, { withCredentials: true }
      );
      const newLocal = {
        entry_index: created.entry_index,
        answers: {},
        status: 'not_started',
        signature: null,
        response_id: created.response_id || null,
      };
      setEaEntries(prev => {
        const next = [...prev, newLocal];
        setActiveEaIdx(next.length - 1);
        setFillAnswers({});
        setFillQ(cur => cur ? { ...cur, readonly: false, existing: newLocal } : cur);
        return next;
      });
      toast.success(lang === 'es' ? `EA${created.entry_index + 1} añadido` : `AE${created.entry_index + 1} added`);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  // URS-F499: eliminar sub-pestana EA (DELETE /entries/{idx}).
  const removeEaEntry = async (idx) => {
    if (!fillQ || !eaEntries) return;
    const target = eaEntries[idx];
    if (!target) return;
    if (!window.confirm(lang === 'es' ? `¿Eliminar EA${target.entry_index + 1}? Esta accion es irreversible.` : `Delete AE${target.entry_index + 1}? This action is irreversible.`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/studies/${studyId}/volunteers/${fillQ.vol.volunteer_id}/visit-responses/${fillQ.visit.visit_id}/${fillQ.questionnaire.questionnaire_id}/entries/${target.entry_index}`,
        { withCredentials: true }
      );
      const next = eaEntries.filter((_, i) => i !== idx);
      setEaEntries(next);
      const newActive = Math.max(0, Math.min(activeEaIdx, next.length - 1));
      setActiveEaIdx(newActive);
      const t = next[newActive];
      setFillAnswers(t?.answers || {});
      setFillQ(cur => cur ? { ...cur, readonly: t?.status === 'completed', existing: { ...t } } : cur);
      toast.success(lang === 'es' ? 'EA eliminado' : 'AE removed');
    } catch (err) {
      toast.error(errText(err));
    }
  };


  // Logica condicional: una pregunta con `condition` solo se muestra/exige si
  // la pregunta Si/No referida tiene exactamente el valor esperado.
  const isConditionMet = (q, answers) => {
    const c = q?.condition;
    if (!c || !c.question_id) return true;
    return (answers || {})[c.question_id] === c.equals;
  };

  // URS-F342: validar que el cuestionario esta 100% respondido antes de
  // marcarlo como completed. Devuelve la lista de preguntas (nombres) que
  // faltan; vacia si todo OK.
  const missingAnswers = () => {
    if (!fillQ?.questionnaire?.questions) return [];
    const missing = [];
    // Helper estricto: considera vacio cualquier valor que no sea un string/numero
    // con contenido util. Importante para derm_scale (cuya respuesta puede
    // ser "0" -> grado valido y NO debe contar como vacio).
    const isEmpty = (v) => {
      if (v === undefined || v === null) return true;
      if (typeof v === 'string') return v.trim() === '';
      if (typeof v === 'number') return false; // numero valido
      if (Array.isArray(v)) return v.length === 0;
      if (typeof v === 'object') return Object.keys(v).length === 0;
      return false;
    };
    for (const q of fillQ.questionnaire.questions) {
      const text = q.text_es || q.text_en || q.text || q.title_es || q.title_en || '';
      // Saltar marcadores de seccion (texto que empieza por '##' o tipos
      // estructurales que NO requieren respuesta).
      if (typeof text === 'string' && text.trim().startsWith('##')) continue;
      if (q.type === 'section' || q.type === 'header' || q.type === 'section_header') continue;
      // Salto condicional: si la condicion no se cumple, la pregunta no se exige.
      if (!isConditionMet(q, fillAnswers)) continue;
      const ans = fillAnswers[q.question_id];
      const label = (typeof text === 'string' && text.trim()) ? text.trim() : (q.question_id || '(sin titulo)');
      if (q.type === 'boolean_with_obs') {
        if (isEmpty(ans)) { missing.push(label); continue; }
        if (ans === 'si' && isEmpty(fillAnswers[q.question_id + '_obs'])) {
          missing.push(`${label} (observacion requerida)`);
        }
        continue;
      }
      if (q.type === 'table_select') {
        const obj = (typeof ans === 'object' && ans) || {};
        const opts = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
        // cada opcion necesita su value (clave directa) y su relacion (__rel:opt)
        for (const opt of opts) {
          if (isEmpty(obj[opt])) { missing.push(`${label} — ${opt}`); }
        }
        continue;
      }
      if (q.type === 'table_text') {
        const obj = (typeof ans === 'object' && ans) || {};
        const opts = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
        for (const opt of opts) {
          if (isEmpty(obj[opt])) { missing.push(`${label} — ${opt}`); }
        }
        continue;
      }
      // URS-F352: custom_table — cada celda interior es obligatoria.
      if (q.type === 'custom_table') {
        const cells = customTableMissing(q, ans);
        if (cells.length) {
          for (const c of cells) missing.push(`${label} — ${c}`);
        }
        continue;
      }
      // URS-F354: nps_multi — cada sub-apartado obligatorio.
      if (q.type === 'nps_multi') {
        const obj = (typeof ans === 'object' && ans) || {};
        const subs = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
        for (const sub of subs) {
          if (isEmpty(obj[sub])) { missing.push(`${label} — ${sub}`); }
        }
        continue;
      }
      // URS-F358: composite_scale — cada sub-apartado debe tener un valor.
      if (q.type === 'composite_scale') {
        const subs = compositeScaleMissing(q, ans);
        for (const s of subs) missing.push(`${label} — ${s}`);
        continue;
      }
      // URS-F502: date_ongoing — valida si ongoing=true (sin fecha)
      // O si date tiene contenido. Ambas caras del OR son validas.
      if (q.type === 'date_ongoing') {
        const val = (typeof ans === 'object' && ans !== null) ? ans : {};
        if (!val.ongoing && isEmpty(val.date)) missing.push(label);
        continue;
      }
      // Likert / scale / derm_scale / nps / select / text / number / textarea /
      // date / boolean / masi: una unica respuesta. El `isEmpty(ans)` la valida.
      if (isEmpty(ans)) missing.push(label);
    }
    return missing;
  };

  const handleSaveQ = async (completed, signature = null) => {
    if (!fillQ) return;
    // URS-F342: bloquear "completar" si faltan respuestas.
    // URS-F500: si la visita EA esta marcada "No aplica" saltamos el
    // chequeo (no se rellena nada por definicion).
    if (completed && !eaNotApplicable) {
      const miss = missingAnswers();
      if (miss.length) {
        const preview = miss.slice(0, 5).map((m, i) => `${i + 1}. ${m}`).join('\n');
        const more = miss.length > 5 ? `\n... y ${miss.length - 5} mas` : '';
        toast.error(
          (lang === 'es'
            ? `Faltan ${miss.length} respuesta(s) para poder completar el cuestionario:\n`
            : `${miss.length} answer(s) missing to complete the questionnaire:\n`) + preview + more,
          { duration: 8000 }
        );
        return;
      }
    }
    setFillSaving(true);
    try {
      // URS-F499: multi-entry — enviar entry_index si estamos en EA.
      const activeEntry = eaEntries ? eaEntries[activeEaIdx] : null;
      const isEA = isMultiEntryType(fillQ?.questionnaire?.type);
      // Limpieza: no guardar respuestas de preguntas ocultas por condicion.
      const cleanedAnswers = { ...fillAnswers };
      for (const q of (fillQ?.questionnaire?.questions || [])) {
        if (q?.condition?.question_id && !isConditionMet(q, fillAnswers)) {
          delete cleanedAnswers[q.question_id];
          delete cleanedAnswers[q.question_id + '_obs'];
        }
      }
      const payload = {
        visit_id: fillQ.visit.visit_id,
        questionnaire_id: fillQ.questionnaire.questionnaire_id,
        answers: eaNotApplicable ? {} : cleanedAnswers,
        completed,
      };
      if (activeEntry) payload.entry_index = activeEntry.entry_index;
      if (isEA) payload.not_applicable = eaNotApplicable;
      if (signature) payload.signature = signature;
      await axios.put(`${API_URL}/api/studies/${studyId}/volunteers/${fillQ.vol.volunteer_id}/visit-responses`, payload, { withCredentials: true });
      toast.success(completed ? lb.q_saved_complete : lb.q_saved_partial);
      const { data } = await axios.get(`${API_URL}/api/studies/${studyId}/volunteers/${fillQ.vol.volunteer_id}/visit-responses`, { withCredentials: true });
      setVisitResponses(prev => ({ ...prev, [fillQ.vol.volunteer_id]: data }));
      // URS-F499: cerrar tras guardar (parcial o completo), igual que el
      // flujo clasico. El usuario reabre el cuestionario cuando quiera.
      setFillQ(null);
      setEaEntries(null);
      setEaNotApplicable(false);
    } catch (err) { toast.error(errText(err)); }
    setFillSaving(false);
  };

  const handleReopen = async () => {
    if (!fillQ) return;
    if (!reopenReason.trim()) {
      toast.error(lang === 'es' ? 'Introduce un motivo' : 'Enter a reason');
      return;
    }
    setReopening(true);
    try {
      await axios.post(
        `${API_URL}/api/studies/${studyId}/volunteers/${fillQ.vol.volunteer_id}/visit-responses/reopen`,
        {
          visit_id: fillQ.visit.visit_id,
          questionnaire_id: fillQ.questionnaire.questionnaire_id,
          reason: reopenReason.trim(),
        },
        { withCredentials: true }
      );
      toast.success(lb.q_reopened);
      const { data } = await axios.get(`${API_URL}/api/studies/${studyId}/volunteers/${fillQ.vol.volunteer_id}/visit-responses`, { withCredentials: true });
      setVisitResponses(prev => ({ ...prev, [fillQ.vol.volunteer_id]: data }));
      // Switch the current dialog into editable mode (keep answers loaded)
      setFillQ(prev => prev ? { ...prev, readonly: false, existing: { ...(prev.existing || {}), status: 'in_progress', signature: null } } : prev);
      setReopenDialog(false);
      setReopenReason('');
    } catch (err) {
      toast.error(errText(err));
    }
    setReopening(false);
  };

  // --- Monitorización y queries del cuestionario ---
  const isStudyMonitor = (studyStaff?.monitor || []).map(String).includes(String(user?.id));
  const activeEntryIndex = eaEntries ? (eaEntries[activeEaIdx]?.entry_index || 0) : 0;
  const fqQueries = fillQ ? queriesForQuestionnaire(studyQueries, fillQ, activeEntryIndex) : [];
  const fqOpenQueries = fqQueries.filter(isQueryOpen);
  const fqMonitoring = fillQ?.existing?.monitoring || {};
  const fqMonitored = fillQ?.existing?.monitored || null;
  const fqCompleted = fillQ?.existing?.status === 'completed';
  const fqRequiredQids = fillQ ? monitorableQids(fillQ.questionnaire, fillAnswers, eaNotApplicable) : [];
  const fqMissingMarks = fqRequiredQids.filter(q => !fqMonitoring[q]).length;
  const fqKey = fillQ ? `${fillQ.vol.volunteer_id}|${fillQ.visit.visit_id}|${fillQ.questionnaire.questionnaire_id}|${activeEntryIndex}` : '';
  const monitorTarget = fillQ ? {
    volunteer_id: fillQ.vol.volunteer_id, visit_id: fillQ.visit.visit_id,
    questionnaire_id: fillQ.questionnaire.questionnaire_id, entry_index: activeEntryIndex,
  } : null;
  const monitorOffReason = !isStudyMonitor
    ? (lang === 'es' ? 'Solo el monitor del estudio' : 'Study monitor only')
    : (lang === 'es' ? 'El cuestionario no está terminado' : 'The questionnaire is not finished');

  useEffect(() => {
    if (!fqKey) { setQPanelOpen(false); setFocusQueryId(null); return; }
    setQPanelOpen(fqQueries.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fqKey]);

  const patchExisting = (patch) => {
    setFillQ(prev => prev ? { ...prev, existing: { ...(prev.existing || {}), ...patch } } : prev);
    setEaEntries(prev => prev ? prev.map((e, i) => (i === activeEaIdx ? { ...e, ...patch } : e)) : prev);
    const volId = fillQ?.vol?.volunteer_id;
    if (volId) {
      axios.get(`${API_URL}/api/studies/${studyId}/volunteers/${volId}/visit-responses`, { withCredentials: true })
        .then(({ data }) => setVisitResponses(prev => ({ ...prev, [volId]: data }))).catch(() => {});
    }
  };

  const toggleMonitor = async (qid, checked) => {
    if (!monitorTarget) return;
    setMonitorBusy(true);
    try {
      const { data } = await monitoringApi.check(studyId, monitorTarget.volunteer_id, { ...monitorTarget, question_id: qid, checked });
      patchExisting({ monitoring: data.monitoring || {}, monitored: data.monitored || null });
    } catch (e) { toast.error(errText(e)); }
    setMonitorBusy(false);
  };

  const goToQuestion = (qid) => {
    setHighlightQid(qid);
    const el = document.getElementById(`fq-card-${qid}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => setHighlightQid(cur => (cur === qid ? null : cur)), 2500);
  };

  const openQueryPanel = (queryId = null) => {
    setQPanelOpen(true);
    if (queryId) setFocusQueryId(queryId);
    else if (fqQueries.length === 0 && (isAdmin || isStudyMonitor)) setQPanelStartNew(n => n + 1);
  };

  const canCloseQuery = (q) => isAdmin || isStudyMonitor || q.created_by === user?.email;

  // Monitorizado = todas las entries del cuestionario terminadas y monitorizadas.
  const getQMonitored = (volId, visitId, qId) => {
    const docs = (visitResponses[volId] || []).filter(rr => rr.visit_id === visitId && rr.questionnaire_id === qId);
    return docs.length > 0 && docs.every(d => d.status === 'completed' && d.monitored);
  };

  // Aviso al contenedor (pestaña Monitorización) cuando se cierra el cuestionario.
  const wasFillOpen = useRef(false);
  useEffect(() => {
    if (fillQ) { wasFillOpen.current = true; return; }
    if (wasFillOpen.current) { wasFillOpen.current = false; if (onFillClosed) onFillClosed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillQ]);

  // Enlace directo (?open_q=visit:cuestionario:entry&query_id=...) desde Queries o Avisos.
  const deepLinkDone = useRef('');
  useEffect(() => {
    if (!openQuestionnaireKey || !autoExpandVolunteerId || deepLinkDone.current === openQuestionnaireKey) return;
    if (!assigned.length || !studyVisits.length || !studyQuestionnaires.length) return;
    const [visitId, qnId] = openQuestionnaireKey.split(':');
    const vol = assigned.find(a => a.volunteer_id === autoExpandVolunteerId);
    const visit = studyVisits.find(v => v.visit_id === visitId);
    const qn = studyQuestionnaires.find(q => q.questionnaire_id === qnId);
    if (!vol || !visit || !qn) return;
    deepLinkDone.current = openQuestionnaireKey;
    axios.get(`${API_URL}/api/studies/${studyId}/volunteers/${vol.volunteer_id}/visit-responses`, { withCredentials: true })
      .then(({ data }) => {
        setVisitResponses(prev => ({ ...prev, [vol.volunteer_id]: data }));
        return openFillQ(vol, visit, qn, data);
      })
      .then(() => { if (openQueryId) { setFocusQueryId(openQueryId); setQPanelOpen(true); } })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openQuestionnaireKey, openQueryId, autoExpandVolunteerId, assigned.length, studyVisits.length, studyQuestionnaires.length]);

  // --- Generate Appointments Logic ---
  const [studySlots, setStudySlots] = useState([]);
  const [outOfScheduleConfirm, setOutOfScheduleConfirm] = useState(null); // {outRows: idx[], inCount, outCount}
  // URS-F424: popup para confirmar creacion de citas sobre huecos con bloqueo total.
  const [globalBlockConfirm, setGlobalBlockConfirm] = useState(null); // {conflicts:[...]}
  const openGenerate = (singleVolId = null) => {
    setGenForm({
      volunteer_ids: singleVolId ? [singleVolId] : [],
      base_date: '',
      base_time: '09:00',
    });
    setGenResult(null);
    setEmailSendResult(null);
    setSendEmailWithAppts(true);
    setGenStep('config');
    setPreviewAppts([]);
    setGenVolSearch('');
    setShowGenerate(true);
    // Load THIS study's schedule slots so the preview step can check whether
    // each generated date/time falls inside a programmed slot.
    axios.get(`${API_URL}/api/schedule/all?study_id=${studyId}`, { withCredentials: true })
      .then(r => setStudySlots(Array.isArray(r.data) ? r.data.filter(s => s.study_id === studyId) : []))
      .catch(() => setStudySlots([]));
  };

  // True if (date, time) falls inside any programmed slot for this study.
  // If the study has no schedule defined we consider every slot valid
  // (otherwise we would block creation entirely on un-scheduled studies).
  const isInStudySchedule = useCallback((date, time) => {
    if (!date || !time) return true;
    if (!studySlots || studySlots.length === 0) return true;
    return studySlots.some(s => s.date === date && s.start_time <= time && s.end_time > time);
  }, [studySlots]);

  const toggleGenVol = (volId) => {
    setGenForm(p => ({
      ...p,
      volunteer_ids: p.volunteer_ids.includes(volId) ? p.volunteer_ids.filter(v => v !== volId) : [...p.volunteer_ids, volId]
    }));
  };

  // Sync calendar day with selected T0 (base_date) so user sees the planned day
  useEffect(() => {
    if (showGenerate && genForm.base_date) setCalendarDay(genForm.base_date);
  }, [showGenerate, genForm.base_date]);

  // When entering preview step, sync calendar to the active row's date
  useEffect(() => {
    if (genStep === 'preview' && previewAppts[activePreviewIdx]?.date) {
      setCalendarDay(previewAppts[activePreviewIdx].date);
    }
  }, [genStep, activePreviewIdx, previewAppts]);

  // Load all appointments + schedule + blocks for the selected calendar day across studies
  // (only when the preview step is visible; saves an unnecessary request in config)
  useEffect(() => {
    if (!showGenerate || genStep !== 'preview') return;
    setLoadingDayAppts(true);
    Promise.all([
      axios.get(`${API_URL}/api/appointments?date_from=${calendarDay}&date_to=${calendarDay}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/schedule/all`, { withCredentials: true }),
      axios.get(`${API_URL}/api/schedule/blocks?date_from=${calendarDay}&date_to=${calendarDay}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
    ])
      .then(([ar, scr, bks, sr]) => {
        setDayAppointments(Array.isArray(ar.data) ? ar.data : []);
        setDayScheduleSlots(Array.isArray(scr.data) ? scr.data.filter(s => s.date === calendarDay) : []);
        setDayScheduleBlocks(Array.isArray(bks.data) ? bks.data.filter(b => b.date === calendarDay) : []);
        setAllStudies(Array.isArray(sr.data) ? sr.data : []);
      })
      .catch(() => {
        setDayAppointments([]);
        setDayScheduleSlots([]);
        setDayScheduleBlocks([]);
      })
      .finally(() => setLoadingDayAppts(false));
  }, [showGenerate, genStep, calendarDay]);

  const handlePreview = async () => {
    if (!genForm.base_date) { toast.error(lang === 'es' ? 'La fecha T0 es obligatoria' : 'T0 date is required'); return; }
    if (genForm.volunteer_ids.length === 0) { toast.error(lang === 'es' ? 'Selecciona al menos un voluntario' : 'Select at least one volunteer'); return; }
    try {
      const { data: visits } = await axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true });
      if (visits.length === 0) { toast.error(lang === 'es' ? 'No hay visitas definidas en el protocolo' : 'No visits defined in protocol'); return; }
      // Use UTC math to avoid local-vs-UTC drift (in CEST Madrid is UTC+2,
      // so `new Date('2026-07-18T00:00:00')` and then `toISOString()`
      // would otherwise roll back one day). Working in UTC throughout
      // keeps the offset arithmetic identical regardless of the server
      // or browser timezone.
      const [byY, byM, byD] = genForm.base_date.split('-').map(Number);
      const baseUTC = Date.UTC(byY, byM - 1, byD);
      const preview = [];
      let skippedDone = 0;
      let skippedEA = 0;
      for (const volId of genForm.volunteer_ids) {
        const vol = assigned.find(a => a.volunteer_id === volId);
        if (!vol) continue;
        // URS-F344b: voluntarios ya completados no deben recibir nuevas citas.
        if (vol.status === 'completado') continue;
        // For volunteers already INCLUDED in the study, skip Valoracion (VAL)
        // and Tiempo 0 (T0) because by the time inclusion happens those visits
        // have already taken place; they should not be re-generated.
        const isIncluded = vol.status === 'incluido';
        for (const visit of visits) {
          // Eventos Adversos visits are handled outside the batch
          // generator (they are created on-demand by another flow).
          if (visit.visit_type === 'eventos_adversos' || visit.code === 'EA') {
            skippedEA += 1;
            continue;
          }
          // URS-F463: al generar el resto para un voluntario INCLUIDO, omitimos
          // VAL y la primera visita del protocolo (basal), no solo T0.
          if (isIncluded && (visit.code === 'VAL' || visit.code === firstProtocolVisitCode || visit.visit_type === 'valoracion')) {
            skippedDone += 1;
            continue;
          }
          // Defensive cast: some legacy/imported studies store visit.day
          // as a string ("14") instead of int (14). parseInt with base 10
          // guarantees correct arithmetic.
          const dayOffset = parseInt(visit.day, 10) || 0;
          // URS-F444: offset intra-dia en minutos (T30MIN, T2H, T1D+30MIN...).
          // Puede ser negativo (T-30MIN para pre-dosis).
          const minuteOffset = parseInt(visit.minute_offset, 10) || 0;
          // Calcular fecha + hora ajustadas por dayOffset + minuteOffset.
          // Si la suma se sale del dia (ej. T1D-2H antes de las 02:00) se
          // traslada la fecha al dia anterior/siguiente.
          let carryDays = 0;
          let timeStr = genForm.base_time;
          if (/^\d{2}:\d{2}$/.test(timeStr) && minuteOffset !== 0) {
            const [bh, bm] = timeStr.split(':').map(Number);
            let total = bh * 60 + bm + minuteOffset;
            while (total < 0) { total += 24 * 60; carryDays -= 1; }
            while (total >= 24 * 60) { total -= 24 * 60; carryDays += 1; }
            const hh = String(Math.floor(total / 60)).padStart(2, '0');
            const mm = String(total % 60).padStart(2, '0');
            timeStr = `${hh}:${mm}`;
          }
          const visitUTC = baseUTC + (dayOffset + carryDays) * 86400000;
          const vd = new Date(visitUTC);
          const dateStr = `${vd.getUTCFullYear()}-${String(vd.getUTCMonth() + 1).padStart(2, '0')}-${String(vd.getUTCDate()).padStart(2, '0')}`;
          preview.push({
            volunteer_id: volId,
            study_volunteer_code: vol.study_volunteer_code,
            volunteer_name: vol.volunteer_name,
            visit_code: visit.code,
            visit_name: visit.name || '',
            visit_day: dayOffset,
            minute_offset: minuteOffset,
            duration_minutes: visit.duration_minutes || 15,
            date: dateStr,
            time: timeStr,
          });
        }
      }
      if (preview.length === 0) {
        toast.error(lang === 'es'
          ? 'No hay visitas para generar. Los voluntarios incluidos ya tienen VAL y T0 realizados.'
          : 'No visits to generate. Included volunteers already have VAL and T0 done.');
        return;
      }
      if (skippedDone > 0) {
        toast.success(lang === 'es'
          ? `Se omitieron ${skippedDone} citas de VAL/T0 ya realizadas para voluntarios incluidos.`
          : `Skipped ${skippedDone} VAL/T0 visits already done for included volunteers.`);
      }
      setPreviewAppts(preview);
      setActivePreviewIdx(0);
      setGenStep('preview');
    } catch { toast.error('Error loading visits'); }
  };

  const updatePreviewAppt = (idx, field, value) => {
    setPreviewAppts(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const printCreatedAppointments = (appts) => {
    // URS-F491 (privacidad/ciego): imprimimos el codigo del voluntario en
    // lugar de su nombre. El subtipo del estudio (studyPhase) SI se
    // imprime — informacion tecnica no identificativa.
    const grouped = {};
    appts.forEach(a => {
      if (!grouped[a.volunteer_id]) grouped[a.volunteer_id] = { code: a.study_volunteer_code || a.volunteer_id, appts: [] };
      grouped[a.volunteer_id].appts.push(a);
    });
    const logoUrl = window.location.origin + '/logo-goya.png';
    const pages = Object.values(grouped).map(vol => {
      const rows = vol.appts.map(a => `<tr>
        <td class="c-date">${a.date}</td>
        <td class="c-time">${a.time || '-'}</td>
        <td class="c-visit">${a.visit_code || ''}<span class="study-sub">${studyPhase || ''}</span></td>
      </tr>`).join('');
      return `<div class="ticket">
        <div class="header">
          <img src="${logoUrl}" alt="Goya Innova" />
          <div class="phone">Tel: 911 928 904</div>
          <div class="ev-clinicas">Evaluaciones Cl\u00ednicas</div>
          <div class="name">${vol.code}</div>
          <div class="subtitle">CITAS</div>
        </div>
        <table><thead><tr><th>Fecha</th><th>Hora</th><th>Visita</th></tr></thead><tbody>${rows}</tbody></table>
      </div>`;
    }).join('');
    // URS-F250: ticket impresion con fuentes ampliadas para mejor legibilidad.
    // URS-F309: margen superior reducido, footer eliminado, nombre voluntario mas pequeno.
    // URS-F310: longitud del ticket reducida ~20% (fuentes y paddings verticales).
    const html = `<!DOCTYPE html><html><head><title>Citas</title><style>
      @page { size: 80mm auto; margin: 0 1mm 1mm 1mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; width: 76mm; padding: 0 1mm 1.5mm 1mm; color: #000; font-size: 12px; line-height: 1.3; }
      .ticket { page-break-after: always; }
      .ticket:last-child { page-break-after: auto; }
      .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
      .header img { width: 55mm; height: auto; margin-bottom: 0.5mm; filter: grayscale(100%) contrast(160%); }
      .header .phone { font-size: 11px; margin-top: 0.8mm; font-weight: 600; }
      .header .ev-clinicas { font-size: 10px; margin-top: 0.6mm; letter-spacing: 0.3px; }
      .header .name { font-size: 12px; font-weight: 700; margin-top: 1.5mm; text-transform: uppercase; line-height: 1.15; }
      .header .subtitle { font-size: 11px; margin-top: 1mm; font-weight: 600; letter-spacing: 1px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 1.5mm; }
      th { font-size: 10px; text-align: left; border-bottom: 2px solid #000; padding: 1mm 1mm; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
      td { font-size: 13px; padding: 2mm 1mm; border-bottom: 1px dashed #999; font-weight: 600; vertical-align: top; }
      td.c-date { white-space: nowrap; }
      td.c-time { font-weight: 700; }
      td.c-visit { font-weight: 700; }
      td.c-visit .study-sub { display: block; font-size: 9px; font-weight: 500; color: #000; margin-top: 0.4mm; text-transform: uppercase; letter-spacing: 0.3px; }
    </style></head><body>${pages}
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.open();
    w.document.writeln(html);
    w.document.close();
  };

  const handleConfirmGenerate = async (skipScheduleCheck = false, forceGlobalBlock = false) => {
    // Pre-flight: warn the user if any of the generated date/time pairs falls
    // outside the study's programmed schedule. The user can still continue
    // ("crear igualmente") but at least is aware of the deviation.
    if (!skipScheduleCheck && studySlots.length > 0) {
      const outRows = previewAppts
        .map((a, i) => ({ a, i }))
        .filter(({ a }) => !isInStudySchedule(a.date, a.time));
      if (outRows.length > 0) {
        setOutOfScheduleConfirm({
          outRows: outRows.map(o => o.i),
          outCount: outRows.length,
          inCount: previewAppts.length - outRows.length,
        });
        return;
      }
    }
    setOutOfScheduleConfirm(null);
    setGenerating(true);
    try {
      const batch = previewAppts.map(a => ({
        study_id: studyId,
        volunteer_id: a.volunteer_id,
        study_volunteer_code: a.study_volunteer_code,
        visit_code: a.visit_code,
        date: a.date,
        time: a.time,
        duration_minutes: a.duration_minutes || 15,
      }));
      // URS-F424: si el usuario ya confirmo el popup de bloqueo global, incluimos
      // el flag force_global_block=true para que el backend cree las citas.
      const payload = { appointments: batch };
      if (forceGlobalBlock) payload.force_global_block = true;
      // URS-F459: reemplazar citas existentes (vivas, no terminadas).
      if (replaceExistingAppts) payload.replace_existing = true;
      const { data } = await axios.post(`${API_URL}/api/appointments/batch`, payload, { withCredentials: true });
      setGenResult(data);
      setGenStep('result');
      // URS-F459: informar al usuario del desglose creadas + reemplazadas.
      const total = (data.created || 0) + (data.replaced || 0);
      if (data.replaced > 0) {
        toast.success(
          lang === 'es'
            ? `${data.created} creadas, ${data.replaced} reemplazadas`
            : `${data.created} created, ${data.replaced} replaced`
        );
      } else {
        toast.success(`${data.created} ${lb.gen_created}`);
      }
      if (total > 0) printCreatedAppointments(previewAppts);
      // URS-F269: refresca el mapa de citas para que los botones "Citar VAL/T0"
      // pasen a mostrar "Citado dd/MM HH:MM" sin recargar la pagina.
      if (total > 0) load();

      // ── Auto-send email with appointments per volunteer ────────────
      // Group preview appts by volunteer_id and call /agenda/send-appointments-email.
      // We do NOT block on this — operator still sees the result screen. We
      // collect the per-volunteer outcome and show it inside genResult.
      if (sendEmailWithAppts && data.created > 0) {
        const byVol = {};
        previewAppts.forEach(a => {
          if (!byVol[a.volunteer_id]) byVol[a.volunteer_id] = [];
          byVol[a.volunteer_id].push(a);
        });
        const results = [];
        await Promise.all(Object.entries(byVol).map(async ([vid, appts]) => {
          try {
            const { data: r } = await axios.post(
              `${API_URL}/api/agenda/send-appointments-email`,
              {
                volunteer_id: vid,
                study_id: studyId,
                since_date: appts.reduce((min, a) => (!min || a.date < min ? a.date : min), null),
              },
              { withCredentials: true },
            );
            results.push({ volunteer_id: vid, ok: true, recipients: r.recipients, count: r.count });
          } catch (e) {
            results.push({ volunteer_id: vid, ok: false, error: e?.response?.data?.detail || 'Error' });
          }
        }));
        setEmailSendResult(results);
      }
    } catch (err) {
      // URS-F424: si el backend detecta huecos con bloqueo total, devuelve 409
      // con detail={error:'global_schedule_block', conflicts:[...]}. Mostramos
      // popup para que el usuario decida si forzar la creacion.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === 'object' && detail?.error === 'global_schedule_block') {
        setGlobalBlockConfirm({ conflicts: detail.conflicts || [] });
        setGenerating(false);
        return;
      }
      toast.error(errText(err));
    }
    setGenerating(false);
  };

  const handleResendApptEmail = async (volunteerId) => {
    const ok = window.confirm(
      lang === 'es'
        ? 'Se enviara un email al voluntario (o titular de su familia si es dependiente) con todas las citas futuras y un archivo .ics para anadirlas a su calendario. Continuar?'
        : 'An email will be sent to the volunteer (or family holder if dependent) with all upcoming appointments and a .ics file. Continue?'
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/agenda/send-appointments-email`,
        { volunteer_id: volunteerId, study_id: studyId },
        { withCredentials: true },
      );
      toast.success(
        lang === 'es'
          ? `${data.count} citas enviadas a ${(data.recipients || []).join(', ')}`
          : `${data.count} appointments sent to ${(data.recipients || []).join(', ')}`
      );
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al enviar email' : 'Email send error'));
    }
  };

  if (!studyId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el estudio primero para gestionar voluntarios' : 'Save the study first to manage volunteers'}</p>;

  // --- Determine if I/E needs observations based on responses ---
  const needsObservations = () => {
    const inclusionQs = ieQuestions.filter(q => q.source === 'inclusion');
    const exclusionQs = ieQuestions.filter(q => q.source === 'exclusion');
    return inclusionQs.some(q => ieResponses[q.question_id] === 'No') ||
           exclusionQs.some(q => ieResponses[q.question_id] === 'Sí');
  };

  // URS-F370: extraido como helper para reutilizar en las pestañas Incluidos,
  // Excluidos y Abandono — un voluntario excluido o que ha abandonado puede
  // haber rellenado cuestionarios antes y debe poder consultarse igual que un
  // incluido. Se usa como funcion (no como componente) para evitar el lint
  // react/no-unstable-nested-components.
  const renderExpandedQsRow = (a, colSpan) => (
    <TableRow className="bg-stone-50/60">
      <TableCell colSpan={colSpan} className="p-0">
        <div className="px-6 py-3 border-t border-stone-100">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">{lb.visits_header}</p>
          {studyVisits.length === 0 ? (
            <p className="text-xs text-stone-400 py-2">{lb.no_visits_assigned}</p>
          ) : (
            <div className="space-y-2">
              {studyVisits
                .filter(visit => {
                  if (visit.visit_type === 'eventos_adversos') {
                    const appts = volAppointments[a.volunteer_id] || [];
                    return appts.some(ap => ap.visit_code === visit.code || ap.visit_id === visit.visit_id);
                  }
                  return true;
                })
                .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
                .map(visit => {
                  const isEventoAdverso = (name) => /eventos\s*adversos|adverse\s*events/i.test(name || '');
                  const badges = [];
                  if (visit.visit_type === 'valoracion' && a.ie_completed) {
                    badges.push({ priority: 0, node: (
                      <button key={`ie-${visit.visit_id}`}
                        data-testid={`view-ie-val-${a.volunteer_id}`}
                        onClick={() => openIEDialog(a)}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs transition-colors cursor-pointer bg-green-50 border-green-200 hover:bg-green-100">
                        <Circle size={8} weight="fill" className="text-green-500" />
                        <span className="text-stone-700">{lang === 'es' ? 'Cuestionario I/E' : 'I/E Questionnaire'}</span>
                      </button>
                    ) });
                  }
                  if (visit.visit_type === 'valoracion' && !a.ie_completed) {
                    badges.push({ priority: 0, node: (
                      <span key={`ie-pending-${visit.visit_id}`}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs bg-red-50 border-red-200 text-stone-500">
                        <Circle size={8} weight="fill" className="text-red-500" />
                        <span>{lang === 'es' ? 'Cuestionario I/E pendiente' : 'I/E Questionnaire pending'}</span>
                      </span>
                    ) });
                  }
                  (visit.questionnaire_ids || []).forEach(qId => {
                    const q = studyQuestionnaires.find(sq => sq.questionnaire_id === qId);
                    if (!q) return;
                    if (visit.visit_type === 'valoracion' && q.type === 'inclusion_exclusion') return;
                    if (q.type === 'satisfaccion') return;
                    const status = getQStatus(a.volunteer_id, visit.visit_id, qId);
                    const colorMap = { not_started: 'text-red-500', in_progress: 'text-yellow-500', completed: 'text-green-500' };
                    const bgMap = {
                      not_started: 'bg-red-50 border-red-200 hover:bg-red-100',
                      in_progress: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
                      completed: 'bg-green-50 border-green-200 hover:bg-green-100',
                    };
                    badges.push({ priority: isEventoAdverso(q.name) ? 20 : 10, node: (
                      <button key={qId}
                        data-testid={`vq-${a.volunteer_id}-${visit.visit_id}-${qId}`}
                        onClick={() => openFillQ(a, visit, q)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs transition-colors cursor-pointer ${bgMap[status]}`}>
                        <StatusDot colorClass={colorMap[status]} monitored={status === 'completed' && getQMonitored(a.volunteer_id, visit.visit_id, qId)} testId={`vq-monitored-${a.volunteer_id}-${visit.visit_id}-${qId}`} />
                        <span className="text-stone-700">{q.name}</span>
                      </button>
                    ) });
                  });
                  // URS-F498: overlay por voluntario (extra_visit_questionnaires).
                  // Se muestran los cuestionarios asignados solo a este voluntario
                  // para esta visita, evitando duplicar los que ya estan a nivel visita.
                  const extraQids = (a.extra_visit_questionnaires || {})[visit.code] || [];
                  extraQids.forEach(qId => {
                    if ((visit.questionnaire_ids || []).includes(qId)) return;
                    const q = studyQuestionnaires.find(sq => sq.questionnaire_id === qId);
                    if (!q) return;
                    if (q.type === 'satisfaccion') return;
                    const status = getQStatus(a.volunteer_id, visit.visit_id, qId);
                    const colorMap = { not_started: 'text-red-500', in_progress: 'text-yellow-500', completed: 'text-green-500' };
                    const bgMap = {
                      not_started: 'bg-red-50 border-red-200 hover:bg-red-100',
                      in_progress: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
                      completed: 'bg-green-50 border-green-200 hover:bg-green-100',
                    };
                    badges.push({ priority: isEventoAdverso(q.name) ? 20 : 10, node: (
                      <button key={`extra-${qId}`}
                        data-testid={`vq-extra-${a.volunteer_id}-${visit.visit_id}-${qId}`}
                        onClick={() => openFillQ(a, visit, q)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs transition-colors cursor-pointer ${bgMap[status]}`}
                        title={lang === 'es' ? 'Añadido especificamente a este voluntario' : 'Assigned specifically to this volunteer'}>
                        <StatusDot colorClass={colorMap[status]} monitored={status === 'completed' && getQMonitored(a.volunteer_id, visit.visit_id, qId)} testId={`vq-extra-monitored-${a.volunteer_id}-${visit.visit_id}-${qId}`} />
                        <span className="text-stone-700">{q.name}</span>
                      </button>
                    ) });
                  });
                  const satAssignments = Array.isArray(visit.satisfaction_assignments) && visit.satisfaction_assignments.length
                    ? visit.satisfaction_assignments
                    : (visit.satisfaction_questionnaire_id
                      ? [{ questionnaire_id: visit.satisfaction_questionnaire_id, send_email: true }]
                      : []);
                  satAssignments.forEach((assign, satIdx) => {
                    const sq = studyQuestionnaires.find(x => x.questionnaire_id === assign.questionnaire_id);
                    if (!sq) return;
                    const completed = getQStatus(a.volunteer_id, visit.visit_id, sq.questionnaire_id) === 'completed';
                    const color = completed ? 'bg-green-50 border-green-200 hover:bg-green-100 text-green-800' : 'bg-red-50 border-red-200 hover:bg-red-100 text-red-800';
                    const emailOn = assign.send_email !== false;
                    badges.push({ priority: 30 + satIdx * 0.1, node: (
                      <button key={`sat-${visit.visit_id}-${sq.questionnaire_id}`}
                        data-testid={`satisfaction-${a.volunteer_id}-${visit.visit_id}-${sq.questionnaire_id}`}
                        onClick={() => setSatisfactionDialog({ vol: a, visit, questionnaire: sq, completed })}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs transition-colors cursor-pointer ${color}`}
                        title={`${sq.name} · ${emailOn ? (lang === 'es' ? 'Envio auto' : 'Auto send') : (lang === 'es' ? 'Solo manual' : 'Manual only')} · ${completed ? (lang === 'es' ? 'Completado' : 'Completed') : (lang === 'es' ? 'Pendiente' : 'Pending')}`}>
                        <Sparkle size={10} weight="fill" className={completed ? 'text-green-600' : 'text-red-500'} />
                        <span>{sq.name}</span>
                        {!emailOn && <span className="text-[9px] opacity-70">📭</span>}
                      </button>
                    ) });
                  });
                  if (a.study_volunteer_code && (symptomsByKey[`${a.study_volunteer_code}|${visit.code}`] || []).length > 0) {
                    badges.push({ priority: 40, node: (
                      <button key={`sym-${visit.code}`}
                        data-testid={`view-symptoms-${a.volunteer_id}-${visit.code}`}
                        onClick={() => setShowSymptomDialog({ assignment: a, visit_code: visit.code })}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs transition-colors cursor-pointer bg-violet-50 border-violet-200 hover:bg-violet-100">
                        <Circle size={8} weight="fill" className="text-violet-500" />
                        <span className="text-stone-700">{lang === 'es' ? 'Sintoma Subjetivo Paciente' : 'Subjective Symptom'}</span>
                      </button>
                    ) });
                  }
                  badges.sort((x, y) => x.priority - y.priority);
                  const visitAppts = (volAppointments[a.volunteer_id] || [])
                    .filter(ap => ap.visit_code === visit.code || ap.visit_id === visit.visit_id);
                  const upcoming = visitAppts.filter(ap => ap.status !== 'cancelada' && ap.status !== 'no_presentada')
                    .sort((x, y) => `${x.date || ''} ${x.time || ''}`.localeCompare(`${y.date || ''} ${y.time || ''}`));
                  const apptForVisit = upcoming[0] || visitAppts[0];
                  const fmtApptDate = apptForVisit?.date
                    ? (() => {
                        const [yyyy, mm, dd] = (apptForVisit.date || '').split('-');
                        if (!dd) return apptForVisit.date;
                        return lang === 'es' ? `${dd}/${mm}/${yyyy}` : `${yyyy}-${mm}-${dd}`;
                      })()
                    : null;
                  return (
                    <div key={visit.visit_id} className={`flex items-center gap-3 border rounded-sm px-3 py-2 ${visit.visit_type === 'valoracion' ? 'bg-emerald-50/50 border-emerald-200' : visit.visit_type === 'eventos_adversos' ? 'bg-orange-50/50 border-orange-200' : 'bg-white border-stone-200'}`}>
                      <span className={`font-mono text-xs font-semibold w-12 ${visit.visit_type === 'valoracion' ? 'text-emerald-700' : visit.visit_type === 'eventos_adversos' ? 'text-orange-700' : 'text-stone-800'}`}>{visit.code}</span>
                      <span className="text-xs text-stone-600 w-32 truncate">{visit.name || '-'}</span>
                      <div className="flex items-center gap-2 flex-1 flex-wrap">
                        {badges.map(b => b.node)}
                      </div>
                      {apptForVisit && (
                        <span
                          data-testid={`visit-appt-date-${a.volunteer_id}-${visit.code}`}
                          title={apptForVisit.time ? `${fmtApptDate} · ${apptForVisit.time}${apptForVisit.status ? ` · ${apptForVisit.status}` : ''}` : fmtApptDate}
                          className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#1f3e80]/8 border border-[#1f3e80]/15 text-[11px] font-mono text-[#1f3e80] whitespace-nowrap"
                        >
                          <CalendarBlank size={10} weight="duotone" />
                          {fmtApptDate}
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className={shellHidden ? 'hidden' : 'space-y-4'} data-testid={shellHidden ? 'volunteers-panel-shell' : 'volunteers-panel'}>
      {/* Top actions */}
      <div className="flex justify-end gap-2">
        {incluidos.length > 0 && (
          <Button variant="outline" data-testid="generate-appointments-btn" onClick={openGenerate} className="rounded-sm gap-2 text-[#1f3e80] border-[#1f3e80]/30 hover:bg-[#1f3e80]/5">
            <CalendarBlank size={16} />{lb.generate}
          </Button>
        )}
        {incluidos.length > 0 && (
          <Button variant="outline" data-testid="add-ea-btn" onClick={() => { setEaSelVols(['all']); setEaSelVisits(['all']); setAddEaDialog(true); }} className="rounded-sm gap-2 text-red-700 border-red-300 hover:bg-red-50">
            <Plus size={16} />{lang === 'es' ? 'Añadir EA' : 'Add AE'}
          </Button>
        )}
        <Button data-testid="add-volunteer-btn" onClick={() => { if (!showAdd) { axios.get(`${API_URL}/api/studies/${studyId}`, { withCredentials: true }).then(r => setProtocolData(r.data?.protocol_data || {})).catch(() => {}); } setShowAdd(!showAdd); setEligibleResults([]); setSearch(''); }} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <UserPlus size={16} />{lb.add}
        </Button>
        {showAdd && (
          <button
            type="button"
            onClick={() => setStaffOnly(!staffOnly)}
            data-testid="staff-only-toggle"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors ${
              staffOnly ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'
            }`}
          >
            <House size={14} />
            {lb.staff_only}
          </button>
        )}
      </div>

      {/* Assign volunteer search panel */}
      {showAdd && (
        <div className="bg-stone-50 border border-stone-200 rounded-sm p-4">
          <p className="text-xs font-semibold text-stone-700 mb-2">{lb.select_vol}</p>
          <div className="relative mb-3">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="volunteer-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder={lb.search} className="pl-9 bg-white border-stone-200 text-sm" />
          </div>
          {!staffOnly && search.length >= 2 && !searchLoading && eligibleResults.length > 0 && (
            <div
              className={`flex items-center justify-between text-[11px] px-2.5 py-1.5 mb-2 rounded-sm border ${eligibleMeta.truncated ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-stone-50 text-stone-500 border-stone-200'}`}
              data-testid="eligible-results-meta"
            >
              <span>
                {lang === 'es'
                  ? `Mostrando ${eligibleMeta.shown} de ${eligibleMeta.total_matching} que cumplen el protocolo`
                  : `Showing ${eligibleMeta.shown} of ${eligibleMeta.total_matching} matching protocol`}
              </span>
              {eligibleMeta.truncated && (
                <span className="font-semibold">
                  {lang === 'es' ? 'Refina la busqueda para ver mas' : 'Refine your search to see more'}
                </span>
              )}
            </div>
          )}
          <div className="max-h-[250px] overflow-y-auto space-y-1">
            {searchLoading ? (
              <div className="text-center py-4"><div className="h-5 w-5 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : filteredAvailable.length === 0 ? (
              <p className="text-xs text-stone-400 text-center py-3">{!staffOnly && search.length < 2 ? (lang === 'es' ? 'Escribe al menos 2 caracteres para buscar...' : 'Type at least 2 characters to search...') : lb.no_results}</p>
            ) : filteredAvailable.map(v => {
              const pd = v.personal_data || {};
              return (
                <div key={v.volunteer_id} data-testid={`available-vol-${v.volunteer_id}`} className="flex items-center justify-between bg-white border border-stone-200 rounded-sm px-3 py-2 hover:bg-stone-50">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-stone-500">{v.volunteer_id}</span>
                    <span className="text-sm font-medium text-stone-900">{pd.nombre} {pd.apellidos}</span>
                    {pd.dni && <span className="text-xs text-stone-500">{pd.dni}</span>}
                  </div>
                  <Button size="sm" variant="outline" data-testid={`assign-vol-${v.volunteer_id}`} onClick={() => handleAssign(v)} className="text-xs gap-1 rounded-sm h-7">
                    <Plus size={12} />{lang === 'es' ? 'Asignar' : 'Assign'}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sub-tabs: Pendientes / Incluidos / Excluidos */}
      {assigned.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <UserPlus size={40} className="text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">{lb.no_assigned}</p>
        </div>
      ) : (
        <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
          <div className="flex items-center justify-between gap-2 mb-1">
            <TabsList className="bg-stone-100">
            <TabsTrigger value="pendientes" data-testid="subtab-pendientes" className="gap-1.5">
              {lb.tab_pending}
              {pendientes.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-yellow-50 text-yellow-700 border-yellow-200">{pendientes.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="aptos" data-testid="subtab-aptos" className="gap-1.5">
              {lb.tab_aptos}
              {aptos.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-emerald-50 text-emerald-700 border-emerald-200">{aptos.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="incluidos" data-testid="subtab-incluidos" className="gap-1.5">
              {lb.tab_included}
              {incluidos.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-green-50 text-green-700 border-green-200">{incluidos.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="no_aptos" data-testid="subtab-no-aptos" className="gap-1.5">
              {lb.tab_no_aptos}
              {noAptos.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-rose-50 text-rose-600 border-rose-200">{noAptos.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="excluidos" data-testid="subtab-excluidos" className="gap-1.5">
              {lb.tab_excluded}
              {excluidos.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-red-50 text-red-600 border-red-200">{excluidos.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="abandonos" data-testid="subtab-abandonos" className="gap-1.5">
              {lb.tab_abandonos}
              {abandonos.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-amber-50 text-amber-700 border-amber-200">{abandonos.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="anulados" data-testid="subtab-anulados" className="gap-1.5">
              {lb.tab_anulados}
              {anulados.length > 0 && <Badge variant="outline" className="ml-1 text-[10px] h-5 px-1.5 bg-red-50 text-red-600 border-red-200">{anulados.length}</Badge>}
            </TabsTrigger>
            <div className="ml-auto flex items-center gap-1.5 px-3" data-testid="total-volunteers-badge">
              <span className="text-xs font-medium text-stone-500">Total:</span>
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-[#1f3e80]/5 text-[#1f3e80] border-[#1f3e80]/20 font-bold">{assigned.length}</Badge>
            </div>
          </TabsList>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              data-testid="export-volunteers-xlsx"
              onClick={() => {
                // URS-F372: descarga el listado de voluntarios del estudio
                // (pendientes + aptos + incluidos + completados) en Excel.
                const url = `${API_URL}/api/studies/${studyId}/volunteers/export.xlsx`;
                window.open(url, '_blank');
              }}
            >
              <DownloadSimple size={14} />
              {lang === 'es' ? 'Exportar Excel' : 'Export Excel'}
            </Button>
          </div>

          {/* ===== PENDIENTES TAB ===== */}
          <TabsContent value="pendientes" className="mt-4">
            {pendientes.length > 0 && (
              <div className="relative max-w-sm mb-3">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  data-testid="search-pendientes"
                  value={tabSearch.pendientes}
                  onChange={e => setTabSearch(s => ({ ...s, pendientes: e.target.value }))}
                  placeholder={lang === 'es' ? 'Buscar por nombre, V-code o numero...' : 'Search by name, V-code or number...'}
                  className="pl-9 h-8 bg-white border-stone-200 text-xs"
                />
              </div>
            )}
            {pendientes.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lb.no_pending}</p>
              </div>
            ) : pendientesView.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lang === 'es' ? 'Sin resultados para la busqueda' : 'No results'}</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-28">{lb.vol_id}</TableHead>
                    <TableHead className="w-52">{lb.name}</TableHead>
                    <TableHead className="w-16 text-center">{lb.age}</TableHead>
                    <TableHead className="w-28">{lb.dni}</TableHead>
                    <TableHead className="w-32">{lang === 'es' ? 'I/E' : 'I/E'}</TableHead>
                    <TableHead className="w-28">{lb.assigned_date}</TableHead>
                    <TableHead className="w-72">{lb.actions}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pendientesView.map(a => (
                      <TableRow
                        key={a.volunteer_id}
                        data-testid={`pending-vol-${a.volunteer_id}`}
                        className={highlightedVol === a.volunteer_id ? 'bg-amber-100 ring-2 ring-amber-400 ring-inset transition-colors duration-700' : 'transition-colors duration-700'}
                      >
                        <TableCell className="font-mono text-xs text-stone-500">{a.volunteer_id}</TableCell>
                        <TableCell className="text-sm font-medium text-stone-900 truncate" title={a.volunteer_name}>{a.volunteer_name}</TableCell>
                        <TableCell className="text-xs text-stone-700 text-center" data-testid={`pending-vol-age-${a.volunteer_id}`}>
                          {a.volunteer_age ?? '-'}
                        </TableCell>
                        <TableCell className="text-xs text-stone-600">{a.volunteer_dni || '-'}</TableCell>
                        <TableCell>
                          {a.ie_completed ? (
                            <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200 gap-1">
                              <Check size={10} weight="bold" />{lb.ie_completed}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-yellow-50 text-yellow-700 border-yellow-200 gap-1">
                              <ClipboardText size={10} />{lb.ie_not_completed}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 items-center flex-nowrap whitespace-nowrap">
                            {renderCitadoOrCreate(a, 'VAL')}
                            <Button
                              variant={a.ie_completed ? 'outline' : 'default'}
                              size="sm"
                              className={`text-xs gap-1 h-7 rounded-sm whitespace-nowrap ${a.ie_completed ? 'text-[#1f3e80] border-[#1f3e80]/30' : 'bg-[#1f3e80] hover:bg-[#2a4f8a] text-white'}`}
                              data-testid={`ie-btn-${a.volunteer_id}`}
                              onClick={() => openIEDialog(a)}
                            >
                              <ClipboardText size={12} />
                              {a.ie_completed ? lb.ie_view : (lang === 'es' ? 'I/E' : 'I/E')}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" data-testid={`remove-pending-${a.volunteer_id}`}
                              onClick={() => setConfirmDeleteVol(a)}
                              style={{ display: isAdmin ? undefined : 'none' }}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ===== APTOS TAB ===== */}
          <TabsContent value="aptos" className="mt-4">
            {aptos.length > 0 && (
              <div className="relative max-w-sm mb-3">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  data-testid="search-aptos"
                  value={tabSearch.aptos}
                  onChange={e => setTabSearch(s => ({ ...s, aptos: e.target.value }))}
                  placeholder={lang === 'es' ? 'Buscar por nombre, V-code o numero...' : 'Search by name, V-code or number...'}
                  className="pl-9 h-8 bg-white border-stone-200 text-xs"
                />
              </div>
            )}
            {aptos.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lb.no_aptos}</p>
              </div>
            ) : aptosView.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lang === 'es' ? 'Sin resultados para la busqueda' : 'No results'}</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-28">{lb.vol_id}</TableHead>
                    <TableHead className="w-52">{lb.name}</TableHead>
                    <TableHead className="w-16 text-center">{lb.age}</TableHead>
                    <TableHead className="w-28">{lb.dni}</TableHead>
                    <TableHead className="w-28">{lb.status}</TableHead>
                    <TableHead className="w-32">{lang === 'es' ? 'I/E' : 'I/E'}</TableHead>
                    <TableHead className="w-28">{lb.assigned_date}</TableHead>
                    <TableHead className="w-60">{lb.actions}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {aptosView.map(a => (
                      <TableRow key={a.volunteer_id} data-testid={`apto-vol-${a.volunteer_id}`}>
                        <TableCell className="font-mono text-xs text-stone-500">{a.volunteer_id}</TableCell>
                        <TableCell className="text-sm font-medium text-stone-900 truncate" title={a.volunteer_name}>{a.volunteer_name}</TableCell>
                        <TableCell className="text-xs text-stone-700 text-center" data-testid={`apto-vol-age-${a.volunteer_id}`}>
                          {a.volunteer_age ?? '-'}
                        </TableCell>
                        <TableCell className="text-xs text-stone-600">{a.volunteer_dni || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${volStatusColors.apto}`}>{lb.apto}</Badge>
                        </TableCell>
                        <TableCell>
                          {a.ie_completed && (
                            <Button variant="ghost" size="sm" className="text-xs gap-1 h-6 text-stone-500" data-testid={`view-ie-apto-${a.volunteer_id}`}
                              onClick={() => openIEDialog(a)}>
                              <Eye size={12} />{lb.ie_view}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 items-center flex-nowrap whitespace-nowrap">
                            {renderCitadoOrCreate(a, firstProtocolVisitCode)}
                            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`change-status-apto-${a.volunteer_id}`}
                              onClick={() => { setShowStatusDialog(a); setStatusForm({ status: a.status, notes: a.notes || '' }); }}>
                              <PencilSimple size={14} />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" data-testid={`remove-apto-${a.volunteer_id}`}
                                onClick={() => setConfirmDeleteVol(a)}>
                                <Trash size={14} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ===== INCLUIDOS TAB ===== */}
          <TabsContent value="incluidos" className="mt-4">
            {incluidos.length > 0 && (
              <div className="relative max-w-sm mb-3">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  data-testid="search-incluidos"
                  value={tabSearch.incluidos}
                  onChange={e => setTabSearch(s => ({ ...s, incluidos: e.target.value }))}
                  placeholder={lang === 'es' ? 'Buscar por nombre, V-code o numero...' : 'Search by name, V-code or number...'}
                  className="pl-9 h-8 bg-white border-stone-200 text-xs"
                />
              </div>
            )}
            {incluidos.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lb.no_included}</p>
              </div>
            ) : incluidosView.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lang === 'es' ? 'Sin resultados para la busqueda' : 'No results'}</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="w-20">{lb.vcode}</TableHead>
                    <TableHead className="w-28">{lb.vol_id}</TableHead>
                    <TableHead>{lb.name}</TableHead>
                    <TableHead className="w-16 text-center">{lb.age}</TableHead>
                    <TableHead className="w-28">{lb.dni}</TableHead>
                    <TableHead className="w-28">{lb.status}</TableHead>
                    <TableHead className="w-28">{lb.assigned_date}</TableHead>
                    <TableHead className="w-10 text-center" title={lang === 'es' ? 'Consentimientos globales (RGPD + Imagenes)' : 'Global consents (RGPD + Images)'}>{lang === 'es' ? 'Docs' : 'Docs'}</TableHead>
                    <TableHead className="w-32">{lb.actions}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {incluidosView.map(a => {
                      const isExpanded = expandedVol[a.volunteer_id];
                      return (
                        <React.Fragment key={a.volunteer_id}>
                          <TableRow
                            data-testid={`included-vol-${a.study_volunteer_code}`}
                            className="cursor-pointer hover:bg-stone-50"
                            onClick={() => toggleExpandVol(a.volunteer_id)}
                          >
                            <TableCell className="px-2">
                              <CaretDown size={12} className={`text-stone-400 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                            </TableCell>
                            <TableCell className="font-mono text-sm font-bold text-[#1f3e80]">{a.study_volunteer_code}</TableCell>
                            <TableCell className="font-mono text-xs text-stone-500">{a.volunteer_id}</TableCell>
                            <TableCell className="text-sm font-medium text-stone-900">{a.volunteer_name}</TableCell>
                            <TableCell className="text-xs text-stone-700 text-center" data-testid={`included-vol-age-${a.study_volunteer_code}`}>
                              {a.volunteer_age ?? '-'}
                            </TableCell>
                            <TableCell className="text-xs text-stone-600">{a.volunteer_dni || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-xs ${volStatusColors[a.status] || ''}`}>
                                {lb[a.status] || a.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-stone-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                            <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                              {(() => {
                                // URS-F494f: semaforo de consentimientos globales
                                // (mismo criterio que la columna Documentos del listado
                                // global de Voluntarios). Verde = LOPD + Imagenes firmados,
                                // amarillo = solo uno, rojo = ninguno.
                                const cm = a.doc_consents || {};
                                const labelMap = { lopd: 'RGPD', imagenes: lang === 'es' ? 'Imagenes' : 'Images' };
                                const parts = Object.keys(cm).map(k => {
                                  const l = labelMap[k] || k;
                                  const s = cm[k] ? (lang === 'es' ? 'firmado' : 'signed') : (lang === 'es' ? 'pendiente' : 'pending');
                                  return `${l}: ${s}`;
                                });
                                const tooltip = parts.join(' \u00b7 ') || (lang === 'es' ? 'Sin consentimientos configurados' : 'No consents configured');
                                const dotClass = a.doc_status === 'green' ? 'bg-green-500' : a.doc_status === 'yellow' ? 'bg-amber-400' : 'bg-red-500';
                                return (
                                  <span
                                    className={`inline-block w-3 h-3 rounded-full ${dotClass}`}
                                    title={tooltip}
                                    data-testid={`included-vol-docs-${a.study_volunteer_code}`}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                {/* URS-F401: toggle Pago 1 / Pago 2. Visible para quien tenga
                                    permiso costes:edit en el estudio (configurable en Ajustes). */}
                                {canEditCostes && (
                                  <button
                                    type="button"
                                    data-testid={`payment-tier-toggle-${a.study_volunteer_code}`}
                                    onClick={async () => {
                                      // URS-F405: ciclo P1 -> P2 -> P3 -> P1
                                      const current = String(a.payment_tier || '1');
                                      const nextTier = current === '1' ? '2' : current === '2' ? '3' : '1';
                                      try {
                                        await axios.put(
                                          `${API_URL}/api/studies/${studyId}/volunteers/${a.volunteer_id}/payment-tier`,
                                          { tier: nextTier },
                                          { withCredentials: true },
                                        );
                                        toast.success(lang === 'es' ? `Pago ${nextTier} asignado` : `Payment ${nextTier} assigned`);
                                        load();
                                      } catch (err) {
                                        toast.error(errText(err));
                                      }
                                    }}
                                    className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-sm border transition-colors ${
                                      a.payment_tier === '3'
                                        ? 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100'
                                        : a.payment_tier === '2'
                                          ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                                          : 'bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100'
                                    }`}
                                    title={lang === 'es'
                                      ? `Voluntario cobra Pago ${a.payment_tier || '1'}. Click para pasar al siguiente tier.`
                                      : `Volunteer paid with Payment ${a.payment_tier || '1'}. Click to cycle to next tier.`}
                                  >
                                    P{a.payment_tier === '2' ? '2' : a.payment_tier === '3' ? '3' : '1'}
                                  </button>
                                )}
                                {a.ie_completed && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-[#1f3e80]" data-testid={`view-ie-${a.study_volunteer_code}`}
                                    onClick={() => openIEDialog(a)} title={lb.ie_view}>
                                    <Eye size={14} />
                                  </Button>
                                )}
                                {canSeeQsalud && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-emerald-600" data-testid={`view-qsalud-${a.study_volunteer_code}`}
                                    onClick={() => setQsaludVolunteer(a)}
                                    title={lang === 'es' ? 'Ver Qsalud del voluntario' : 'View volunteer Qsalud'}>
                                    <Heartbeat size={14} />
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-emerald-600" data-testid={`appoint-vol-${a.study_volunteer_code}`}
                                  onClick={() => openGenerate(a.volunteer_id)}
                                  title={lang === 'es' ? 'Generar citas para este voluntario' : 'Generate appointments for this volunteer'}>
                                  <CalendarPlus size={14} />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-[#1f3e80]" data-testid={`resend-appt-email-${a.study_volunteer_code}`}
                                  onClick={() => handleResendApptEmail(a.volunteer_id)}
                                  title={lang === 'es' ? 'Reenviar email con citas (.ics) al voluntario' : 'Resend appointments email (.ics) to volunteer'}>
                                  <EnvelopeSimple size={14} />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`change-status-${a.study_volunteer_code}`}
                                  onClick={() => { setShowStatusDialog(a); setStatusForm({ status: a.status, notes: a.notes || '' }); }}>
                                  <PencilSimple size={14} />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" data-testid={`remove-vol-${a.study_volunteer_code}`}
                                  onClick={() => setConfirmDeleteVol(a)}
                                  style={{ display: isAdmin ? undefined : 'none' }}>
                                  <Trash size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {isExpanded && renderExpandedQsRow(a, 9)}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ===== NO APTOS TAB ===== */}
          <TabsContent value="no_aptos" className="mt-4">
            {noAptos.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lb.no_no_aptos}</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-28">{lb.vol_id}</TableHead>
                    <TableHead>{lb.name}</TableHead>
                    <TableHead className="w-28">{lb.dni}</TableHead>
                    <TableHead className="w-28">{lb.status}</TableHead>
                    <TableHead className="w-32">{lang === 'es' ? 'I/E' : 'I/E'}</TableHead>
                    <TableHead className="w-28">{lb.assigned_date}</TableHead>
                    <TableHead className="w-24">{lb.actions}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {noAptos.map(a => (
                      <TableRow key={a.volunteer_id} data-testid={`no-apto-vol-${a.volunteer_id}`}>
                        <TableCell className="font-mono text-xs text-stone-500">{a.volunteer_id}</TableCell>
                        <TableCell className="text-sm font-medium text-stone-900">{a.volunteer_name}</TableCell>
                        <TableCell className="text-xs text-stone-600">{a.volunteer_dni || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${volStatusColors.no_apto}`}>{lb.no_apto}</Badge>
                        </TableCell>
                        <TableCell>
                          {a.ie_completed && (
                            <Button variant="ghost" size="sm" className="text-xs gap-1 h-6 text-stone-500" data-testid={`view-ie-noapto-${a.volunteer_id}`}
                              onClick={() => openIEDialog(a)}>
                              <Eye size={12} />{lb.ie_view}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" data-testid={`remove-noapto-${a.volunteer_id}`}
                            onClick={() => setConfirmDeleteVol(a)}
                            style={{ display: isAdmin ? undefined : 'none' }}>
                            <Trash size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ===== EXCLUIDOS TAB ===== */}
          <TabsContent value="excluidos" className="mt-4">
            {excluidos.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lb.no_excluded}</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-8" />
                    <TableHead className="w-20">{lb.vcode}</TableHead>
                    <TableHead className="w-28">{lb.vol_id}</TableHead>
                    <TableHead>{lb.name}</TableHead>
                    <TableHead className="w-28">{lb.dni}</TableHead>
                    <TableHead className="w-28">{lb.status}</TableHead>
                    <TableHead className="w-32">{lang === 'es' ? 'I/E' : 'I/E'}</TableHead>
                    <TableHead className="w-28">{lb.assigned_date}</TableHead>
                    <TableHead className="w-28">{lang === 'es' ? 'Motivo' : 'Reason'}</TableHead>
                    <TableHead className="w-24">{lb.actions}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {excluidos.map(a => {
                      const isExpanded = expandedVol[a.volunteer_id];
                      return (
                      <React.Fragment key={a.volunteer_id}>
                      <TableRow data-testid={`excluded-vol-${a.volunteer_id}`}>
                        <TableCell className="p-0 pl-2">
                          <button
                            type="button"
                            data-testid={`excluded-expand-${a.volunteer_id}`}
                            onClick={() => toggleExpandVol(a.volunteer_id)}
                            className="p-1 hover:bg-stone-100 rounded-sm transition-colors"
                            title={isExpanded ? (lang === 'es' ? 'Contraer' : 'Collapse') : (lang === 'es' ? 'Expandir' : 'Expand')}
                          >
                            <CaretDown size={12} className={`text-stone-400 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                          </button>
                        </TableCell>
                        <TableCell className="font-mono text-sm font-bold text-[#1f3e80]">{a.study_volunteer_code || '-'}</TableCell>
                        <TableCell className="font-mono text-xs text-stone-500">{a.volunteer_id}</TableCell>
                        <TableCell className="text-sm font-medium text-stone-900">{a.volunteer_name}</TableCell>
                        <TableCell className="text-xs text-stone-600">{a.volunteer_dni || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${volStatusColors.excluido}`}>{lb.excluido}</Badge>
                        </TableCell>
                        <TableCell>
                          {a.ie_completed && (
                            <Button variant="ghost" size="sm" className="text-xs gap-1 h-6 text-stone-500" data-testid={`view-ie-excl-${a.volunteer_id}`}
                              onClick={() => openIEDialog(a)}>
                              <Eye size={12} />{lb.ie_view}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs gap-1 h-6 text-stone-500 hover:text-[#1f3e80]"
                            data-testid={`view-exclusion-reason-${a.volunteer_id}`}
                            disabled={!a.notes}
                            onClick={() => setReasonPopup({ volunteer: a, kind: 'excluido' })}
                          >
                            <Eye size={12} />{lang === 'es' ? 'Ver motivo' : 'View reason'}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" data-testid={`remove-excluded-${a.volunteer_id}`}
                            onClick={() => setConfirmDeleteVol(a)}
                            style={{ display: isAdmin ? undefined : 'none' }}>
                            <Trash size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && renderExpandedQsRow(a, 10)}
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ===== ABANDONOS TAB ===== */}
          <TabsContent value="abandonos" className="mt-4">
            {abandonos.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lb.no_abandonos}</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-8" />
                    <TableHead className="w-20">{lb.vcode}</TableHead>
                    <TableHead className="w-28">{lb.vol_id}</TableHead>
                    <TableHead>{lb.name}</TableHead>
                    <TableHead className="w-28">{lb.dni}</TableHead>
                    <TableHead className="w-28">{lb.status}</TableHead>
                    <TableHead className="w-28">{lb.assigned_date}</TableHead>
                    <TableHead className="w-28">{lang === 'es' ? 'Motivo' : 'Reason'}</TableHead>
                    {isAdmin && <TableHead className="w-10" />}
                  </TableRow></TableHeader>
                  <TableBody>
                    {abandonos.map(a => {
                      const isExpanded = expandedVol[a.volunteer_id];
                      return (
                      <React.Fragment key={a.volunteer_id}>
                      <TableRow data-testid={`abandono-vol-${a.volunteer_id}`}>
                        <TableCell className="p-0 pl-2">
                          <button
                            type="button"
                            data-testid={`abandono-expand-${a.volunteer_id}`}
                            onClick={() => toggleExpandVol(a.volunteer_id)}
                            className="p-1 hover:bg-stone-100 rounded-sm transition-colors"
                            title={isExpanded ? (lang === 'es' ? 'Contraer' : 'Collapse') : (lang === 'es' ? 'Expandir' : 'Expand')}
                          >
                            <CaretDown size={12} className={`text-stone-400 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                          </button>
                        </TableCell>
                        <TableCell className="font-mono text-sm font-bold text-[#1f3e80]">{a.study_volunteer_code || '-'}</TableCell>
                        <TableCell className="font-mono text-xs text-stone-500">{a.volunteer_id}</TableCell>
                        <TableCell className="text-sm font-medium text-stone-900">{a.volunteer_name}</TableCell>
                        <TableCell className="text-xs text-stone-600">{a.volunteer_dni || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${volStatusColors.abandono}`}>{lb.abandono}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs gap-1 h-6 text-stone-500 hover:text-[#1f3e80]"
                            data-testid={`view-abandono-reason-${a.volunteer_id}`}
                            disabled={!a.notes}
                            onClick={() => setReasonPopup({ volunteer: a, kind: 'abandono' })}
                          >
                            <Eye size={12} />{lang === 'es' ? 'Ver motivo' : 'View reason'}
                          </Button>
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-stone-400 hover:text-red-500"
                              data-testid={`remove-abandono-${a.volunteer_id}`}
                              title={lang === 'es' ? 'Eliminar del estudio' : 'Remove from study'}
                              onClick={() => setConfirmDeleteVol(a)}
                            >
                              <Trash size={14} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                      {isExpanded && renderExpandedQsRow(a, isAdmin ? 9 : 8)}
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ===== ANULADOS TAB (URS-F465) ===== */}
          <TabsContent value="anulados" className="mt-4">
            {anulados.length > 0 && (
              <div className="relative max-w-sm mb-3">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  data-testid="search-anulados"
                  value={tabSearch.anulados}
                  onChange={e => setTabSearch(s => ({ ...s, anulados: e.target.value }))}
                  placeholder={lang === 'es' ? 'Buscar por nombre, V-code o numero...' : 'Search by name, V-code or number...'}
                  className="pl-9 h-8 bg-white border-stone-200 text-xs"
                />
              </div>
            )}
            {anulados.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lb.no_anulados}</p>
              </div>
            ) : anuladosView.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-8 text-center">
                <p className="text-sm text-stone-400">{lang === 'es' ? 'Sin resultados para la busqueda' : 'No results'}</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-28">{lb.vol_id}</TableHead>
                    <TableHead className="w-52">{lb.name}</TableHead>
                    <TableHead className="w-16 text-center">{lb.age}</TableHead>
                    <TableHead className="w-28">{lb.dni}</TableHead>
                    <TableHead className="w-56">{lang === 'es' ? 'Cita anulada' : 'Cancelled appointment'}</TableHead>
                    <TableHead className="w-28">{lb.assigned_date}</TableHead>
                    <TableHead className="w-72">{lb.actions}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {anuladosView.map(a => {
                      const missed = (missedApptByVolVisit[a.volunteer_id] || {})['VAL'];
                      const mDate = missed ? new Date(missed.date) : null;
                      const mDateLabel = mDate
                        ? `${String(mDate.getDate()).padStart(2, '0')}/${String(mDate.getMonth() + 1).padStart(2, '0')}/${mDate.getFullYear()}`
                        : '-';
                      const mTimeLabel = missed?.time || '';
                      const missedStatusLabel = missed?.status === 'no_asiste'
                        ? (lang === 'es' ? 'No asistio' : 'No show')
                        : (lang === 'es' ? 'Anulada' : 'Cancelled');
                      return (
                        <TableRow key={a.volunteer_id} data-testid={`anulado-vol-${a.volunteer_id}`}>
                          <TableCell className="font-mono text-xs text-stone-500">{a.volunteer_id}</TableCell>
                          <TableCell className="text-sm font-medium text-stone-900 truncate" title={a.volunteer_name}>{a.volunteer_name}</TableCell>
                          <TableCell className="text-xs text-stone-700 text-center" data-testid={`anulado-vol-age-${a.volunteer_id}`}>
                            {a.volunteer_age ?? '-'}
                          </TableCell>
                          <TableCell className="text-xs text-stone-600">{a.volunteer_dni || '-'}</TableCell>
                          <TableCell>
                            <Badge className="bg-red-50 text-red-700 border-red-200 text-[11px] gap-1 px-2 py-0.5 whitespace-nowrap">
                              <CalendarBlank size={11} />
                              {missedStatusLabel} {mDateLabel} {mTimeLabel}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-stone-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1 items-center flex-nowrap whitespace-nowrap">
                              <Button
                                variant="outline" size="sm"
                                className="text-xs gap-1 h-7 rounded-sm text-[#1f3e80] border-[#1f3e80]/30"
                                data-testid={`give-val-${a.volunteer_id}`}
                                onClick={() => setApptPreselect({ studyId, volunteerId: a.volunteer_id, volunteerName: a.volunteer_name, visitCode: 'VAL' })}
                              >
                                <CalendarBlank size={12} />
                                {lang === 'es' ? 'Nueva cita' : 'New appointment'}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500" data-testid={`remove-anulado-${a.volunteer_id}`}
                                onClick={() => setConfirmDeleteVol(a)}
                                style={{ display: isAdmin ? undefined : 'none' }}>
                                <Trash size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* ===== I/E QUESTIONNAIRE DIALOG ===== */}
      <Dialog open={!!showIEDialog} onOpenChange={v => { if (!v) setShowIEDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={20} className="text-[#1f3e80]" />
              {lb.ie_title}
            </DialogTitle>
            <DialogDescription>
              <div className="flex flex-col gap-0.5">
                <span>{showIEDialog?.volunteer_name} ({showIEDialog?.volunteer_id})</span>
                {showIEDialog?.status !== 'pendiente' && showIEDialog?.ie_signed_by_name && (
                  <span className="text-xs text-stone-600">
                    <b>{lang === 'es' ? 'Firmado por' : 'Signed by'}:</b> {showIEDialog.ie_signed_by_name}
                    {showIEDialog?.ie_signed_at && (
                      <span className="text-stone-500"> · {new Date(showIEDialog.ie_signed_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                    )}
                  </span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 pr-1">
            {/* URS-F419: aviso de respuestas huerfanas (question_ids en
                ie_responses que no existen en el questionnaire actual). Ocurre
                cuando se editan textos de criterios y las respuestas quedan
                atadas al UUID viejo. */}
            {(() => {
              const currentIds = new Set(ieQuestions.map(q => q.question_id));
              const respKeys = Object.keys(ieResponses || {});
              const orphanKeys = respKeys.filter(k => !currentIds.has(k));
              const hasAnyCurrent = respKeys.some(k => currentIds.has(k));
              if (orphanKeys.length === 0) return null;
              return (
                <div className="border-l-4 border-amber-500 bg-amber-50 rounded-sm px-3 py-2" data-testid="ie-orphan-banner">
                  <div className="text-xs text-amber-900">
                    <b>{lang === 'es' ? 'Respuestas huérfanas detectadas' : 'Orphan answers detected'}:</b>{' '}
                    {lang === 'es'
                      ? `Hay ${orphanKeys.length} respuestas ligadas a preguntas que ya no existen (probablemente el texto del criterio fue editado tras rellenar el cuestionario). ${hasAnyCurrent ? 'Algunas respuestas están alineadas y otras no.' : 'Ninguna respuesta actual coincide.'}`
                      : `${orphanKeys.length} answers are tied to questions that no longer exist.`}
                  </div>
                  {isAdmin && (
                    <button
                      type="button"
                      data-testid="ie-orphan-recover-btn"
                      onClick={async () => {
                        try {
                          // First: dry-run to preview
                          const dry = await axios.post(
                            `${API_URL}/api/studies/${studyId}/recover-ie-responses`,
                            { dry_run: true },
                            { withCredentials: true }
                          );
                          const msg = lang === 'es'
                            ? `Se rescatarán ${dry.data.repaired} voluntarios (${dry.data.already_aligned} ya alineados, ${dry.data.failed} sin poder mapear). ¿Aplicar?`
                            : `Will repair ${dry.data.repaired} volunteers. Apply?`;
                          if (!window.confirm(msg)) return;
                          const r = await axios.post(
                            `${API_URL}/api/studies/${studyId}/recover-ie-responses`,
                            { dry_run: false },
                            { withCredentials: true }
                          );
                          toast.success(`${r.data.repaired} voluntarios recuperados`);
                          // Reload current volunteer's ie_responses
                          const fresh = await axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true });
                          const updated = (fresh.data || []).find(x => x.volunteer_id === showIEDialog.volunteer_id);
                          if (updated) setIEResponses(updated.ie_responses || {});
                          load();
                        } catch (err) {
                          toast.error(errText(err, 'Error recuperando respuestas'));
                        }
                      }}
                      className="mt-1 text-xs inline-flex items-center gap-1 text-[#1f3e80] hover:underline font-semibold"
                    >
                      {lang === 'es' ? 'Recuperar respuestas huérfanas (todo el estudio)' : 'Recover orphan answers (whole study)'}
                    </button>
                  )}
                </div>
              );
            })()}
            {/* Inclusion Criteria */}
            {ieQuestions.filter(q => q.source === 'inclusion').length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-1 w-3 bg-green-500 rounded-full" />
                  <h3 className="text-sm font-semibold text-stone-800">{lb.ie_inclusion}</h3>
                </div>
                <div className="space-y-2">
                  {ieQuestions.filter(q => q.source === 'inclusion').map(q => (
                    <div key={q.question_id} data-testid={`ie-q-${q.question_id}`}
                      className={`flex items-center justify-between gap-4 px-3 py-2.5 rounded-sm border ${
                        ieResponses[q.question_id] === 'No' ? 'bg-red-50 border-red-200' :
                        ieResponses[q.question_id] === 'Sí' ? 'bg-green-50 border-green-200' :
                        'bg-white border-stone-200'
                      }`}>
                      <p className="text-sm text-stone-700 flex-1">{lang === 'es' ? q.text_es : q.text_en}</p>
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          data-testid={`ie-q-${q.question_id}-si`}
                          onClick={() => setIEResponse(q.question_id, 'Sí')}
                          className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                            ieResponses[q.question_id] === 'Sí'
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                          }`}
                        >Sí</button>
                        <button
                          type="button"
                          data-testid={`ie-q-${q.question_id}-no`}
                          onClick={() => setIEResponse(q.question_id, 'No')}
                          className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                            ieResponses[q.question_id] === 'No'
                              ? 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                          }`}
                        >No</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exclusion Criteria */}
            {ieQuestions.filter(q => q.source === 'exclusion').length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-1 w-3 bg-red-500 rounded-full" />
                  <h3 className="text-sm font-semibold text-stone-800">{lb.ie_exclusion}</h3>
                </div>
                <div className="space-y-2">
                  {ieQuestions.filter(q => q.source === 'exclusion').map(q => (
                    <div key={q.question_id} data-testid={`ie-q-${q.question_id}`}
                      className={`flex items-center justify-between gap-4 px-3 py-2.5 rounded-sm border ${
                        ieResponses[q.question_id] === 'Sí' ? 'bg-red-50 border-red-200' :
                        ieResponses[q.question_id] === 'No' ? 'bg-green-50 border-green-200' :
                        'bg-white border-stone-200'
                      }`}>
                      <p className="text-sm text-stone-700 flex-1">{lang === 'es' ? q.text_es : q.text_en}</p>
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          data-testid={`ie-q-${q.question_id}-si`}
                          onClick={() => setIEResponse(q.question_id, 'Sí')}
                          className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                            ieResponses[q.question_id] === 'Sí'
                              ? 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                          }`}
                        >Sí</button>
                        <button
                          type="button"
                          data-testid={`ie-q-${q.question_id}-no`}
                          onClick={() => setIEResponse(q.question_id, 'No')}
                          className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                            ieResponses[q.question_id] === 'No'
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                          }`}
                        >No</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Observations */}
            {ieQuestions.filter(q => q.source === 'observations').map(q => (
              <div key={q.question_id}>
                <Label className="text-sm font-semibold text-stone-800">
                  {lb.ie_observations}
                  {needsObservations() && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Textarea
                  data-testid="ie-observations"
                  ref={ieObservationsRef}
                  value={ieResponses[q.question_id] || ''}
                  onChange={e => { setIEResponse(q.question_id, e.target.value); setIEError(''); }}
                  placeholder={lang === 'es' ? 'Escriba sus observaciones...' : 'Write your observations...'}
                  className={`mt-1.5 min-h-[80px] bg-white ${needsObservations() && !(ieResponses[q.question_id] || '').trim() ? 'border-red-300 ring-2 ring-red-100' : 'border-stone-200'}`}
                />
              </div>
            ))}

            {/* Error message */}
            {ieError && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 flex items-start gap-2" data-testid="ie-error-msg">
                <WarningCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{ieError}</p>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4 flex justify-between gap-2">
            <Button variant="ghost" onClick={() => setShowIEDialog(null)}>{lb.cancel}</Button>
            <div className="flex gap-2">
              {showIEDialog?.status === 'pendiente' && (() => {
                // Apto: requires every question answered AND no failing criterion.
                // No Apto: requires every question answered AND at least one
                // failing criterion (inclusion='No' or exclusion='Si').
                // Until both conditions are met, the corresponding button is
                // disabled and shows an explanatory tooltip.
                const allAnswered = allIEAnswered();
                const fails = failsCriteria();
                const aptoEnabled = allAnswered && !fails;
                const noAptoEnabled = allAnswered && fails;
                const aptoTip = !allAnswered
                  ? (lang === 'es' ? 'Responde todas las preguntas primero' : 'Answer every question first')
                  : (fails ? (lang === 'es' ? 'Hay criterios incumplidos: el voluntario no puede ser Apto' : 'Volunteer fails at least one criterion') : '');
                const noAptoTip = !allAnswered
                  ? (lang === 'es' ? 'Responde todas las preguntas primero' : 'Answer every question first')
                  : (!fails ? (lang === 'es' ? 'No hay ningun criterio incumplido' : 'No failing criteria') : '');
                return (
                <>
                  <Button
                    variant="outline"
                    data-testid="ie-no-apto-btn"
                    onClick={() => {
                      if (!noAptoEnabled) return; // belt-and-suspenders
                      if (!validateIEObservations()) {
                        setIEError(lb.ie_obs_required);
                        setTimeout(() => {
                          if (ieObservationsRef.current) {
                            ieObservationsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            ieObservationsRef.current.focus();
                          }
                        }, 50);
                        return;
                      }
                      setShowExcludeSignature(true);
                    }}
                    disabled={ieSaving || !noAptoEnabled}
                    title={noAptoTip}
                    className={`rounded-sm gap-1 ${noAptoEnabled ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-stone-400 border-stone-200 cursor-not-allowed'}`}
                  >
                    <X size={14} />
                    {ieSaving ? '...' : lb.ie_exclude}
                  </Button>
                  <Button
                    data-testid={aptoEnabled ? 'ie-apto-btn' : 'ie-apto-btn-disabled'}
                    onClick={() => {
                      if (!aptoEnabled) {
                        if (fails) setShowCannotInclude(true);
                        return;
                      }
                      if (!validateIEObservations()) {
                        setIEError(lb.ie_obs_required);
                        setTimeout(() => {
                          if (ieObservationsRef.current) {
                            ieObservationsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            ieObservationsRef.current.focus();
                          }
                        }, 50);
                        return;
                      }
                      setShowIncludeSignature(true);
                    }}
                    disabled={ieSaving || !aptoEnabled}
                    title={aptoTip}
                    className={`rounded-sm gap-1 ${aptoEnabled ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-stone-300 text-stone-500 cursor-not-allowed hover:bg-stone-300'}`}
                  >
                    <Check size={14} weight="bold" />
                    {ieSaving ? '...' : lb.ie_include}
                  </Button>
                </>
                );
              })()}
              {showIEDialog?.status !== 'pendiente' && (
                <Button variant="outline" onClick={() => setShowIEDialog(null)} className="rounded-sm">
                  {lang === 'es' ? 'Cerrar' : 'Close'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URS-F420: popup Qsalud del voluntario incluido (iframe) */}
      <Dialog open={!!qsaludVolunteer} onOpenChange={v => { if (!v) setQsaludVolunteer(null); }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="qsalud-view-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 border-b border-stone-200">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Heartbeat size={18} className="text-emerald-600" />
              {lang === 'es' ? 'Qsalud del voluntario' : 'Volunteer Qsalud'}
              {qsaludVolunteer && (
                <span className="text-xs font-normal text-stone-500 ml-2">
                  {qsaludVolunteer.volunteer_name} · {qsaludVolunteer.study_volunteer_code || qsaludVolunteer.volunteer_id}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-stone-50">
            {qsaludVolunteer?.volunteer_id && (
              <iframe
                key={qsaludVolunteer.volunteer_id}
                src={`/volunteers/${qsaludVolunteer.volunteer_id}?embedded=1&tab=health&only_tab=health`}
                title="Qsalud"
                className="w-full h-full border-0"
                data-testid="qsalud-iframe"
              />
            )}
          </div>
          <div className="px-4 py-2 border-t border-stone-200 flex justify-end bg-white">
            <Button variant="ghost" onClick={() => setQsaludVolunteer(null)} data-testid="qsalud-close-btn">
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* URS-F423b: dialogo compartido para ver diagnostico/historial de envios */}
      <SatisfactionDiagnosticDialog
        studyId={studyId}
        open={showSatDiag}
        onOpenChange={setShowSatDiag}
      />

      {/* ===== CANNOT INCLUDE POPUP ===== */}
      <Dialog open={showCannotInclude} onOpenChange={setShowCannotInclude}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <WarningCircle size={22} className="text-red-500" />
              {lang === 'es' ? 'No se puede marcar como Apto' : 'Cannot mark as Eligible'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-700" data-testid="cannot-include-msg">{lb.ie_cannot_include}</p>
          <DialogFooter>
            <Button onClick={() => setShowCannotInclude(false)} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
              {lang === 'es' ? 'Entendido' : 'Understood'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== E-SIGNATURE FOR APTO ===== */}
      <ESignature
        open={showIncludeSignature}
        onClose={() => setShowIncludeSignature(false)}
        onSigned={(signature) => {
          setShowIncludeSignature(false);
          handleApto(showIEDialog);
        }}
        action={lang === 'es' ? 'Marcar voluntario como Apto' : 'Mark volunteer as Eligible'}
        resource={showIEDialog ? `${studyId}/${showIEDialog.volunteer_id}` : ''}
        lang={lang}
      />

      {/* ===== SINTOMA SUBJETIVO PACIENTE DIALOG ===== */}
      <Dialog open={!!showSymptomDialog} onOpenChange={v => { if (!v) setShowSymptomDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Circle size={14} weight="fill" className="text-violet-500" />
              {lang === 'es' ? 'Sintoma Subjetivo Paciente' : 'Subjective Symptom (Patient)'}
            </DialogTitle>
            <DialogDescription>
              {showSymptomDialog?.assignment?.volunteer_name} ({showSymptomDialog?.assignment?.study_volunteer_code || showSymptomDialog?.assignment?.volunteer_id})
              {showSymptomDialog?.visit_code && (
                <span className="ml-2 text-violet-700 font-medium">· {lang === 'es' ? 'Visita' : 'Visit'} {showSymptomDialog.visit_code}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {((showSymptomDialog && symptomsByKey[`${showSymptomDialog.assignment.study_volunteer_code}|${showSymptomDialog.visit_code}`]) || []).map((o, i) => (
              <div key={o.symptom_id || i} className="border border-violet-100 bg-violet-50/50 rounded p-3" data-testid={`symptom-row-${o.symptom_id || i}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs font-semibold text-violet-900">
                    <span className="text-stone-500 mr-1">{lang === 'es' ? 'Visita' : 'Visit'}:</span>
                    {o.visit_code}
                  </p>
                  {o.created_at && (
                    <p className="text-[10px] text-stone-500">{new Date(o.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                  )}
                </div>
                <p className="text-sm text-stone-800 whitespace-pre-wrap">{o.observation}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSymptomDialog(null)} className="rounded-sm">
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== E-SIGNATURE FOR NO APTO ===== */}
      <ESignature
        open={showExcludeSignature}
        onClose={() => setShowExcludeSignature(false)}
        onSigned={(signature) => {
          setShowExcludeSignature(false);
          handleNoApto(showIEDialog);
        }}
        action={lang === 'es' ? 'Marcar voluntario como No Apto' : 'Mark volunteer as Not Eligible'}
        resource={showIEDialog ? `${studyId}/${showIEDialog.volunteer_id}` : ''}
        lang={lang}
      />

      {/* ===== FILL QUESTIONNAIRE DIALOG ===== */}
      <Dialog open={!!fillQ} onOpenChange={v => { if (!v) { setFillQ(null); setEaEntries(null); setEaNotApplicable(false); } }}>
        <DialogContent className={`${qPanelOpen ? 'max-w-[96vw] w-[96vw] h-[92vh]' : 'max-w-2xl'} max-h-[92vh] overflow-hidden flex flex-col`} data-testid="fq-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <ClipboardText size={20} className="text-[#1f3e80]" />
              {fillQ?.questionnaire?.name}
              {fillQ?.readonly && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-sm bg-green-100 text-green-800 border border-green-300">
                  <Check size={10} weight="bold" />{lb.q_locked_badge}
                </span>
              )}
              {/* URS-F275: button to compare volunteer photos (Baseline vs Current visit)
                  available on EVERY questionnaire, not only on derm-scale / MASI ones. */}
              <Button type="button" variant="outline" size="sm" data-testid="fq-query-btn"
                onClick={() => (qPanelOpen && !focusQueryId ? setQPanelOpen(false) : openQueryPanel())}
                className="ml-auto h-7 gap-1 text-xs rounded-sm text-amber-800 border-amber-300 hover:bg-amber-50">
                <ChatCircleText size={13} />Query
                {fqOpenQueries.length > 0 && (
                  <span data-testid="fq-query-count" className="ml-0.5 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{fqOpenQueries.length}</span>
                )}
              </Button>
              {fillQ?.vol?.study_volunteer_code && (
                <div data-testid="fq-header-photo-compare">
                  <VolunteerPhotoCompare
                    studyId={studyId}
                    volunteerCode={fillQ.vol.study_volunteer_code}
                    currentVisitCode={fillQ?.visit?.code || fillQ?.visit?.visit_code}
                    triggerLabel={lang === 'es' ? 'Ver fotos del voluntario' : 'View volunteer photos'}
                    testIdPrefix="fq-header-photo"
                  />
                </div>
              )}
            </DialogTitle>
            <DialogDescription>
              {fillQ?.vol?.study_volunteer_code} — {fillQ?.vol?.volunteer_name} | {fillQ?.visit?.code} {fillQ?.visit?.name ? `— ${fillQ.visit.name}` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className={`flex-1 min-h-0 flex gap-4 ${qPanelOpen ? 'flex-col lg:flex-row' : 'flex-col'}`}>
          <div className={`flex-1 min-h-0 flex flex-col gap-4 ${qPanelOpen ? 'lg:basis-2/3' : ''}`}>
          {/* URS-F499: sub-pestanas EA (varios eventos adversos en el mismo cuestionario). */}
          {eaEntries && eaEntries.length > 0 && (
            <div className="border-b border-stone-200 -mx-6 px-6 pb-2" data-testid="ea-subtabs">
              <div className="flex items-center gap-1 flex-wrap">
                {eaEntries.map((e, i) => {
                  const active = i === activeEaIdx;
                  const label = `${entryPrefix(fillQ?.questionnaire?.type)}${(e.entry_index ?? i) + 1}`;
                  const statusDot = e.status === 'completed' ? 'bg-green-500' : e.status === 'in_progress' ? 'bg-yellow-500' : 'bg-red-500';
                  return (
                    <div key={e.entry_index ?? i} className={`inline-flex items-center gap-1.5 rounded-t-sm border border-b-0 px-2.5 py-1.5 text-xs cursor-pointer ${active ? 'bg-white border-stone-300 font-semibold text-[#1f3e80]' : 'bg-stone-50 border-stone-200 text-stone-600 hover:bg-stone-100'} ${eaNotApplicable ? 'opacity-60 pointer-events-none' : ''}`}
                      data-testid={`ea-tab-${e.entry_index ?? i}`}
                      onClick={() => switchEaTab(i)}>
                      <Circle size={7} weight="fill" className={statusDot} />
                      <span>{label}</span>
                      {eaEntries.length > 1 && e.status !== 'completed' && active && !eaNotApplicable && (
                        <button
                          type="button"
                          data-testid={`ea-tab-remove-${e.entry_index ?? i}`}
                          onClick={(ev) => { ev.stopPropagation(); removeEaEntry(i); }}
                          className="ml-1 text-stone-400 hover:text-red-600"
                          title={removeEntryTitle(fillQ?.questionnaire?.type, lang)}
                        ><X size={11} /></button>
                      )}
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEaEntry}
                  disabled={eaNotApplicable}
                  className="h-7 gap-1 text-xs text-red-700 border-red-300 hover:bg-red-50 ml-1"
                  data-testid="ea-tab-add-btn"
                >
                  <Plus size={12} />{addEntryLabel(fillQ?.questionnaire?.type, lang)}
                </Button>
              </div>
            </div>
          )}

          {/* URS-F500 / URS-F503: check "No aplica" a nivel visita multi-entry. */}
          {eaEntries && (
            <div className="border border-orange-200 bg-orange-50/60 rounded-sm px-3 py-2 flex items-center gap-2" data-testid="ea-not-applicable-wrap">
              <input
                type="checkbox"
                id="ea-not-applicable"
                data-testid="ea-not-applicable-checkbox"
                checked={eaNotApplicable}
                disabled={!!fillQ?.readonly}
                onChange={(ev) => setEaNotApplicable(ev.target.checked)}
                className="h-4 w-4 accent-orange-600 cursor-pointer disabled:cursor-not-allowed"
              />
              <label htmlFor="ea-not-applicable" className={`text-sm text-orange-900 select-none ${fillQ?.readonly ? '' : 'cursor-pointer'}`}>
                {notApplicableLabel(fillQ?.questionnaire?.type, lang)}
              </label>
            </div>
          )}

          {fillQ?.readonly && (
            <div className="bg-green-50 border border-green-200 rounded-sm px-3 py-2 text-xs text-green-800 space-y-1">
              <p>{lb.q_locked_note}</p>
              {fillQ?.existing?.signature && (
                <p className="text-[11px] text-green-700">
                  {lang === 'es' ? 'Firmado por' : 'Signed by'}: <span className="font-semibold">{fillQ.existing.signature.user_email || fillQ.existing.updated_by || '—'}</span>
                  {fillQ.existing.signature.signed_at ? ` · ${new Date(fillQ.existing.signature.signed_at).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}` : ''}
                </p>
              )}
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto pr-1" data-testid="fq-scroll-area">
            <div className="space-y-3">
              <div className="flex justify-end sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-1" data-testid="fq-monitor-col-header">
                <span className="whitespace-nowrap text-right text-[10px] font-semibold uppercase tracking-wider text-teal-700">{lang === 'es' ? 'Monitorización' : 'Monitoring'}</span>
              </div>
              {/* URS-F499b: dedup defensivo por question_id — evita que un
                  cuestionario con preguntas duplicadas (mismo question_id
                  guardado dos veces por error) pinte cards duplicadas
                  que comparten estado y se auto-rellenan al escribir. */}
              {(() => {
                const raw = fillQ?.questionnaire?.questions || [];
                const seen = new Set();
                const dedup = [];
                for (const q of raw) {
                  if (q?.type === 'section_header') { dedup.push(q); continue; }
                  // Salto condicional: ocultar preguntas cuya condicion no se cumple.
                  if (!isConditionMet(q, fillAnswers)) continue;
                  const key = q?.question_id;
                  if (!key || seen.has(key)) continue;
                  seen.add(key);
                  dedup.push(q);
                }
                return dedup;
              })().map((q, idx) => (
              q.type === 'section_header' ? (
                <div key={q.question_id || `sh-${idx}`} data-testid={`fq-section-${idx}`} className="border-t-2 border-violet-300 pt-3 mt-1">
                  <h4 className="text-sm font-bold uppercase tracking-wide text-violet-700">{q.title_es || ''}</h4>
                </div>
              ) : (
              <div key={q.question_id || idx} id={`fq-card-${q.question_id}`} className="relative flex items-stretch gap-2">
              {(() => {
                const qs = fqOpenQueries.filter(x => x.question_id === q.question_id);
                return qs.length > 0 ? (
                  <div className="absolute top-2 right-16 z-[1]">
                    <QuestionQueryBadge qid={q.question_id} count={qs.length} lang={lang} onClick={() => openQueryPanel(qs[0].query_id)} />
                  </div>
                ) : null;
              })()}
              <fieldset disabled={!!fillQ?.readonly || eaNotApplicable} data-testid={`fq-${q.question_id || idx}`}
                className={`flex-1 min-w-0 bg-white border rounded-sm px-4 py-3 disabled:opacity-60 transition-shadow duration-300 ${highlightQid === q.question_id ? 'border-[#1f3e80] ring-2 ring-[#1f3e80]/30' : 'border-stone-200'}`}>
                <p className="text-sm font-medium text-stone-800 mb-2 pr-12">{q.text_es || q.text_en || q.text || ''}</p>
                {q.type === 'text' && (
                  <Input value={fillAnswers[q.question_id] || ''} onChange={e => setFillAnswers(p => ({ ...p, [q.question_id]: e.target.value }))}
                    placeholder={lang === 'es' ? 'Escribir respuesta...' : 'Write answer...'} className="bg-white" />
                )}
                {q.type === 'number' && (
                  <Input type="number" value={fillAnswers[q.question_id] || ''} onChange={e => setFillAnswers(p => ({ ...p, [q.question_id]: e.target.value }))} className="bg-white w-40" />
                )}
                {q.type === 'textarea' && (
                  <Textarea value={fillAnswers[q.question_id] || ''} onChange={e => setFillAnswers(p => ({ ...p, [q.question_id]: e.target.value }))}
                    className="bg-white min-h-[50px]" />
                )}
                {(q.type === 'select' || q.type === 'scale' || q.type === 'radio') && (
                  <div className="flex gap-1.5 flex-wrap" data-testid={`fq-select-${q.question_id}`}>
                    {(q.options || []).map(opt => (
                      <button key={opt} type="button" onClick={() => setFillAnswers(p => ({ ...p, [q.question_id]: opt }))}
                        className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors disabled:cursor-not-allowed ${fillAnswers[q.question_id] === opt ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
                {q.type === 'multiselect' && (
                  // URS-F392: soporte multiselect tambien en el flujo interno
                  // (staff rellena por el voluntario). Se guarda como array.
                  <div className="flex gap-1.5 flex-wrap" data-testid={`fq-multiselect-${q.question_id}`}>
                    {(q.options || []).map(opt => {
                      const cur = Array.isArray(fillAnswers[q.question_id]) ? fillAnswers[q.question_id] : [];
                      const sel = cur.includes(opt);
                      return (
                        <button key={opt} type="button" onClick={() => setFillAnswers(p => ({
                          ...p,
                          [q.question_id]: sel ? cur.filter(x => x !== opt) : [...cur, opt]
                        }))}
                          className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'}`}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                )}
                {q.type === 'table_text' && (
                  <div className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`fq-table-${q.question_id}`}>
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="text-left font-semibold text-stone-700 px-3 py-2 w-2/5 border-b border-stone-200">&nbsp;</th>
                          <th className="text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200">{lang === 'es' ? 'Respuesta' : 'Answer'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(q.options || []).map((opt, oi) => {
                          // URS-F271: opciones que empiezan por '##' actuan como
                          // cabeceras de seccion (colSpan a toda la fila).
                          const isSection = typeof opt === 'string' && opt.trim().startsWith('##');
                          if (isSection) {
                            const label = opt.replace(/^##\s*/, '').trim();
                            return (
                              <tr key={`sec-${oi}`} data-testid={`fq-table-${q.question_id}-section-${oi}`}>
                                <td colSpan={2} className="bg-violet-50/70 border-t border-violet-200 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
                                  {label}
                                </td>
                              </tr>
                            );
                          }
                          const cur = (typeof fillAnswers[q.question_id] === 'object' && fillAnswers[q.question_id]) || {};
                          const isNum = q.cell_type === 'number';
                          return (
                            <tr key={opt + oi} className="border-t border-stone-100">
                              <td className="px-3 py-2 text-stone-700 align-middle">{opt}</td>
                              <td className="px-3 py-2">
                                <Input
                                  type={isNum ? 'number' : 'text'}
                                  inputMode={isNum ? 'decimal' : undefined}
                                  value={cur[opt] || ''}
                                  onKeyDown={isNum ? (e) => {
                                    // URS-F241: bloquear caracteres no numericos
                                    // (number input nativo deja escribir 'e','+','-')
                                    if (['e', 'E', '+'].includes(e.key)) e.preventDefault();
                                  } : undefined}
                                  onChange={e => {
                                    let val = e.target.value;
                                    if (isNum) {
                                      // Sanea: solo digitos, un punto/coma decimal y signo - inicial.
                                      val = val.replace(/[^0-9.,-]/g, '');
                                      val = val.replace(/(?!^)-/g, ''); // - solo al principio
                                      const parts = val.replace(',', '.').split('.');
                                      if (parts.length > 2) val = `${parts[0]}.${parts.slice(1).join('')}`;
                                    }
                                    setFillAnswers(p => ({
                                      ...p,
                                      [q.question_id]: { ...(typeof p[q.question_id] === 'object' && p[q.question_id] ? p[q.question_id] : {}), [opt]: val }
                                    }));
                                  }}
                                  className="bg-white h-8 text-sm"
                                  data-testid={`fq-table-${q.question_id}-${oi}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {q.show_observations !== false && (
                      <div className="border-t border-stone-200 bg-stone-50/40 px-3 py-2 space-y-1">
                        <Label className="text-[11px] font-medium text-stone-600">{lang === 'es' ? 'Observaciones' : 'Observations'}</Label>
                        <Textarea
                          value={((typeof fillAnswers[q.question_id] === 'object' && fillAnswers[q.question_id]) || {})._observations || ''}
                          onChange={e => setFillAnswers(p => ({
                            ...p,
                            [q.question_id]: { ...(typeof p[q.question_id] === 'object' && p[q.question_id] ? p[q.question_id] : {}), _observations: e.target.value }
                          }))}
                          placeholder={lang === 'es' ? 'Comentarios adicionales (opcional)...' : 'Additional comments (optional)...'}
                          className="bg-white min-h-[50px] text-sm"
                          data-testid={`fq-table-obs-${q.question_id}`}
                        />
                      </div>
                    )}
                  </div>
                )}
                {q.type === 'table_select' && (() => {
                  // URS-F270: en preguntas de Tolerancia con tabla, anadimos una
                  // tercera columna "Relacion con el producto" con dropdown fijo
                  // de farmacovigilancia. Se guarda en la misma respuesta bajo la
                  // clave `__rel:${opt}` para mantener compat con respuestas
                  // historicas que solo guardan `cur[opt]`.
                  //
                  // Heuristica:
                  //  - q.category === 'tolerancia' (preguntas creadas correctamente
                  //    propagando category desde el Banco), o
                  //  - el cuestionario contenedor es de tipo 'tolerancia'
                  //    (fallback retrocompat para preguntas importadas antes del
                  //    fix de URS-F271 que no llevan category).
                  const qCat = (q.category || '').toLowerCase();
                  const sqType = (fillQ?.questionnaire?.type || '').toLowerCase();
                  const isTolerancia = qCat === 'tolerancia' || sqType === 'tolerancia';
                  const RELATION_OPTS = ['No relacionado', 'Improbable', 'Posible', 'Probable', 'Seguro', 'No valorable'];
                  return (
                  <div className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`fq-table-select-${q.question_id}`}>
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className={`text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200 ${isTolerancia ? 'w-1/3' : 'w-2/5'}`}>&nbsp;</th>
                          <th className="text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200">{lang === 'es' ? 'Respuesta' : 'Answer'}</th>
                          {isTolerancia && (
                            <th className="text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200 w-1/3" data-testid={`fq-table-select-${q.question_id}-rel-head`}>
                              {lang === 'es' ? 'Relación con el producto' : 'Product relation'}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(q.options || []).map((opt, oi) => {
                          // URS-F271: opciones que empiezan por '##' actuan como
                          // cabeceras de seccion (colSpan a toda la fila). Las
                          // opciones siguientes quedan agrupadas hasta la
                          // proxima '##' o el final.
                          const isSection = typeof opt === 'string' && opt.trim().startsWith('##');
                          if (isSection) {
                            const label = opt.replace(/^##\s*/, '').trim();
                            return (
                              <tr key={`sec-${oi}`} className="border-t border-stone-100" data-testid={`fq-table-select-${q.question_id}-section-${oi}`}>
                                <td
                                  colSpan={isTolerancia ? 3 : 2}
                                  className="bg-violet-50/70 border-t border-violet-200 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700"
                                >
                                  {label}
                                </td>
                              </tr>
                            );
                          }
                          const cur = (typeof fillAnswers[q.question_id] === 'object' && fillAnswers[q.question_id]) || {};
                          return (
                            <tr key={opt + oi} className="border-t border-stone-100">
                              <td className="px-3 py-2 text-stone-700 align-middle">{opt}</td>
                              <td className="px-3 py-2">
                                <select
                                  value={cur[opt] || ''}
                                  onChange={e => setFillAnswers(p => ({
                                    ...p,
                                    [q.question_id]: { ...(typeof p[q.question_id] === 'object' && p[q.question_id] ? p[q.question_id] : {}), [opt]: e.target.value }
                                  }))}
                                  className="bg-white border border-stone-200 rounded-sm h-8 text-sm px-2 w-full"
                                  data-testid={`fq-table-select-${q.question_id}-${oi}`}
                                  disabled={fillQ?.readonly}
                                >
                                  <option value="">{lang === 'es' ? '— Selecciona —' : '— Select —'}</option>
                                  {(q.cell_options || []).map(co => (
                                    <option key={co} value={co}>{co}</option>
                                  ))}
                                </select>
                              </td>
                              {isTolerancia && (
                                <td className="px-3 py-2">
                                  <select
                                    value={cur[`__rel:${opt}`] || 'No valorable'}
                                    onChange={e => setFillAnswers(p => ({
                                      ...p,
                                      [q.question_id]: { ...(typeof p[q.question_id] === 'object' && p[q.question_id] ? p[q.question_id] : {}), [`__rel:${opt}`]: e.target.value }
                                    }))}
                                    className="bg-white border border-stone-200 rounded-sm h-8 text-sm px-2 w-full"
                                    data-testid={`fq-table-select-${q.question_id}-rel-${oi}`}
                                    disabled={fillQ?.readonly}
                                  >
                                    {RELATION_OPTS.map(ro => (
                                      <option key={ro} value={ro}>{ro}</option>
                                    ))}
                                  </select>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {/* URS-F243: campo libre de "Observaciones" opcional debajo de la tabla (gobernado por show_observations) */}
                    {q.show_observations !== false && (
                      <div className="border-t border-stone-200 bg-stone-50/40 px-3 py-2 space-y-1">
                        <Label className="text-[11px] font-medium text-stone-600">{lang === 'es' ? 'Observaciones' : 'Observations'}</Label>
                        <Textarea
                          value={((typeof fillAnswers[q.question_id] === 'object' && fillAnswers[q.question_id]) || {})._observations || ''}
                          onChange={e => setFillAnswers(p => ({
                            ...p,
                            [q.question_id]: { ...(typeof p[q.question_id] === 'object' && p[q.question_id] ? p[q.question_id] : {}), _observations: e.target.value }
                          }))}
                          placeholder={lang === 'es' ? 'Comentarios adicionales (opcional)...' : 'Additional comments (optional)...'}
                          className="bg-white min-h-[50px] text-sm"
                          data-testid={`fq-table-select-obs-${q.question_id}`}
                        />
                      </div>
                    )}
                  </div>
                  );
                })()}
                {q.type === 'custom_table' && (() => {
                  // URS-F352: tabla NxM personalizada (cabeceras + tipos de celda)
                  const cur = (typeof fillAnswers[q.question_id] === 'object' && fillAnswers[q.question_id]) || {};
                  return (
                    <div className="space-y-2" data-testid={`fq-custom-table-wrap-${q.question_id}`}>
                      <CustomTableRenderer
                        question={q}
                        answers={cur}
                        onChange={(next) => setFillAnswers(p => ({ ...p, [q.question_id]: next }))}
                        readOnly={!!fillQ?.readonly}
                        lang={lang}
                      />
                      {q.show_observations !== false && (
                        <div className="border border-stone-200 bg-stone-50/40 rounded-sm px-3 py-2 space-y-1">
                          <Label className="text-[11px] font-medium text-stone-600">{lang === 'es' ? 'Observaciones' : 'Observations'}</Label>
                          <Textarea
                            value={cur._observations || ''}
                            onChange={e => setFillAnswers(p => ({
                              ...p,
                              [q.question_id]: { ...((typeof p[q.question_id] === 'object' && p[q.question_id]) || {}), _observations: e.target.value },
                            }))}
                            placeholder={lang === 'es' ? 'Comentarios adicionales (opcional)...' : 'Additional comments (optional)...'}
                            className="bg-white min-h-[50px] text-sm"
                            data-testid={`fq-custom-table-obs-${q.question_id}`}
                            disabled={!!fillQ?.readonly}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
                {q.type === 'checklist' && (() => {
                  // URS-F355: lista de verificación — multiselección con checkboxes verticales.
                  // Payload: array de strings con los items seleccionados.
                  const cur = Array.isArray(fillAnswers[q.question_id]) ? fillAnswers[q.question_id] : [];
                  const items = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
                  return (
                    <div className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`fq-checklist-${q.question_id}`}>
                      {items.map((it, ii) => {
                        const checked = cur.includes(it);
                        return (
                          <label
                            key={it + ii}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-stone-50 ${ii > 0 ? 'border-t border-stone-100' : ''} ${checked ? 'bg-violet-50/40' : ''}`}
                            data-testid={`fq-checklist-row-${q.question_id}-${ii}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!!fillQ?.readonly}
                              onChange={() => setFillAnswers(p => {
                                const prev = Array.isArray(p[q.question_id]) ? p[q.question_id] : [];
                                return { ...p, [q.question_id]: checked ? prev.filter(x => x !== it) : [...prev, it] };
                              })}
                              className="w-4 h-4 rounded-sm border-stone-300 text-[#1f3e80] focus:ring-[#1f3e80] cursor-pointer"
                              data-testid={`fq-checklist-cb-${q.question_id}-${ii}`}
                            />
                            <span className="text-sm text-stone-700 select-none">{it}</span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
                {/* URS-F507: NPS simple (0-10). Un unico grid de botones.
                    Payload: string con el numero elegido (ej. '8'). */}
                {q.type === 'nps' && (
                  <div className="flex flex-wrap gap-1.5" data-testid={`fq-nps-${q.question_id}`}>
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
                      const active = fillAnswers[q.question_id] === String(n);
                      return (
                        <button
                          key={n}
                          type="button"
                          data-testid={`fq-nps-btn-${q.question_id}-${n}`}
                          onClick={() => setFillAnswers(p => ({ ...p, [q.question_id]: String(n) }))}
                          className={`w-9 h-9 rounded-sm border text-sm font-semibold transition-colors ${active ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'}`}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                )}
                {q.type === 'nps_multi' && (() => {
                  // URS-F354: NPS múltiple — varios sub-apartados, cada uno con escala 0-10.
                  // Payload: { 'Sub-apartado X': '8', ..., '_observations': '...' }.
                  const cur = (typeof fillAnswers[q.question_id] === 'object' && fillAnswers[q.question_id]) || {};
                  const subs = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
                  return (
                    <div className="space-y-2" data-testid={`fq-nps-multi-${q.question_id}`}>
                      <div className="border border-stone-200 rounded-sm overflow-hidden">
                        {subs.map((sub, si) => (
                          <div key={sub + si} className={`flex flex-col gap-1.5 px-3 py-2.5 ${si > 0 ? 'border-t border-stone-100' : ''}`}>
                            <span className="text-sm text-stone-700">{sub}</span>
                            <div className="flex flex-wrap gap-1.5">
                              {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
                                const sel = cur[sub] === String(n);
                                return (
                                  <button
                                    key={n}
                                    type="button"
                                    data-testid={`fq-nps-multi-${q.question_id}-${si}-${n}`}
                                    onClick={() => setFillAnswers(p => ({
                                      ...p,
                                      [q.question_id]: { ...((typeof p[q.question_id] === 'object' && p[q.question_id]) || {}), [sub]: String(n) },
                                    }))}
                                    disabled={!!fillQ?.readonly}
                                    className={`w-9 h-9 rounded-sm border text-sm font-semibold transition-colors ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'}`}
                                  >
                                    {n}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex justify-between text-[10px] text-stone-400 px-1">
                              <span>0 · {lang === 'es' ? 'Nada probable' : 'Not at all likely'}</span>
                              <span>10 · {lang === 'es' ? 'Muy probable' : 'Extremely likely'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {q.show_observations !== false && (
                        <div className="border border-stone-200 bg-stone-50/40 rounded-sm px-3 py-2 space-y-1">
                          <Label className="text-[11px] font-medium text-stone-600">{lang === 'es' ? 'Observaciones' : 'Observations'}</Label>
                          <Textarea
                            value={cur._observations || ''}
                            onChange={e => setFillAnswers(p => ({
                              ...p,
                              [q.question_id]: { ...((typeof p[q.question_id] === 'object' && p[q.question_id]) || {}), _observations: e.target.value },
                            }))}
                            placeholder={lang === 'es' ? 'Comentarios adicionales (opcional)...' : 'Additional comments (optional)...'}
                            className="bg-white min-h-[50px] text-sm"
                            data-testid={`fq-nps-multi-obs-${q.question_id}`}
                            disabled={!!fillQ?.readonly}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
                {q.type === 'composite_scale' && (() => {
                  // URS-F358: escala compuesta — sub-apartados con escalas dermatológicas + suma.
                  const cur = (typeof fillAnswers[q.question_id] === 'object' && fillAnswers[q.question_id]) || {};
                  return (
                    <div className="space-y-2" data-testid={`fq-composite-scale-${q.question_id}`}>
                      <CompositeScaleRenderer
                        question={q}
                        answers={cur}
                        onChange={(next) => setFillAnswers(p => ({ ...p, [q.question_id]: next }))}
                        readOnly={!!fillQ?.readonly}
                        lang={lang}
                        studyId={studyId}
                      />
                    </div>
                  );
                })()}
                {(q.type === 'likert_1_5' || q.type === 'likert') && (() => {
                  const size = q.scale_size === 7 ? 7 : 5;
                  const nums = Array.from({ length: size }, (_, i) => i + 1);
                  const leftLabel = size === 7 ? 'Me disgusta mucho' : 'Totalmente en desacuerdo';
                  const rightLabel = size === 7 ? 'Me gusta mucho' : 'Totalmente de acuerdo';
                  return (
                  <div>
                    <div className={`grid gap-1.5 ${size === 7 ? 'grid-cols-7' : 'grid-cols-5'}`}>
                      {nums.map(n => {
                        const sel = fillAnswers[q.question_id] === String(n);
                        return (
                          <button
                            key={n}
                            type="button"
                            data-testid={`fq-likert-${q.question_id}-${n}`}
                            onClick={() => setFillAnswers(p => ({ ...p, [q.question_id]: String(n) }))}
                            className={`py-2 rounded-sm border text-sm font-semibold transition disabled:cursor-not-allowed ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1f3e80]/40'}`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[10px] text-stone-400 px-1 mt-1">
                      <span>1 · {leftLabel}</span>
                      <span>{size} · {rightLabel}</span>
                    </div>
                  </div>
                  );
                })()}
                {q.type === 'derm_scale' && q.scale_id && (
                  <DermScaleRenderer
                    scaleId={q.scale_id}
                    value={fillAnswers[q.question_id] || ''}
                    onChange={(v) => setFillAnswers(p => ({ ...p, [q.question_id]: v }))}
                    testIdPrefix={`fq-derm-${q.question_id}`}
                    studyId={studyId}
                    volunteerCode={fillQ?.vol?.study_volunteer_code}
                    currentVisitCode={fillQ?.visit?.code || fillQ?.visit?.visit_code}
                  />
                )}
                {q.type === 'masi' && (
                  <MasiRenderer
                    value={fillAnswers[q.question_id] || ''}
                    onChange={(v) => setFillAnswers(p => ({ ...p, [q.question_id]: v }))}
                    testIdPrefix={`fq-masi-${q.question_id}`}
                    studyId={studyId}
                    volunteerCode={fillQ?.vol?.study_volunteer_code}
                    currentVisitCode={fillQ?.visit?.code || fillQ?.visit?.visit_code}
                  />
                )}
                {q.type === 'date' && (
                  <Input data-testid={`fq-date-${q.question_id}`} type="date" value={fillAnswers[q.question_id] || ''} onChange={e => setFillAnswers(p => ({ ...p, [q.question_id]: e.target.value }))} className="bg-white w-48" />
                )}
                {/* URS-F502: fecha con opcion "continua". Valor:
                    { date: 'YYYY-MM-DD'|'', ongoing: bool }. Si ongoing
                    esta marcado, el input de fecha queda deshabilitado y
                    su valor se ignora (validacion pasa). */}
                {q.type === 'date_ongoing' && (() => {
                  const raw = fillAnswers[q.question_id];
                  const val = (typeof raw === 'object' && raw !== null) ? raw : { date: (raw || ''), ongoing: false };
                  const setVal = (patch) => setFillAnswers(p => ({ ...p, [q.question_id]: { ...val, ...patch } }));
                  return (
                    <div className="flex flex-wrap items-center gap-3">
                      <Input
                        data-testid={`fq-date-ongoing-date-${q.question_id}`}
                        type="date"
                        value={val.date || ''}
                        disabled={!!val.ongoing}
                        onChange={e => setVal({ date: e.target.value })}
                        className="bg-white w-48 disabled:opacity-60"
                      />
                      <label className="inline-flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid={`fq-date-ongoing-check-${q.question_id}`}
                          checked={!!val.ongoing}
                          onChange={e => setVal({ ongoing: e.target.checked, date: e.target.checked ? '' : val.date })}
                          className="h-3.5 w-3.5 accent-[#1f3e80] cursor-pointer"
                        />
                        <span>{lang === 'es' ? 'Continua (sin fecha de fin)' : 'Ongoing (no end date)'}</span>
                      </label>
                    </div>
                  );
                })()}
                {q.type === 'boolean' && (
                  <div className="flex gap-2">
                    {['si', 'no', 'na'].map(opt => (
                      <button key={opt} type="button" data-testid={`fq-bool-${q.question_id}-${opt}`}
                        onClick={() => setFillAnswers(p => ({ ...p, [q.question_id]: opt }))}
                        className={`px-4 py-1.5 text-xs font-medium rounded-sm border transition-colors disabled:cursor-not-allowed ${fillAnswers[q.question_id] === opt ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'}`}>
                        {opt === 'si' ? (lang === 'es' ? 'Si' : 'Yes') : opt === 'no' ? 'No' : (lang === 'es' ? 'No aplica' : 'N/A')}
                      </button>
                    ))}
                  </div>
                )}
                {q.type === 'boolean_with_obs' && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      {['si', 'no', 'na'].map(opt => (
                        <button key={opt} type="button" data-testid={`fq-boolobs-${q.question_id}-${opt}`}
                          onClick={() => setFillAnswers(p => ({ ...p, [q.question_id]: opt, ...(opt !== 'si' ? { [q.question_id + '_obs']: '' } : {}) }))}
                          className={`px-4 py-1.5 text-xs font-medium rounded-sm border transition-colors disabled:cursor-not-allowed ${fillAnswers[q.question_id] === opt ? (opt === 'si' ? 'bg-red-600 text-white border-red-600' : 'bg-[#1f3e80] text-white border-[#1f3e80]') : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'}`}>
                          {opt === 'si' ? (lang === 'es' ? 'Si' : 'Yes') : opt === 'no' ? 'No' : (lang === 'es' ? 'No aplica' : 'N/A')}
                        </button>
                      ))}
                    </div>
                    {fillAnswers[q.question_id] === 'si' && (
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-red-700">
                          {lang === 'es' ? 'Observaciones (obligatorio)' : 'Observations (required)'} <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          data-testid={`fq-boolobs-text-${q.question_id}`}
                          value={fillAnswers[q.question_id + '_obs'] || ''}
                          onChange={e => setFillAnswers(p => ({ ...p, [q.question_id + '_obs']: e.target.value }))}
                          placeholder={lang === 'es' ? 'Describa las observaciones...' : 'Describe the observations...'}
                          className={`min-h-[60px] bg-white ${fillAnswers[q.question_id] === 'si' && !(fillAnswers[q.question_id + '_obs'] || '').trim() ? 'border-red-300 focus:ring-red-400' : 'border-stone-200'}`}
                        />
                      </div>
                    )}
                  </div>
                )}
              </fieldset>
              <MonitorCell qid={q.question_id} mark={fqMonitoring[q.question_id]} lang={lang} busy={monitorBusy}
                canEdit={isStudyMonitor && fqCompleted} disabledReason={monitorOffReason} onToggle={toggleMonitor} />
              </div>
              )
              ))}
            </div>
            <QuestionnaireHistory studyId={studyId} target={monitorTarget} lang={lang}
              refreshKey={`${fillQ?.existing?.status}|${fillQ?.existing?.monitored?.at || ''}|${fqQueries.map(q => q.status).join(',')}`} />
          </div>
          </div>
          {qPanelOpen && fillQ && (
            <div className="lg:basis-1/3 min-h-0 max-h-[45vh] lg:max-h-none flex flex-col" data-testid="fq-queries-column">
              <QuestionnaireQueriesPanel
                studyId={studyId} fillQ={fillQ} entryIndex={activeEntryIndex} queries={fqQueries} lang={lang}
                questions={(fillQ.questionnaire.questions || []).filter(q => fqRequiredQids.includes(q.question_id))}
                canCreate={isAdmin || isStudyMonitor} canCloseQuery={canCloseQuery}
                focusQueryId={focusQueryId} onFocus={setFocusQueryId} onRefresh={loadQueries}
                onGoToQuestion={goToQuestion} onCollapse={() => setQPanelOpen(false)} startNew={qPanelStartNew}
              />
            </div>
          )}
          </div>

          <DialogFooter className="mt-4 flex justify-between gap-2">
            {fillQ?.readonly ? (
              <>
                <div className="flex-1" />
                <MonitorFooterButton studyId={studyId} target={monitorTarget} monitored={fqMonitored} lang={lang}
                  isMonitor={isStudyMonitor} completed={fqCompleted} missingCount={fqMissingMarks}
                  onChanged={(m) => patchExisting({ monitored: m })} />
                {isAdmin && (
                  <Button
                    variant="outline"
                    data-testid="fq-reopen-btn"
                    onClick={() => { setReopenReason(''); setReopenDialog(true); }}
                    className="rounded-sm gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                  >
                    <ArrowCounterClockwise size={14} />{lb.q_reopen}
                  </Button>
                )}
                <Button
                  data-testid="fq-close-btn"
                  onClick={() => setFillQ(null)}
                  className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm"
                >
                  {lb.q_close}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setFillQ(null)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                <div className="flex gap-2">
                  <MonitorFooterButton studyId={studyId} target={monitorTarget} monitored={null} lang={lang}
                    isMonitor={isStudyMonitor} completed={false} missingCount={fqMissingMarks} onChanged={() => {}} />
                  <Button variant="outline" data-testid="fq-save-btn" onClick={() => handleSaveQ(false)} disabled={fillSaving}
                    className="rounded-sm gap-1 text-yellow-700 border-yellow-300 hover:bg-yellow-50">
                    {fillSaving ? '...' : lb.q_save}
                  </Button>
                  <Button data-testid="fq-finish-btn" onClick={() => {
                    // URS-F500: si "No aplica" esta marcado en un EA,
                    // saltamos TODAS las validaciones de campos y vamos
                    // directo a la firma electronica.
                    if (eaNotApplicable) {
                      setShowESign(true);
                      return;
                    }
                    const obsQuestions = (fillQ?.questionnaire?.questions || []).filter(q => q.type === 'boolean_with_obs');
                    const missingObs = obsQuestions.find(q => fillAnswers[q.question_id] === 'si' && !(fillAnswers[q.question_id + '_obs'] || '').trim());
                    if (missingObs) {
                      toast.error(lang === 'es' ? 'Debe completar las observaciones para las preguntas marcadas como "Si"' : 'You must fill observations for questions marked as "Yes"');
                      return;
                    }
                    // URS-F241: table_text y table_select -> SIEMPRE todas las
                    // filas obligatorias para finalizar (semantica del tipo).
                    const tableQs = (fillQ?.questionnaire?.questions || []).filter(q => q.type === 'table_text' || q.type === 'table_select');
                    const missingTable = tableQs.find(q => {
                      const v = fillAnswers[q.question_id];
                      const obj = (typeof v === 'object' && v) ? v : {};
                      return (q.options || []).some(opt => !(obj[opt] || '').toString().trim());
                    });
                    if (missingTable) {
                      toast.error(lang === 'es' ? 'Debes rellenar todas las filas de las preguntas tipo tabla antes de finalizar' : 'All table rows must be filled before finalizing');
                      return;
                    }
                    // URS-F352: custom_table -> validar todas las celdas interiores.
                    const ctQs = (fillQ?.questionnaire?.questions || []).filter(q => q.type === 'custom_table');
                    const missingCt = ctQs.find(q => customTableMissing(q, fillAnswers[q.question_id]).length > 0);
                    if (missingCt) {
                      toast.error(lang === 'es' ? 'Debes rellenar todas las celdas de las tablas personalizadas antes de finalizar' : 'All custom table cells must be filled before finalizing');
                      return;
                    }
                    // URS-F354: nps_multi -> todos los sub-apartados obligatorios.
                    const npsQs = (fillQ?.questionnaire?.questions || []).filter(q => q.type === 'nps_multi');
                    const missingNps = npsQs.find(q => {
                      const v = fillAnswers[q.question_id];
                      const obj = (typeof v === 'object' && v) ? v : {};
                      const subs = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
                      return subs.some(sub => !(obj[sub] || '').toString().trim());
                    });
                    if (missingNps) {
                      toast.error(lang === 'es' ? 'Debes puntuar todos los sub-apartados del NPS múltiple antes de finalizar' : 'All multi-NPS sub-items must be scored before finalizing');
                      return;
                    }
                    // URS-F358: composite_scale -> todos los sub-apartados deben tener valor.
                    const csQs = (fillQ?.questionnaire?.questions || []).filter(q => q.type === 'composite_scale');
                    const missingCs = csQs.find(q => compositeScaleMissing(q, fillAnswers[q.question_id]).length > 0);
                    if (missingCs) {
                      toast.error(lang === 'es' ? 'Debes puntuar todos los sub-apartados de la escala compuesta antes de finalizar' : 'All composite scale sub-items must be scored before finalizing');
                      return;
                    }
                    // URS-F342: bloquear "completar" si CUALQUIER pregunta
                    // (escala, derm_scale, likert, select, etc.) esta vacia.
                    // Se hace ANTES de abrir el dialogo de firma para no
                    // gastar firma del usuario.
                    const miss = missingAnswers();
                    if (miss.length) {
                      const preview = miss.slice(0, 5).map((m, i) => `${i + 1}. ${m}`).join('\n');
                      const more = miss.length > 5 ? `\n... y ${miss.length - 5} mas` : '';
                      toast.error(
                        (lang === 'es'
                          ? `Faltan ${miss.length} respuesta(s) para poder completar el cuestionario:\n`
                          : `${miss.length} answer(s) missing to complete the questionnaire:\n`) + preview + more,
                        { duration: 8000 }
                      );
                      return;
                    }
                    setShowESign(true);
                  }} disabled={fillSaving}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-sm gap-1">
                    <Check size={14} weight="bold" />
                    {fillSaving ? '...' : lb.q_finish}
                  </Button>
                </div>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== REOPEN QUESTIONNAIRE CONFIRM DIALOG (admin only) ===== */}
      <Dialog open={reopenDialog} onOpenChange={v => { if (!v && !reopening) { setReopenDialog(false); setReopenReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="reopen-q-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ArrowCounterClockwise size={18} />
              {lb.q_reopen_title}
            </DialogTitle>
            <DialogDescription>
              {fillQ?.questionnaire?.name} — {fillQ?.vol?.study_volunteer_code} · {fillQ?.visit?.code}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
            {lb.q_reopen_desc}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-stone-800">
              {lb.q_reopen_reason} <span className="text-red-500">*</span>
            </Label>
            <Textarea
              data-testid="reopen-q-reason"
              value={reopenReason}
              onChange={e => setReopenReason(e.target.value)}
              placeholder={lb.q_reopen_reason_ph}
              className="min-h-[70px] bg-white"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReopenDialog(false); setReopenReason(''); }} disabled={reopening}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              data-testid="reopen-q-confirm"
              onClick={handleReopen}
              disabled={reopening || !reopenReason.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-1"
            >
              <ArrowCounterClockwise size={14} />
              {reopening ? '...' : lb.q_reopen_confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!satisfactionDialog} onOpenChange={v => { if (!v) setSatisfactionDialog(null); }}>
        <DialogContent data-testid="satisfaction-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-violet-600" />
              {lang === 'es' ? 'Cuestionario de Satisfaccion' : 'Satisfaction Survey'}
            </DialogTitle>
            <DialogDescription className="pt-2 text-stone-600 text-sm">
              {satisfactionDialog?.completed
                ? (lang === 'es' ? 'Este voluntario ya completo el cuestionario. Puedes revisar sus respuestas o reabrirlo si se equivoco al rellenarlo (la respuesta actual se archivara).' : 'This volunteer has already completed the survey. You can review the answers or reopen it if they made a mistake (the current response will be archived).')
                : (lang === 'es' ? 'Puedes enviar (o reenviar) el enlace al voluntario por email. Se generara un enlace unico con 7 dias de caducidad.' : 'You can send (or resend) the link to the volunteer by email. A unique link will be generated, valid for 7 days.')}
            </DialogDescription>
          </DialogHeader>
          {satisfactionDialog && (
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-stone-500">{lang === 'es' ? 'Voluntario' : 'Volunteer'}:</span><span className="font-mono">{satisfactionDialog.vol.study_volunteer_code || satisfactionDialog.vol.volunteer_id}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">{lang === 'es' ? 'Visita' : 'Visit'}:</span><span className="font-mono">{satisfactionDialog.visit.code}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">{lang === 'es' ? 'Cuestionario' : 'Survey'}:</span><span>{satisfactionDialog.questionnaire.name}</span></div>
            </div>
          )}
          {satisfactionDialog?.completed && (() => {
            const responses = visitResponses[satisfactionDialog.vol.volunteer_id] || [];
            const r = responses.find(x => x.visit_id === satisfactionDialog.visit.visit_id && x.questionnaire_id === satisfactionDialog.questionnaire.questionnaire_id);
            const answers = r?.answers || {};
            // URS-F343: las preguntas tipo seccion deben verse como titulos
            // separadores (no como respuestas vacias) y la numeracion debe
            // restablecer la cuenta solo para preguntas reales.
            const sectionTitle = (q) => {
              if (['section', 'header', 'section_header'].includes(q.type)) {
                return (q.title_es || q.title_en || q.text_es || q.text_en || q.text || '').trim();
              }
              const t = (q.text_es || q.text_en || q.text || '').trim();
              if (t.startsWith('##')) return t.replace(/^##\s*/, '').trim();
              return null;
            };
            const allQuestions = satisfactionDialog.questionnaire.questions || [];
            const hasRealQuestion = allQuestions.some(q => sectionTitle(q) === null);
            let realIdx = 0;
            return (
              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1" data-testid="satisfaction-answers-list">
                <p className="text-[11px] uppercase font-semibold text-stone-500 tracking-wider">{lang === 'es' ? 'Respuestas' : 'Answers'}</p>
                {!hasRealQuestion ? (
                  <p className="text-xs text-stone-400 italic">{lang === 'es' ? 'Sin preguntas' : 'No questions'}</p>
                ) : allQuestions.map((q, i) => {
                  const secTitle = sectionTitle(q);
                  if (secTitle !== null) {
                    return (
                      <div key={q.question_id || `sh-${i}`} data-testid={`satisfaction-answers-section-${i}`}
                        className="border-t-2 border-violet-300 pt-2 pb-1 mt-2 first:mt-0 first:border-t-0 first:pt-0">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-violet-700">{secTitle || (lang === 'es' ? 'Seccion' : 'Section')}</h4>
                      </div>
                    );
                  }
                  realIdx += 1;
                  const qid = q.question_id || q.id;
                  const val = answers[qid];
                  const display = Array.isArray(val) ? val.join(', ') : (val ?? '—');
                  return (
                    <div key={qid || i} className="border border-stone-200 rounded-sm p-2 bg-white">
                      <p className="text-xs text-stone-700"><span className="text-stone-400 font-mono mr-1">{realIdx}.</span>{q.text_es || q.text_en || q.text || ''}</p>
                      <p className="text-sm text-stone-900 mt-0.5 font-medium">{display === '' ? '—' : display}</p>
                    </div>
                  );
                })}
                {r?.completed_at || r?.updated_at ? (
                  <p className="text-[10px] text-stone-400 text-right pt-1">
                    {lang === 'es' ? 'Completado' : 'Completed'}: {new Date(r.updated_at || r.created_at).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}
                  </p>
                ) : null}
              </div>
            );
          })()}
          {satReopenConfirm && (
            <div
              data-testid="sat-reopen-warning"
              className="rounded-sm border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900"
            >
              {lang === 'es'
                ? 'La respuesta actual del voluntario se archivara (trazabilidad ALCOA+) y el cuestionario volvera a estado pendiente. Se enviara un nuevo enlace unico por email al voluntario.'
                : 'The current response will be archived (ALCOA+ traceability) and the survey will return to pending. A new unique link will be sent to the volunteer by email.'}
            </div>
          )}
          {(!satisfactionDialog?.completed || satReopenConfirm) && (
            <div className="rounded-sm border border-stone-200 p-2 bg-white space-y-1.5" data-testid="sat-sender-picker">
              <p className="text-[11px] text-stone-600 font-medium">
                {lang === 'es' ? 'Remitente' : 'Sender'}
              </p>
              <select
                value={satSenderChoice}
                onChange={(e) => setSatSenderChoice(e.target.value)}
                className="w-full h-7 text-xs border border-stone-200 rounded-sm px-2 bg-white"
                data-testid="sat-sender-select"
              >
                <option value="">
                  {lang === 'es' ? 'Por defecto' : 'Default'}{satSenders.default?.email ? ` — ${satSenders.default.email}` : ''}
                </option>
                {satSenders.alternatives.map(a => (
                  <option key={a.email} value={a.email}>{a.name} — {a.email}</option>
                ))}
                <option value="custom">{lang === 'es' ? 'Otro... (introducir)' : 'Other... (custom)'}</option>
              </select>
              {satSenderChoice === 'custom' && (
                <div className="grid grid-cols-2 gap-1.5">
                  <Input value={satCustomEmail} onChange={e => setSatCustomEmail(e.target.value)} type="email"
                    placeholder={lang === 'es' ? 'Email autorizado' : 'Authorized email'} className="h-7 text-xs" data-testid="sat-sender-custom-email" />
                  <Input value={satCustomName} onChange={e => setSatCustomName(e.target.value)}
                    placeholder={lang === 'es' ? 'Nombre visible' : 'Display name'} className="h-7 text-xs" data-testid="sat-sender-custom-name" />
                </div>
              )}
              {satSenderChoice === 'custom' && (
                <p className="text-[10px] text-amber-700 italic">
                  {lang === 'es' ? 'Recuerda: el email tiene que estar autorizado en tu cuenta de Acumbamail.' : 'Reminder: email must be authorized in your Acumbamail account.'}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            {isAdmin && (
              <Button
                variant="ghost"
                onClick={() => { setSatisfactionDialog(null); setShowSatDiag(true); }}
                data-testid="open-sat-history-from-dialog"
                className="rounded-sm text-[#1f3e80] hover:bg-blue-50 mr-auto"
                title={lang === 'es' ? 'Ver historial de envíos de este estudio' : 'View sending history'}
              >
                <ClockCounterClockwise size={14} className="mr-1" />
                {lang === 'es' ? 'Historial de envíos' : 'Sending history'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setSatisfactionDialog(null)} className="rounded-sm">
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </Button>
            {satisfactionDialog?.completed && !satReopenConfirm && (
              <Button
                data-testid="reopen-satisfaction-btn"
                onClick={() => setSatReopenConfirm(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm gap-2"
              >
                <ArrowClockwise size={14} />
                {lang === 'es' ? 'Reabrir y reenviar' : 'Reopen and resend'}
              </Button>
            )}
            {(!satisfactionDialog?.completed || satReopenConfirm) && (
              <Button
                data-testid="send-satisfaction-btn"
                disabled={sendingSat || (satSenderChoice === 'custom' && !satCustomEmail.includes('@'))}
                onClick={async () => {
                  setSendingSat(true);
                  try {
                    const body = {
                      volunteer_id: satisfactionDialog.vol.volunteer_id,
                      visit_id: satisfactionDialog.visit.visit_id,
                      // URS-F289: especificar el cuestionario concreto (multi-asignacion)
                      questionnaire_id: satisfactionDialog.questionnaire?.questionnaire_id,
                    };
                    // URS-F391: si venimos del flujo "Reabrir", pedimos al
                    // backend que archive la respuesta previa antes de crear
                    // la nueva invitacion.
                    if (satReopenConfirm) body.reopen = true;
                    if (satSenderChoice === 'custom' && satCustomEmail.includes('@')) {
                      body.from_email = satCustomEmail.trim();
                      body.from_name = satCustomName.trim() || satCustomEmail.split('@')[0];
                    } else if (satSenderChoice && satSenderChoice !== '') {
                      const alt = satSenders.alternatives.find(a => a.email === satSenderChoice);
                      if (alt) { body.from_email = alt.email; body.from_name = alt.name; }
                    }
                    await axios.post(
                      `${API_URL}/api/studies/${studyId}/survey-invitations/send`,
                      body,
                      { withCredentials: true }
                    );
                    toast.success(satReopenConfirm
                      ? (lang === 'es' ? 'Cuestionario reabierto y enlace reenviado' : 'Survey reopened and link resent')
                      : (lang === 'es' ? 'Enlace enviado por email' : 'Link sent by email'));
                    // URS-F391: refrescamos las respuestas del voluntario para
                    // que la visita vuelva a mostrarse en estado "pendiente".
                    if (satReopenConfirm && satisfactionDialog?.vol?.volunteer_id) {
                      try {
                        const { data } = await axios.get(
                          `${API_URL}/api/studies/${studyId}/volunteers/${satisfactionDialog.vol.volunteer_id}/visit-responses`,
                          { withCredentials: true }
                        );
                        setVisitResponses(prev => ({ ...prev, [satisfactionDialog.vol.volunteer_id]: data }));
                      } catch (_) { /* noop */ }
                    }
                    setSatisfactionDialog(null);
                  } catch (e) {
                    toast.error(errText(e, 'Error'));
                  }
                  setSendingSat(false);
                }}
                className={`${satReopenConfirm ? 'bg-amber-600 hover:bg-amber-700' : 'bg-violet-600 hover:bg-violet-700'} text-white rounded-sm gap-2`}
              >
                <PaperPlaneTilt size={14} />
                {sendingSat ? '...' : (satReopenConfirm
                  ? (lang === 'es' ? 'Confirmar reenvío' : 'Confirm resend')
                  : (lang === 'es' ? 'Enviar enlace' : 'Send link'))}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== CONFLICT DIALOG ===== */}
      <Dialog open={!!showConflict} onOpenChange={() => { setShowConflict(null); setPendingVol(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WarningCircle size={20} className="text-amber-500" />
              {lb.conflict_title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-700">{lb.conflict_msg}</p>
            <div className="max-h-[45vh] overflow-y-auto space-y-2">
            {showConflict && showConflict.map(s => (
              <div key={s.study_id} className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-sm font-semibold text-stone-900">{s.study_code} — {s.study_title}</p>
                <p className="text-xs text-stone-500">{lang === 'es' ? 'Asignado como' : 'Assigned as'}: {s.study_volunteer_code}</p>
              </div>
            ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowConflict(null); setPendingVol(null); }} className="rounded-sm">
              {lb.conflict_reject}
            </Button>
            <Button data-testid="confirm-conflict-btn" onClick={() => pendingVol && doAssign(pendingVol.volunteer_id, true)} disabled={saving}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
              {saving ? '...' : lb.conflict_accept}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== STATUS CHANGE DIALOG ===== */}
      <Dialog open={!!showStatusDialog} onOpenChange={() => setShowStatusDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{lb.change_status}: {showStatusDialog?.study_volunteer_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{lb.status}</Label>
              <Select value={statusForm.status} onValueChange={v => setStatusForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="vol-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const currentStatus = showStatusDialog?.status;
                    let options = [];
                    if (currentStatus === 'incluido') options = ['excluido', 'abandono'];
                    // URS-F475: desde `apto` no tiene sentido pasar a `no_apto`
                    // (No Apto es una decision del cuestionario I/E, no un
                    // cambio manual valido). Ocultamos la opcion.
                    else if (currentStatus === 'apto') options = ['incluido', 'excluido', 'abandono'];
                    else options = ['incluido', 'excluido', 'abandono', 'apto', 'no_apto'];
                    // URS-F475: filtrar el estado actual (redundante).
                    options = options.filter(s => s !== currentStatus);
                    return options.map(s => (
                      <SelectItem key={s} value={s}>{lb[s]}</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
              <div className="mt-1.5 space-y-0.5 text-xs text-stone-500 italic">
                <p>{lang === 'es' ? 'Abandono: decisión del voluntario' : 'Withdrawal: volunteer\'s decision'}</p>
                <p>{lang === 'es' ? 'Excluido: decisión técnica' : 'Excluded: technical decision'}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{lb.notes} {(statusForm.status === 'excluido' || statusForm.status === 'abandono' || statusForm.status === 'no_apto') && <span className="text-red-500">*</span>}</Label>
              <Textarea data-testid="vol-status-notes" value={statusForm.notes} onChange={e => setStatusForm(p => ({ ...p, notes: e.target.value }))}
                placeholder={lang === 'es' ? 'Motivo del cambio de estado...' : 'Reason for status change...'} 
                className={`min-h-[60px] ${(statusForm.status === 'excluido' || statusForm.status === 'abandono' || statusForm.status === 'no_apto') && !statusForm.notes?.trim() ? 'border-red-300' : ''}`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowStatusDialog(null)}>{lb.cancel}</Button>
            <Button data-testid="save-vol-status-btn" onClick={() => {
              if (!statusForm.status) return;
              if ((statusForm.status === 'excluido' || statusForm.status === 'abandono' || statusForm.status === 'no_apto') && !statusForm.notes?.trim()) {
                toast.error(lang === 'es' ? 'El motivo es obligatorio para este estado' : 'Reason is required for this status');
                return;
              }
              setShowStatusSignature(true);
            }} disabled={saving}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== E-SIGNATURE FOR STATUS CHANGE ===== */}
      <ESignature
        open={showStatusSignature}
        onClose={() => setShowStatusSignature(false)}
        onSigned={(signature) => {
          setShowStatusSignature(false);
          handleStatusChange();
        }}
        action={lang === 'es' ? `Cambiar estado a ${lb[statusForm.status] || statusForm.status}` : `Change status to ${lb[statusForm.status] || statusForm.status}`}
        resource={showStatusDialog ? `${studyId}/${showStatusDialog.volunteer_id}` : ''}
        lang={lang}
      />

      {/* ===== GENERATE APPOINTMENTS DIALOG ===== */}
      <Dialog open={showGenerate} onOpenChange={v => { if (!v) { setShowGenerate(false); setGenStep('config'); } }}>
        <DialogContent className={genStep === 'preview' ? 'max-w-6xl max-h-[92vh] overflow-hidden flex flex-col' : 'max-w-2xl max-h-[90vh] overflow-hidden flex flex-col'}>
          <DialogHeader><DialogTitle>{lb.gen_title}</DialogTitle></DialogHeader>

          {genStep === 'config' && (
            <>
              <div className="flex-1 overflow-hidden">
                {/* Single column form (day-view calendar moved to preview step) */}
                <div className="space-y-4 overflow-y-auto pr-1 h-full">
                  <p className="text-sm text-stone-600">{lb.gen_desc}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>{lb.gen_base_date} <span className="text-red-500">*</span></Label>
                      <Input data-testid="gen-base-date" type="date" value={genForm.base_date} onChange={e => setGenForm(p => ({ ...p, base_date: e.target.value }))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{lb.gen_base_time}</Label>
                      <Input data-testid="gen-base-time" type="time" value={genForm.base_time} onChange={e => setGenForm(p => ({ ...p, base_time: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">{lb.gen_volunteers} ({genForm.volunteer_ids.length})</Label>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="text-xs h-6 rounded-sm"
                          onClick={() => setGenForm(p => ({ ...p, volunteer_ids: assigned.map(a => a.volunteer_id) }))}>{lb.gen_select_all}</Button>
                        <Button variant="outline" size="sm" className="text-xs h-6 rounded-sm"
                          onClick={() => setGenForm(p => ({ ...p, volunteer_ids: assigned.filter(a => a.status === 'incluido').map(a => a.volunteer_id) }))}>{lb.gen_select_included}</Button>
                      </div>
                    </div>
                    <div className="relative mb-2">
                      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <Input
                        data-testid="gen-vol-search"
                        value={genVolSearch}
                        onChange={e => setGenVolSearch(e.target.value)}
                        placeholder={lang === 'es' ? 'Buscar voluntario por nombre o V-code...' : 'Search volunteer by name or V-code...'}
                        className="pl-9 h-8 bg-white border-stone-200 text-xs"
                      />
                    </div>
                    {(() => {
                      const q = (genVolSearch || '').trim().toLowerCase();
                      const filtered = q
                        ? assigned.filter(a =>
                            (a.volunteer_name || '').toLowerCase().includes(q) ||
                            (a.study_volunteer_code || '').toLowerCase().includes(q) ||
                            String(a.study_volunteer_num || '').includes(q)
                          )
                        : assigned;
                      const sorted = sortByVCode(filtered);
                      if (sorted.length === 0) {
                        return <p className="text-xs text-stone-400 italic text-center py-4">{lang === 'es' ? 'Sin resultados' : 'No results'}</p>;
                      }
                      return (
                        <div className="max-h-[300px] overflow-y-auto space-y-1">
                          {sorted.map(a => (
                            <label key={a.volunteer_id} data-testid={`gen-vol-${a.study_volunteer_code}`}
                              className={`flex items-center gap-3 px-3 py-1.5 rounded-sm border cursor-pointer transition-colors ${genForm.volunteer_ids.includes(a.volunteer_id) ? 'bg-[#1f3e80]/5 border-[#1f3e80]/30' : 'bg-white border-stone-200'}`}>
                              <input type="checkbox" checked={genForm.volunteer_ids.includes(a.volunteer_id)} onChange={() => toggleGenVol(a.volunteer_id)}
                                className="rounded border-stone-300 text-[#1f3e80] focus:ring-[#1f3e80]" />
                              <span className="font-mono text-sm font-semibold text-[#1f3e80]">{a.study_volunteer_code || '-'}</span>
                              <span className="text-sm text-stone-700">{a.volunteer_name}</span>
                              <Badge variant="outline" className={`text-[10px] ml-auto ${volStatusColors[a.status] || ''}`}>{lb[a.status]}</Badge>
                            </label>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowGenerate(false)}>{lb.cancel}</Button>
                <Button data-testid="preview-generate-btn" onClick={handlePreview}
                  className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
                  {lang === 'es' ? 'Previsualizar citas' : 'Preview appointments'}
                </Button>
              </DialogFooter>
            </>
          )}

          {genStep === 'preview' && (
            <>
              <p className="text-sm text-stone-600">
                {lang === 'es'
                  ? `Se van a crear ${previewAppts.length} citas. Las visitas con offset intra-dia (T30MIN, T2H, T1D+30MIN...) llevan chip morado indicando el desplazamiento aplicado a la hora base T0. Selecciona una fila y haz clic en el calendario para asignar la hora, o edita fecha/hora manualmente.`
                  : `${previewAppts.length} appointments will be created. Visits with intra-day offset (T30MIN, T2H, T1D+30MIN...) show a purple chip with the offset applied to the T0 base time. Select a row and click a slot to set the time, or edit date/time manually.`}
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 flex-1 overflow-hidden">
                {/* Left: table */}
                <div className="overflow-y-auto border border-stone-200 rounded-sm">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50">
                      <TableHead className="w-16 sticky top-0 bg-stone-50">V-Code</TableHead>
                      <TableHead className="sticky top-0 bg-stone-50">{lang === 'es' ? 'Voluntario' : 'Volunteer'}</TableHead>
                      <TableHead className="w-16 sticky top-0 bg-stone-50">{lang === 'es' ? 'Visita' : 'Visit'}</TableHead>
                      <TableHead className="sticky top-0 bg-stone-50">{lang === 'es' ? 'Nombre visita' : 'Visit name'}</TableHead>
                      <TableHead className="w-16 sticky top-0 bg-stone-50">{lang === 'es' ? 'Dia' : 'Day'}</TableHead>
                      <TableHead className="w-36 sticky top-0 bg-stone-50">{lang === 'es' ? 'Fecha' : 'Date'}</TableHead>
                      <TableHead className="w-28 sticky top-0 bg-stone-50">{lang === 'es' ? 'Hora' : 'Time'}</TableHead>
                      <TableHead className="w-20 sticky top-0 bg-stone-50">{lang === 'es' ? 'Dur.' : 'Dur.'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewAppts.map((a, i) => {
                      const outOfSchedule = studySlots.length > 0 && !isInStudySchedule(a.date, a.time);
                      // URS-F444/F445: representacion compacta del offset intra-dia
                      // (T30MIN -> "+30m", T2H -> "+2h", T-30MIN -> "-30m",
                      // T1D+30MIN -> "+30m" junto al Dia=1). Solo se muestra
                      // cuando minute_offset != 0.
                      const mo = parseInt(a.minute_offset, 10) || 0;
                      let offsetLabel = '';
                      if (mo !== 0) {
                        const sign = mo < 0 ? '-' : '+';
                        const abs = Math.abs(mo);
                        const hh = Math.floor(abs / 60);
                        const mm = abs % 60;
                        offsetLabel = hh > 0 && mm > 0 ? `${sign}${hh}h${mm}m`
                                    : hh > 0 ? `${sign}${hh}h`
                                    : `${sign}${mm}m`;
                      }
                      return (
                      <TableRow
                        key={i}
                        data-testid={`preview-row-${i}`}
                        onClick={() => setActivePreviewIdx(i)}
                        className={`cursor-pointer ${activePreviewIdx === i ? 'bg-[#1f3e80]/10 hover:bg-[#1f3e80]/15' : (outOfSchedule ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-stone-50')} ${i > 0 && a.volunteer_id !== previewAppts[i - 1].volunteer_id ? 'border-t-2 border-stone-300' : ''}`}>
                        <TableCell className="font-mono text-sm font-bold text-[#1f3e80]">{a.study_volunteer_code}</TableCell>
                        <TableCell className="text-sm text-stone-700">
                          {a.volunteer_name}
                        </TableCell>
                        <TableCell className="font-mono text-sm font-semibold text-stone-800">
                          <div className="flex items-center gap-1.5">
                            <span>{a.visit_code}</span>
                            {offsetLabel && (
                              <span
                                data-testid={`preview-offset-${i}`}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 text-[10px] font-semibold border border-indigo-200"
                                title={lang === 'es' ? `Offset intra-dia derivado del codigo (${offsetLabel})` : `Intra-day offset derived from code (${offsetLabel})`}>
                                {offsetLabel}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">{a.visit_name}</TableCell>
                        <TableCell className="text-xs text-stone-500">
                          {a.visit_day}{offsetLabel ? <span className="text-indigo-600 ml-1">({offsetLabel})</span> : null}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Input data-testid={`preview-date-${i}`} type="date" value={a.date}
                              onFocus={() => setActivePreviewIdx(i)}
                              onClick={e => { e.stopPropagation(); setActivePreviewIdx(i); }}
                              onChange={e => { setActivePreviewIdx(i); updatePreviewAppt(i, 'date', e.target.value); }}
                              className={`h-7 text-sm border-stone-200 ${outOfSchedule ? 'bg-amber-50' : 'bg-white'}`} />
                            {outOfSchedule && (
                              <span data-testid={`preview-out-of-schedule-${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 text-[10px] font-semibold border border-amber-300 whitespace-nowrap" title={lang === 'es' ? 'Fuera de programacion del estudio' : 'Out of study schedule'}>
                                <WarningCircle size={10} weight="fill" />
                                {lang === 'es' ? 'Fuera de prog.' : 'Out of sched.'}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input data-testid={`preview-time-${i}`} type="time" value={a.time}
                            onFocus={() => setActivePreviewIdx(i)}
                            onClick={e => { e.stopPropagation(); setActivePreviewIdx(i); }}
                            onChange={e => { setActivePreviewIdx(i); updatePreviewAppt(i, 'time', e.target.value); }}
                            className={`h-7 text-sm border-stone-200 ${outOfSchedule ? 'bg-amber-50' : 'bg-white'}`} />
                        </TableCell>
                        <TableCell className="text-xs text-stone-600 text-center">{a.duration_minutes} min</TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>

                {/* Right: day-view calendar */}
                <div className="border-l border-stone-200 pl-4 flex flex-col overflow-hidden" data-testid="preview-day-calendar">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">{lang === 'es' ? 'Agenda del dia' : 'Day agenda'}</Label>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-7 w-7"
                        onClick={() => { const d = new Date(calendarDay); d.setDate(d.getDate() - 1); setCalendarDay(d.toISOString().split('T')[0]); }}
                        data-testid="preview-day-prev"><CaretLeft size={12} /></Button>
                      <Input type="date" value={calendarDay} onChange={e => setCalendarDay(e.target.value)}
                        className="h-7 text-xs w-36" data-testid="preview-day-input" />
                      <Button variant="outline" size="icon" className="h-7 w-7"
                        onClick={() => { const d = new Date(calendarDay); d.setDate(d.getDate() + 1); setCalendarDay(d.toISOString().split('T')[0]); }}
                        data-testid="preview-day-next"><CaretRight size={12} /></Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-stone-400 mb-2">
                    {lang === 'es'
                      ? `Fila activa: ${previewAppts[activePreviewIdx]?.study_volunteer_code || '-'} · ${previewAppts[activePreviewIdx]?.visit_code || ''}. Haz clic en un hueco para asignar la hora.`
                      : `Active row: ${previewAppts[activePreviewIdx]?.study_volunteer_code || '-'} · ${previewAppts[activePreviewIdx]?.visit_code || ''}. Click a slot to set the time.`}
                  </p>
                  <div className="flex-1 overflow-y-auto border border-stone-200 rounded-sm bg-stone-50/30">
                    {(() => {
                      const studyMap = {};
                      (allStudies || []).forEach(s => { studyMap[s.study_id] = s; });
                      const totalHeight = DAY_TIME_SLOTS.length * DAY_CELL_HEIGHT;
                      return (
                        <div className="relative" style={{ height: totalHeight + 'px' }}>
                          {/* Cuadricula de slots de 5 min + etiquetas horarias */}
                          {DAY_TIME_SLOTS.map((slot, idx) => {
                            const isHour = slot.m === 0;
                            const isQuarter = slot.m === 15 || slot.m === 30 || slot.m === 45;
                            const borderClass = isHour
                              ? 'border-b border-stone-200'
                              : (isQuarter ? 'border-b border-stone-100' : 'border-b border-stone-50');
                            return (
                              <div
                                key={`slot-${slot.label}`}
                                data-testid={`preview-slot-${slot.label}`}
                                onClick={() => {
                                  const idxRow = activePreviewIdx;
                                  if (idxRow < 0 || idxRow >= previewAppts.length) return;
                                  setPreviewAppts(prev => prev.map((a, i) => i === idxRow ? { ...a, date: calendarDay, time: slot.label } : a));
                                }}
                                className={`absolute left-0 right-0 cursor-pointer hover:bg-blue-50/40 transition-colors ${borderClass}`}
                                style={{ top: idx * DAY_CELL_HEIGHT + 'px', height: DAY_CELL_HEIGHT + 'px', zIndex: 1 }}
                                title={`${calendarDay} ${slot.label}`}
                              >
                                {isHour && (
                                  <span className="absolute left-1 top-0 text-[9px] text-stone-500 font-mono bg-stone-50/80 px-1 pointer-events-none select-none">
                                    {slot.label}
                                  </span>
                                )}
                              </div>
                            );
                          })}

                          {/* Huecos de programacion del estudio (bandas de color por estudio) */}
                          {dayScheduleSlots.map((cs, i) => {
                            const study = studyMap[cs.study_id];
                            const color = study?.color || '#888';
                            const startMin = timeToMinutesFrom8(cs.start_time);
                            const endMin = timeToMinutesFrom8(cs.end_time);
                            const top = (startMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                            const height = Math.max(0, ((endMin - startMin) / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT);
                            if (height <= 0) return null;
                            return (
                              <div
                                key={`sched-${i}`}
                                className="absolute left-10 right-1 pointer-events-none"
                                style={{
                                  top: top + 'px',
                                  height: height + 'px',
                                  backgroundColor: hexToRgba(color, 0.15),
                                  borderLeft: `3px solid ${hexToRgba(color, 0.5)}`,
                                  zIndex: 2,
                                }}
                                title={`${study?.code || cs.study_id} · ${cs.visit_code || ''} · ${cs.start_time}-${cs.end_time}`}
                              >
                                <div className="px-1 py-0.5 text-[9px] font-medium truncate select-none" style={{ color: hexToRgba(color, 0.85) }}>
                                  {study?.code || cs.study_id}{cs.visit_code ? ` · ${cs.visit_code}` : ''}
                                </div>
                              </div>
                            );
                          })}

                          {/* Bloqueos del dia (globales o por estudio) */}
                          {dayScheduleBlocks.map(b => {
                            const startMin = timeToMinutesFrom8(b.start_time);
                            const endMin = timeToMinutesFrom8(b.end_time);
                            const top = (startMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                            const height = Math.max(0, ((endMin - startMin) / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT);
                            if (height <= 0) return null;
                            const isGlobal = !b.study_id;
                            const study = !isGlobal ? studyMap[b.study_id] : null;
                            const bg = isGlobal
                              ? 'repeating-linear-gradient(45deg, rgba(220,38,38,0.28) 0px, rgba(220,38,38,0.28) 8px, rgba(220,38,38,0.12) 8px, rgba(220,38,38,0.12) 16px)'
                              : 'repeating-linear-gradient(45deg, rgba(220,38,38,0.18) 0px, rgba(220,38,38,0.18) 6px, rgba(220,38,38,0.06) 6px, rgba(220,38,38,0.06) 12px)';
                            return (
                              <div
                                key={b.block_id}
                                data-testid={`preview-block-${b.block_id}`}
                                className="absolute left-10 right-1 pointer-events-none"
                                style={{
                                  top: top + 'px',
                                  height: height + 'px',
                                  background: bg,
                                  borderLeft: isGlobal ? '3px solid #dc2626' : '3px solid rgba(220,38,38,0.5)',
                                  borderTop: '1px solid rgba(220,38,38,0.4)',
                                  borderBottom: '1px solid rgba(220,38,38,0.4)',
                                  zIndex: 4,
                                }}
                                title={`${isGlobal ? (lang === 'es' ? 'Bloqueo global' : 'Global block') : (study?.code || b.study_id)} · ${b.start_time}-${b.end_time} · ${b.reason || ''}`}
                              >
                                <div className="px-1 py-0.5 text-[9px] font-medium text-red-800 truncate select-none">
                                  {isGlobal ? '🔒 ' : ''}
                                  {isGlobal ? (lang === 'es' ? 'Bloqueo global' : 'Global block') : (study?.code || b.study_id)}
                                  {b.reason ? <span className="text-red-700/80 ml-1">· {b.reason}</span> : null}
                                </div>
                              </div>
                            );
                          })}

                          {/* Citas existentes del dia (otros estudios + este estudio) */}
                          {!loadingDayAppts && dayAppointments.map(ap => {
                            const startMin = timeToMinutesFrom8(ap.time || '08:00');
                            const top = (startMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                            const dur = ap.duration_minutes || 30;
                            const height = (dur / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                            if (top < 0 || top > totalHeight) return null;
                            const isCurrentStudy = ap.study_id === studyId;
                            return (
                              <div
                                key={ap.appointment_id}
                                className={`absolute left-10 right-1 rounded-sm px-1.5 py-0.5 text-[10px] overflow-hidden border pointer-events-none ${isCurrentStudy ? 'bg-[#1f3e80]/15 border-[#1f3e80]/40 text-[#1f3e80]' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                                style={{ top: top + 'px', height: Math.max(height, 18) + 'px', zIndex: 5 }}
                                title={`${ap.time} | ${ap.volunteer_name || ''} | ${ap.study_id || ''} | ${ap.visit_code || ''}`}
                              >
                                <div className="flex items-center gap-1 font-mono font-semibold">
                                  {ap.time} {isCurrentStudy && <span className="text-[9px] uppercase">·{lang === 'es' ? 'Este estudio' : 'This study'}</span>}
                                </div>
                                <div className="truncate leading-tight">
                                  {ap.study_volunteer_code || ''} {ap.volunteer_name || ''} {ap.visit_code ? `· ${ap.visit_code}` : ''}
                                </div>
                              </div>
                            );
                          })}

                          {/* Filas de preview (citas que se van a crear) */}
                          {previewAppts.map((p, i) => {
                            if (p.date !== calendarDay || !p.time) return null;
                            const startMin = timeToMinutesFrom8(p.time);
                            const top = (startMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                            const dur = p.duration_minutes || 15;
                            const height = (dur / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                            if (top < 0 || top > totalHeight) return null;
                            const isActive = i === activePreviewIdx;
                            return (
                              <div
                                key={`prev-${i}`}
                                className={`absolute left-10 right-1 rounded-sm px-1.5 py-0.5 text-[10px] overflow-hidden border pointer-events-none ${isActive ? 'bg-emerald-500/30 border-emerald-600 text-emerald-900 ring-2 ring-emerald-500' : 'bg-emerald-100/60 border-emerald-300 text-emerald-800 border-dashed'}`}
                                style={{ top: top + 'px', height: Math.max(height, 18) + 'px', zIndex: 6 }}
                                title={`${p.time} | ${p.study_volunteer_code} · ${p.visit_code}`}
                              >
                                <div className="flex items-center gap-1 font-mono font-semibold">
                                  {p.time} <span className="text-[9px] uppercase">{isActive ? (lang === 'es' ? '· activa' : '· active') : (lang === 'es' ? '· nueva' : '· new')}</span>
                                </div>
                                <div className="truncate leading-tight">
                                  {p.study_volunteer_code} · {p.visit_code}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  {loadingDayAppts && (
                    <p className="text-center text-[10px] text-stone-400 py-1">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setGenStep('config')} className="rounded-sm">
                  {lang === 'es' ? 'Volver' : 'Back'}
                </Button>
                <div className="flex flex-col gap-1.5 mr-auto">
                  <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid="send-email-appts-toggle">
                    <input
                      type="checkbox"
                      checked={sendEmailWithAppts}
                      onChange={(e) => setSendEmailWithAppts(e.target.checked)}
                      className="rounded border-stone-300"
                    />
                    {lang === 'es'
                      ? 'Enviar tambien por email al voluntario (con archivo .ics)'
                      : 'Also send by email to the volunteer (with .ics)'}
                  </label>
                  <label className="flex items-center gap-2 text-xs text-amber-700 cursor-pointer select-none" data-testid="replace-existing-appts-toggle">
                    <input
                      type="checkbox"
                      checked={replaceExistingAppts}
                      onChange={(e) => setReplaceExistingAppts(e.target.checked)}
                      className="rounded border-amber-400"
                    />
                    {lang === 'es'
                      ? 'Reemplazar citas ya existentes (no terminadas)'
                      : 'Replace existing appointments (not completed)'}
                  </label>
                </div>
                <Button data-testid="confirm-generate-btn" onClick={() => handleConfirmGenerate()} disabled={generating}
                  className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
                  <CalendarBlank size={14} />
                  {generating ? '...' : (lang === 'es' ? `Confirmar, crear e imprimir ${previewAppts.length} citas` : `Confirm, create and print ${previewAppts.length} appointments`)}
                </Button>
              </DialogFooter>
            </>
          )}

          {genStep === 'result' && (
            <>
              <div className="space-y-3 py-4">
                <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-center">
                  <p className="text-lg font-semibold text-green-700">
                    {genResult?.created || 0} {lb.gen_created}
                  </p>
                  {/* URS-F459: mostrar tambien las citas reemplazadas cuando el usuario activo la opcion */}
                  {genResult?.replaced > 0 && (
                    <p className="text-sm font-medium text-amber-700 mt-1" data-testid="gen-result-replaced">
                      {genResult.replaced} {lang === 'es' ? 'reemplazadas (reprogramadas)' : 'replaced (rescheduled)'}
                    </p>
                  )}
                  {genResult?.skipped > 0 && (
                    <p className="text-sm text-stone-500 mt-1">{genResult.skipped} {lb.gen_skipped}</p>
                  )}
                </div>

                {/* URS-F459c: si todo se omitio por existencia previa y aun NO se
                    intento reemplazar, ofrecer un boton directo para reintentar
                    con replace_existing=true. Mucho mas descubrible que el
                    checkbox del footer. */}
                {genResult?.created === 0 && genResult?.skipped > 0 && !replaceExistingAppts && (
                  <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 flex flex-col gap-2" data-testid="gen-result-retry-replace-box">
                    <p className="text-xs text-amber-800">
                      {lang === 'es'
                        ? `${genResult.skipped} citas ya existian en el sistema. ¿Reemplazarlas por las nuevas fechas y horas del plan actual?`
                        : `${genResult.skipped} appointments already exist. Replace them with the new dates and times from the current plan?`}
                    </p>
                    <p className="text-[11px] text-stone-500">
                      {lang === 'es'
                        ? 'Solo se reprograman citas no terminadas. Las terminadas se preservan intactas por auditoria.'
                        : 'Only non-completed appointments are rescheduled. Completed ones are preserved intact for audit.'}
                    </p>
                    <Button
                      data-testid="gen-result-retry-replace-btn"
                      onClick={async () => {
                        setReplaceExistingAppts(true);
                        // Volvemos al step preview y reintentamos con el flag encendido.
                        setGenStep('preview');
                        // pequeña espera para que el setState propague antes de re-ejecutar
                        setTimeout(() => { handleConfirmGenerate(false, false); }, 50);
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs"
                    >
                      {lang === 'es' ? 'Reintentar reemplazando existentes' : 'Retry replacing existing'}
                    </Button>
                  </div>
                )}
                {emailSendResult && emailSendResult.length > 0 && (
                  <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs space-y-1.5" data-testid="email-send-result">
                    <p className="font-semibold text-stone-700 mb-1">
                      {lang === 'es' ? 'Envio por email' : 'Email send'}
                    </p>
                    {emailSendResult.map(r => (
                      <div key={r.volunteer_id} className="flex items-start gap-2">
                        {r.ok ? (
                          <span className="text-emerald-700">&#10003;</span>
                        ) : (
                          <span className="text-red-600">&#10005;</span>
                        )}
                        <span className="font-mono text-[10px]">{r.volunteer_id}</span>
                        <span className="flex-1 text-stone-700">
                          {r.ok
                            ? (lang === 'es'
                                ? `${r.count} citas enviadas a ${(r.recipients || []).join(', ')}`
                                : `${r.count} appointments sent to ${(r.recipients || []).join(', ')}`)
                            : r.error}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setShowGenerate(false)} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
                  {lang === 'es' ? 'Cerrar' : 'Close'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Out-of-schedule warning before bulk-creating appointments */}
      <Dialog open={!!outOfScheduleConfirm} onOpenChange={(v) => { if (!v) setOutOfScheduleConfirm(null); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-out-of-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              {lang === 'es' ? 'Citas fuera de programacion' : 'Appointments out of schedule'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-700">
            {lang === 'es'
              ? `Se van a crear ${previewAppts.length} citas. ${outOfScheduleConfirm?.outCount || 0} estan fuera de la programacion de este estudio.`
              : `${previewAppts.length} appointments will be created. ${outOfScheduleConfirm?.outCount || 0} fall outside this study's schedule.`}
          </p>
          {outOfScheduleConfirm?.outRows && outOfScheduleConfirm.outRows.length > 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-sm p-2 max-h-48 overflow-y-auto space-y-0.5">
              {outOfScheduleConfirm.outRows.map(idx => {
                const a = previewAppts[idx];
                if (!a) return null;
                return (
                  <div key={idx} className="flex items-center gap-2" data-testid={`bulk-out-row-${idx}`}>
                    <span className="font-mono text-[#1f3e80] font-semibold">{a.study_volunteer_code}</span>
                    <span className="font-mono text-stone-700">{a.visit_code}</span>
                    <span className="font-mono text-amber-700">{a.date} {a.time}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-stone-500">
            {lang === 'es'
              ? 'Puedes volver al paso anterior para reajustar fechas/horas dentro de las franjas programadas, o crear todas las citas igualmente (quedaran marcadas para revision).'
              : 'You can go back to adjust dates/times to fall inside programmed slots, or create them anyway (they will be flagged for review).'}
          </p>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="outline" data-testid="bulk-out-back" onClick={() => setOutOfScheduleConfirm(null)} className="rounded-sm">
              {lang === 'es' ? 'Volver y ajustar' : 'Back & adjust'}
            </Button>
            <Button data-testid="bulk-out-create-anyway"
              disabled={generating}
              onClick={() => { setOutOfScheduleConfirm(null); handleConfirmGenerate(true); }}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">
              {generating ? '...' : (lang === 'es' ? 'Crear igualmente' : 'Create anyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URS-F424: popup de confirmacion para huecos con bloqueo TOTAL (global) */}
      <Dialog open={!!globalBlockConfirm} onOpenChange={(v) => { if (!v) setGlobalBlockConfirm(null); }}>
        <DialogContent className="max-w-2xl" data-testid="global-block-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <WarningCircle size={20} weight="fill" />
              {lang === 'es' ? 'Bloqueo total en la agenda' : 'Total schedule block'}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>
              {lang === 'es'
                ? 'Las siguientes citas caen en huecos con bloqueo total (bloqueo global desde Agenda, no de estudio ni de usuario). ¿Aún así quieres crearlas?'
                : 'The following appointments fall inside slots with a total block. Create anyway?'}
            </p>
            <div className="max-h-64 overflow-y-auto border border-stone-200 rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 text-stone-600 uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-2">{lang === 'es' ? 'Voluntario' : 'Volunteer'}</th>
                    <th className="text-left p-2">{lang === 'es' ? 'Visita' : 'Visit'}</th>
                    <th className="text-left p-2">{lang === 'es' ? 'Fecha' : 'Date'}</th>
                    <th className="text-left p-2">{lang === 'es' ? 'Hora' : 'Time'}</th>
                    <th className="text-left p-2">{lang === 'es' ? 'Bloqueo' : 'Block'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(globalBlockConfirm?.conflicts || []).map((c, i) => (
                    <tr key={i} className="border-t border-stone-100" data-testid={`global-block-conflict-${i}`}>
                      <td className="p-2">{c.study_volunteer_code || c.volunteer_id?.slice(0,8)} {c.volunteer_name ? `· ${c.volunteer_name}` : ''}</td>
                      <td className="p-2 font-mono">{c.visit_code}</td>
                      <td className="p-2 whitespace-nowrap">{c.date}</td>
                      <td className="p-2 font-mono whitespace-nowrap">{c.time} <span className="text-stone-400">({c.block_start}–{c.block_end})</span></td>
                      <td className="p-2 text-red-700">{c.block_reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="outline" data-testid="global-block-cancel" onClick={() => setGlobalBlockConfirm(null)} className="rounded-sm">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button data-testid="global-block-force"
              disabled={generating}
              onClick={() => { setGlobalBlockConfirm(null); handleConfirmGenerate(true, true); }}
              className="bg-red-700 hover:bg-red-800 text-white rounded-sm">
              {generating ? '...' : (lang === 'es' ? 'Crear igualmente' : 'Create anyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ESignature
        open={showESign}
        onClose={() => setShowESign(false)}
        onSigned={(signature) => {
          setShowESign(false);
          handleSaveQ(true, signature);
        }}
        action={lang === 'es' ? 'Completar cuestionario' : 'Complete questionnaire'}
        resource={fillQ ? `${fillQ.vol?.study_volunteer_code || ''}/${fillQ.visit?.code || ''}/${fillQ.questionnaire?.name || ''}` : ''}
        lang={lang}
      />

      <ConfirmDeleteDialog
        open={!!confirmDeleteVol}
        onOpenChange={(v) => { if (!v) setConfirmDeleteVol(null); }}
        title={lang === 'es' ? 'Desasignar voluntario' : 'Remove volunteer'}
        description={lang === 'es'
          ? `¿Estás seguro de que deseas desasignar a ${confirmDeleteVol?.volunteer_name || ''} (${confirmDeleteVol?.volunteer_id || ''}) de este estudio?`
          : `Are you sure you want to remove ${confirmDeleteVol?.volunteer_name || ''} (${confirmDeleteVol?.volunteer_id || ''}) from this study?`}
        onConfirm={() => handleRemove(confirmDeleteVol?.volunteer_id)}
        loading={deleting}
      />

      {/* URS-F323: popup con el motivo de exclusion / abandono */}
      <Dialog open={!!reasonPopup} onOpenChange={(v) => { if (!v) setReasonPopup(null); }}>
        <DialogContent className="max-w-lg" data-testid="reason-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={18} className="text-[#1f3e80]" />
              {reasonPopup?.kind === 'abandono'
                ? (lang === 'es' ? 'Motivo del abandono' : 'Withdrawal reason')
                : (lang === 'es' ? 'Motivo de la exclusión' : 'Exclusion reason')}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {reasonPopup?.volunteer?.study_volunteer_code} · {reasonPopup?.volunteer?.volunteer_id}
              {reasonPopup?.volunteer?.volunteer_name && <> · {reasonPopup.volunteer.volunteer_name}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-sm text-stone-800 whitespace-pre-line min-h-20" data-testid="reason-popup-text">
            {reasonPopup?.volunteer?.notes || <span className="text-stone-400">{lang === 'es' ? 'Sin motivo registrado.' : 'No reason recorded.'}</span>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonPopup(null)} data-testid="reason-popup-close">
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppointmentFormDialog
        open={!!apptPreselect}
        onClose={() => setApptPreselect(null)}
        editing={null}
        preselect={apptPreselect}
        lang={lang}
        isAdmin={isAdmin}
        onSaved={() => { load(); }}
      />

      {/* URS-F498: dialogo para añadir cuestionario de Eventos Adversos. */}
      <Dialog open={addEaDialog} onOpenChange={(v) => { if (!v && !eaSaving) setAddEaDialog(false); }}>
        <DialogContent data-testid="add-ea-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Añadir cuestionario de Eventos Adversos' : 'Add Adverse Events questionnaire'}</DialogTitle>
            <DialogDescription>
              {lang === 'es'
                ? 'Se añadira el cuestionario de EA a los voluntarios y visitas seleccionadas. Selecciona "Todos" en cualquiera de los dos para incluirlos a todos.'
                : 'The AE questionnaire will be added to the selected volunteers and visits. Choose "All" on either selector to include everyone.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Voluntarios */}
            <div>
              <Label className="text-sm font-medium">{lang === 'es' ? 'Voluntarios (incluidos)' : 'Volunteers (included)'}</Label>
              <div className="mt-1.5 max-h-40 overflow-y-auto border border-stone-200 rounded-sm bg-white divide-y divide-stone-100">
                <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-stone-50">
                  <input
                    type="checkbox"
                    data-testid="add-ea-vol-all"
                    checked={eaSelVols.includes('all')}
                    onChange={() => setEaSelVols(eaSelVols.includes('all') ? [] : ['all'])}
                  />
                  <span className="text-sm font-medium text-[#1f3e80]">{lang === 'es' ? 'Todos' : 'All'}</span>
                </label>
                {incluidos.map(v => (
                  <label key={v.volunteer_id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-stone-50">
                    <input
                      type="checkbox"
                      data-testid={`add-ea-vol-${v.volunteer_id}`}
                      disabled={eaSelVols.includes('all')}
                      checked={eaSelVols.includes(v.volunteer_id)}
                      onChange={() => {
                        setEaSelVols(prev => {
                          const withoutAll = prev.filter(x => x !== 'all');
                          return withoutAll.includes(v.volunteer_id)
                            ? withoutAll.filter(x => x !== v.volunteer_id)
                            : [...withoutAll, v.volunteer_id];
                        });
                      }}
                    />
                    <span className="font-mono text-xs text-stone-700">{v.study_volunteer_code || v.volunteer_id}</span>
                  </label>
                ))}
                {incluidos.length === 0 && (
                  <p className="px-3 py-3 text-xs text-stone-400 italic">{lang === 'es' ? 'No hay voluntarios incluidos' : 'No included volunteers'}</p>
                )}
              </div>
            </div>

            {/* Visitas */}
            <div>
              <Label className="text-sm font-medium">{lang === 'es' ? 'Visitas' : 'Visits'}</Label>
              <p className="text-[10px] text-stone-500 mb-1.5">{lang === 'es' ? 'Se excluyen las visitas VAL y EA.' : 'Visits VAL and EA are excluded.'}</p>
              <div className="max-h-40 overflow-y-auto border border-stone-200 rounded-sm bg-white divide-y divide-stone-100">
                <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-stone-50">
                  <input
                    type="checkbox"
                    data-testid="add-ea-visit-all"
                    checked={eaSelVisits.includes('all')}
                    onChange={() => setEaSelVisits(eaSelVisits.includes('all') ? [] : ['all'])}
                  />
                  <span className="text-sm font-medium text-[#1f3e80]">{lang === 'es' ? 'Todas' : 'All'}</span>
                </label>
                {studyVisits.filter(v => !['VAL', 'EA'].includes((v.code || '').toUpperCase())).map(v => (
                  <label key={v.visit_id || v.code} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-stone-50">
                    <input
                      type="checkbox"
                      data-testid={`add-ea-visit-${v.code}`}
                      disabled={eaSelVisits.includes('all')}
                      checked={eaSelVisits.includes(v.code)}
                      onChange={() => {
                        setEaSelVisits(prev => {
                          const withoutAll = prev.filter(x => x !== 'all');
                          return withoutAll.includes(v.code)
                            ? withoutAll.filter(x => x !== v.code)
                            : [...withoutAll, v.code];
                        });
                      }}
                    />
                    <span className="font-mono text-xs text-stone-700">{v.code}</span>
                    <span className="text-xs text-stone-500 truncate">{v.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddEaDialog(false)} disabled={eaSaving} data-testid="add-ea-cancel">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={eaSaving || eaSelVols.length === 0 || eaSelVisits.length === 0}
              data-testid="add-ea-confirm"
              onClick={async () => {
                setEaSaving(true);
                try {
                  const { data } = await axios.post(
                    `${API_URL}/api/studies/${studyId}/assign-adverse-events`,
                    { volunteer_ids: eaSelVols, visit_codes: eaSelVisits },
                    { withCredentials: true },
                  );
                  const scope = data?.scope === 'visit_level'
                    ? (lang === 'es' ? `${data.affected_visits} visitas actualizadas` : `${data.affected_visits} visits updated`)
                    : (lang === 'es' ? `${data.affected_volunteers} voluntarios actualizados` : `${data.affected_volunteers} volunteers updated`);
                  toast.success(lang === 'es' ? `Cuestionario de EA añadido (${scope})` : `AE questionnaire added (${scope})`);
                  setAddEaDialog(false);
                  load();
                } catch (err) {
                  toast.error(errText(err));
                } finally {
                  setEaSaving(false);
                }
              }}
            >
              {eaSaving ? '...' : (lang === 'es' ? 'Añadir EA' : 'Add AE')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URS-F495: tras asignar un voluntario al estudio, ofrecer enviar HIP por email. */}
      <SendInfoSheetOnAssignDialog
        open={!!sendHipDialog}
        onClose={() => setSendHipDialog(null)}
        studyId={studyId}
        volunteerId={sendHipDialog?.volunteerId}
        volunteerName={sendHipDialog?.volunteerName}
        volunteerEmail={sendHipDialog?.volunteerEmail}
        lang={lang}
      />
    </div>
  );
}
